import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useUser } from "@clerk/clerk-react";
import { useMemo, useState, useEffect, useRef, Component, type ReactNode } from "react";
import { getUserFromClerkId } from "@/hooks/useUser";
import { getDailyTip, getScamOfTheDay } from "@/hooks/useDailyTipScam";
import { getScams, type scamSchema } from "@/hooks/useScam";
import GuardianPointsCard from "@/components/GuardianPointsCard";
import QuickActionsCard from "@/components/QuickActionsCard";
import ThreatFeedCard from "@/components/ThreatFeedCard";
import {
  HiOutlineAcademicCap,
  HiOutlineArrowRight,
  HiOutlineShieldExclamation,
  HiOutlineShieldCheck,
  HiOutlineChevronRight,
  HiOutlineLockClosed,
  HiOutlineSearch,
  HiOutlineTrendingUp,
  HiOutlineCheck,
  HiOutlineFlag,
  HiOutlineInformationCircle,
  HiOutlineChatAlt2,
} from "react-icons/hi";
import { FaTrophy } from "react-icons/fa";
import { FiAlertTriangle, FiMessageCircle, FiSearch, FiShield, FiUsers, FiZap, FiEye } from "react-icons/fi";
import { SearchCheck } from "lucide-react";
import courseGraphic from "@/assets/coursegraphic10.webp";
import courseBg from "@/assets/bgabstract3.png";
import gLogoViolet from "@/assets/g_logo_violet.png";
import { useMissions, type Mission } from "@/hooks/useMissions";
import { type User } from "@/hooks/useUser";
import { useCourseProgress } from "@/hooks/useCourseProgress";
import { queuePendingTextScamCheck } from "@/lib/scamCheckPending";
import { FamilyAvatarRail } from "@/components/family/FamilyAvatarRail";
import { useFamilyProgress } from "@/hooks/useFamilyProgress";
import AvatarDisplay from "@/components/AvatarDisplay";
import { UserPlus, Shield } from "lucide-react";
import Avvvatars from "avvvatars-react";
import type { ScamOfTheDay } from "@/hooks/useDailyTipScam";
import { type z } from "zod";

type Scam = z.infer<typeof scamSchema>;

type Variant = "main" | "current" | "a" | "b" | "c-pure" | "c-teaser" | "c-rail" | "c-rail-live" | "d" | "e" | "f" | "g";

const VALID_VARIANTS: Variant[] = ["main", "current", "a", "b", "c-pure", "c-teaser", "c-rail", "c-rail-live", "d", "e", "f", "g"];

type HeroStyle = "chrome" | "ghost" | "outline" | "unified" | "dividers" | "flat" | "divided";
const VALID_HERO_STYLES: HeroStyle[] = ["chrome", "ghost", "outline", "unified", "dividers", "flat", "divided"];

type ProgressStyle = "default" | "quiet" | "muted" | "dots" | "minimal" | "bar";
const VALID_PROGRESS_STYLES: ProgressStyle[] = ["default", "quiet", "muted", "dots", "minimal", "bar"];

type TeaserLayout = "grid-2" | "grid-3";
const VALID_TEASER_LAYOUTS: TeaserLayout[] = ["grid-2", "grid-3"];

type RightSide = "checklist" | "level-moves";
const VALID_RIGHT_SIDES: RightSide[] = ["checklist", "level-moves"];

type BadgeStyle = "pill" | "text" | "ghost" | "underline" | "soft" | "slate" | "dot" | "logo";
const VALID_BADGE_STYLES: BadgeStyle[] = ["pill", "text", "ghost", "underline", "soft", "slate", "dot", "logo"];

type CheckStyle = "filled" | "outline" | "square" | "tick" | "dot" | "subtle" | "ring";
const VALID_CHECK_STYLES: CheckStyle[] = ["filled", "outline", "square", "tick", "dot", "subtle", "ring"];

type FamilyStyle = "card" | "fieldset" | "outline" | "floating" | "tint" | "underline" | "bracket";
const VALID_FAMILY_STYLES: FamilyStyle[] = ["card", "fieldset", "outline", "floating", "tint", "underline", "bracket"];

type TopBlurb =
  | "status"
  | "plain"
  | "emphatic"
  | "bar"
  | "friendly"
  | "inline"
  | "proud"
  | "coach"
  | "journey"
  | "affirming";
const VALID_TOP_BLURBS: TopBlurb[] = [
  "status",
  "plain",
  "emphatic",
  "bar",
  "friendly",
  "inline",
  "proud",
  "coach",
  "journey",
  "affirming",
];

type RingStyle =
  | "classic"
  | "notched"
  | "dotted"
  | "half-gauge"
  | "layered"
  | "tick"
  | "notched-level"
  | "notched-level-dense"
  | "notched-level-active"
  | "notched-level-animated"
  | "notched-level-deluxe";
const VALID_RING_STYLES: RingStyle[] = [
  "classic",
  "notched",
  "dotted",
  "half-gauge",
  "layered",
  "tick",
  "notched-level",
  "notched-level-dense",
  "notched-level-active",
  "notched-level-animated",
  "notched-level-deluxe",
];

type ScoreCardStyle =
  | "ring"
  | "guardian"
  | "guardian-split"
  | "competencies"
  | "levels"
  | "bento"
  | "hero"
  | "hero-quiet"
  | "hero-arc"
  | "hero-split"
  | "hero-edge"
  | "hero-edge-split"
  | "hero-edge-balanced"
  | "hero-edge-stacked"
  | "hero-edge-tiles"
  | "hero-edge-rail"
  | "hero-edge-minimal"
  | "journey"
  | "next-action"
  | "trajectory"
  | "milestone-gap"
  | "skill-grid"
  | "credit-gauge"
  | "credit-breakdown"
  | "credit-factors"
  | "credit-peer"
  | "credit-audit"
  | "pdrc-breakdown"
  | "pdrc-pillars"
  | "pdrc-radar"
  | "pdrc-cards"
  | "minimal-level"
  | "minimal-score"
  | "minimal-hero"
  | "minimal-twin"
  | "minimal-ladder";
const VALID_SCORE_CARD_STYLES: ScoreCardStyle[] = [
  "ring",
  "guardian",
  "guardian-split",
  "competencies",
  "levels",
  "bento",
  "hero",
  "hero-quiet",
  "hero-arc",
  "hero-split",
  "hero-edge",
  "hero-edge-split",
  "hero-edge-balanced",
  "hero-edge-stacked",
  "hero-edge-tiles",
  "hero-edge-rail",
  "hero-edge-minimal",
  "journey",
  "next-action",
  "trajectory",
  "milestone-gap",
  "skill-grid",
  "credit-gauge",
  "credit-breakdown",
  "credit-factors",
  "credit-peer",
  "credit-audit",
  "pdrc-breakdown",
  "pdrc-pillars",
  "pdrc-radar",
  "pdrc-cards",
  "minimal-level",
  "minimal-score",
  "minimal-hero",
  "minimal-twin",
  "minimal-ladder",
];

type RailStyle =
  | "default"
  | "minimal"
  | "grouped"
  | "icons"
  | "pills"
  | "numbered"
  | "apple"
  | "editorial"
  | "strip"
  | "big-icons"
  | "ghost"
  | "tinted"
  | "menu"
  | "hero";
const VALID_RAIL_STYLES: RailStyle[] = [
  "default",
  "minimal",
  "grouped",
  "icons",
  "pills",
  "numbered",
  "apple",
  "editorial",
  "strip",
  "big-icons",
  "ghost",
  "tinted",
  "menu",
  "hero",
];

export const Route = createFileRoute("/_auth/home-lab")({
  validateSearch: (search: Record<string, unknown>): { v: Variant; rail: RailStyle; heroStyle: HeroStyle; progressStyle: ProgressStyle; teaserLayout: TeaserLayout; scoreCard: ScoreCardStyle; ringStyle: RingStyle; rightSide: RightSide; topBlurb: TopBlurb; badgeStyle: BadgeStyle; checkStyle: CheckStyle; familyStyle: FamilyStyle; previewLevel?: string; unlocked?: string; guardianInHero?: string; goldTrophy?: string } => {
    const rawV = search.v;
    const v = typeof rawV === "string" && (VALID_VARIANTS as string[]).includes(rawV) ? (rawV as Variant) : "current";
    const rawR = search.rail;
    const rail = typeof rawR === "string" && (VALID_RAIL_STYLES as string[]).includes(rawR) ? (rawR as RailStyle) : "default";
    const rawH = search.heroStyle;
    const heroStyle = typeof rawH === "string" && (VALID_HERO_STYLES as string[]).includes(rawH) ? (rawH as HeroStyle) : "chrome";
    const rawP = search.progressStyle;
    const progressStyle = typeof rawP === "string" && (VALID_PROGRESS_STYLES as string[]).includes(rawP) ? (rawP as ProgressStyle) : "default";
    const rawT = search.teaserLayout;
    const teaserLayout = typeof rawT === "string" && (VALID_TEASER_LAYOUTS as string[]).includes(rawT) ? (rawT as TeaserLayout) : "grid-2";
    const rawSC = search.scoreCard;
    const scoreCard = typeof rawSC === "string" && (VALID_SCORE_CARD_STYLES as string[]).includes(rawSC) ? (rawSC as ScoreCardStyle) : "ring";
    const rawRS = search.ringStyle;
    const ringStyle = typeof rawRS === "string" && (VALID_RING_STYLES as string[]).includes(rawRS) ? (rawRS as RingStyle) : "classic";
    const rawRightSide = search.rightSide;
    const rightSide = typeof rawRightSide === "string" && (VALID_RIGHT_SIDES as string[]).includes(rawRightSide) ? (rawRightSide as RightSide) : "checklist";
    const rawTopBlurb = search.topBlurb;
    const topBlurb = typeof rawTopBlurb === "string" && (VALID_TOP_BLURBS as string[]).includes(rawTopBlurb) ? (rawTopBlurb as TopBlurb) : "plain";
    const rawBadgeStyle = search.badgeStyle;
    const badgeStyle = typeof rawBadgeStyle === "string" && (VALID_BADGE_STYLES as string[]).includes(rawBadgeStyle) ? (rawBadgeStyle as BadgeStyle) : "pill";
    const rawCheckStyle = search.checkStyle;
    const checkStyle = typeof rawCheckStyle === "string" && (VALID_CHECK_STYLES as string[]).includes(rawCheckStyle) ? (rawCheckStyle as CheckStyle) : "filled";
    const rawFamilyStyle = search.familyStyle;
    const familyStyle = typeof rawFamilyStyle === "string" && (VALID_FAMILY_STYLES as string[]).includes(rawFamilyStyle) ? (rawFamilyStyle as FamilyStyle) : "card";
    const rawPreviewLevel = search.previewLevel;
    const previewLevel = (typeof rawPreviewLevel === "number" || (typeof rawPreviewLevel === "string" && /^\d+$/.test(rawPreviewLevel)))
      ? String(Math.min(20, Math.max(1, Number(rawPreviewLevel))))
      : undefined;
    const unlocked = search.unlocked === "1" ? "1" : undefined;
    const guardianInHero = search.guardianInHero === "1" ? "1" : undefined;
    const goldTrophy = search.goldTrophy === "1" ? "1" : undefined;
    return { v, rail, heroStyle, progressStyle, teaserLayout, scoreCard, ringStyle, rightSide, topBlurb, badgeStyle, checkStyle, familyStyle, previewLevel, unlocked, guardianInHero, goldTrophy };
  },
  component: HomeLabPage,
});

const VARIANTS: { id: Variant; label: string; tagline: string }[] = [
  { id: "main", label: "★ Main · Recommended", tagline: "Locked: notched ring, slate badge, subtle checks, proud blurb. Candidate to replace the live dash." },
  { id: "current", label: "Current", tagline: "Today's live homepage" },
  { id: "a", label: "A · Up Next First", tagline: "Course is the hero, tools demoted" },
  { id: "b", label: "B · Single Column", tagline: "One vertical flow, no above-fold grid" },
  { id: "c-pure", label: "C · Focus Mode", tagline: "Hide everything until course done" },
  { id: "c-teaser", label: "C · Focus + Teaser", tagline: "Locked previews unlock after course" },
  { id: "c-rail", label: "C · Focus + Rail", tagline: "Centered focus with a left quick-actions rail" },
  { id: "c-rail-live", label: "C · Focus + Rail + Live Checker", tagline: "C-rail but with the inline scam checker in the centre" },
  { id: "d", label: "D · Home-3 Style", tagline: "Inline scam checker + dark path card + trending radar" },
  { id: "e", label: "E · Protection Score", tagline: "Score + Up Next hero, three quick actions, scam radar with red flags" },
  { id: "f", label: "F · Best of Both", tagline: "E's Guardian Level card hero + live home's quick actions + side-by-side Up Next & Radar" },
  { id: "g", label: "G · Bento Grid", tagline: "Tall left card with Level + Quick Actions list, stacked Up Next & Scam Radar on the right" },
];

/* ─── Page shell ──────────────────────────────────────────────────── */

function HomeLabPage() {
  const { v, rail, heroStyle, progressStyle, teaserLayout, scoreCard, ringStyle, rightSide, topBlurb, badgeStyle, checkStyle, familyStyle, previewLevel, unlocked, guardianInHero, goldTrophy } = Route.useSearch();
  const isUnlocked = unlocked === "1";
  const isGuardianInHero = guardianInHero === "1";
  const isGoldTrophy = goldTrophy === "1";
  const navigate = Route.useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <style>{`
        .home-lab-scroll::-webkit-scrollbar { width: 6px; }
        .home-lab-scroll::-webkit-scrollbar-track { background: transparent; }
        .home-lab-scroll::-webkit-scrollbar-thumb { background: rgba(15,23,42,0.12); border-radius: 3px; }
        .home-lab-scroll::-webkit-scrollbar-thumb:hover { background: rgba(15,23,42,0.22); }
        .home-lab-scroll { scrollbar-width: thin; scrollbar-color: rgba(15,23,42,0.18) transparent; }
      `}</style>
      {/* Floating draggable variant switcher — desktop only */}
      <DraggableConfigurator
        currentVariant={v}
        currentRail={rail}
        currentHeroStyle={heroStyle}
        currentProgressStyle={progressStyle}
        currentUnlocked={isUnlocked}
        currentGuardianInHero={isGuardianInHero}
        currentGoldTrophy={isGoldTrophy}
        currentTeaserLayout={teaserLayout}
        currentScoreCard={scoreCard}
        currentRingStyle={ringStyle}
        currentRightSide={rightSide}
        currentTopBlurb={topBlurb}
        currentBadgeStyle={badgeStyle}
        currentPreviewLevel={previewLevel}
        currentCheckStyle={checkStyle}
        currentFamilyStyle={familyStyle}
        onSelect={(id) => navigate({ search: (prev) => ({ ...prev, v: id }) })}
        onSelectRail={(id) => navigate({ search: (prev) => ({ ...prev, rail: id }) })}
        onSelectHeroStyle={(id) => navigate({ search: (prev) => ({ ...prev, heroStyle: id }) })}
        onSelectProgressStyle={(id) => navigate({ search: (prev) => ({ ...prev, progressStyle: id }) })}
        onToggleUnlocked={(next) => navigate({ search: (prev) => ({ ...prev, unlocked: next ? "1" : undefined }) })}
        onToggleGuardianInHero={(next) => navigate({ search: (prev) => ({ ...prev, guardianInHero: next ? "1" : undefined }) })}
        onToggleGoldTrophy={(next) => navigate({ search: (prev) => ({ ...prev, goldTrophy: next ? "1" : undefined }) })}
        onSelectTeaserLayout={(id) => navigate({ search: (prev) => ({ ...prev, teaserLayout: id }) })}
        onSelectScoreCard={(id) => navigate({ search: (prev) => ({ ...prev, scoreCard: id }) })}
        onSelectRingStyle={(id) => navigate({ search: (prev) => ({ ...prev, ringStyle: id }) })}
        onSelectRightSide={(id) => navigate({ search: (prev) => ({ ...prev, rightSide: id }) })}
        onSelectTopBlurb={(id) => navigate({ search: (prev) => ({ ...prev, topBlurb: id }) })}
        onSelectBadgeStyle={(id) => navigate({ search: (prev) => ({ ...prev, badgeStyle: id }) })}
        onSelectPreviewLevel={(lvl) => navigate({ search: (prev) => ({ ...prev, previewLevel: lvl ? String(lvl) : undefined }) })}
        onSelectCheckStyle={(id) => navigate({ search: (prev) => ({ ...prev, checkStyle: id }) })}
        onSelectFamilyStyle={(id) => navigate({ search: (prev) => ({ ...prev, familyStyle: id }) })}
      />

      {/* Mobile fallback: compact pill bar under the header (below md the draggable panel is hidden) */}
      <div className="md:hidden border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex gap-1.5 overflow-x-auto scrollbar-hide">
          {VARIANTS.map((variant) => {
            const active = variant.id === v;
            return (
              <button
                key={variant.id}
                type="button"
                onClick={() => navigate({ search: (prev) => ({ ...prev, v: variant.id }) })}
                className={`cursor-pointer rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors ${
                  active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {variant.label}
              </button>
            );
          })}
        </div>
      </div>

      <VariantBoundary key={v}>
        {v === "main" && <VariantMain />}
        {v === "current" && <VariantCurrent />}
        {v === "a" && <VariantA />}
        {v === "b" && <VariantB />}
        {v === "c-pure" && <VariantC mode="pure" />}
        {v === "c-teaser" && <VariantC mode="teaser" />}
        {v === "c-rail" && <VariantC mode="rail" />}
        {v === "c-rail-live" && <VariantC mode="rail-live" />}
        {v === "d" && <VariantD />}
        {v === "e" && <VariantE />}
        {v === "f" && <VariantF />}
        {v === "g" && <VariantG />}
      </VariantBoundary>
    </div>
  );
}

const CONFIGURATOR_POSITION_KEY = "home-lab-configurator-position";
const CONFIGURATOR_COLLAPSED_KEY = "home-lab-configurator-collapsed";
const PANEL_WIDTH = 240;

const HERO_STYLES: { id: HeroStyle; label: string }[] = [
  { id: "chrome", label: "Chrome" },
  { id: "ghost", label: "Ghost" },
  { id: "outline", label: "Outline" },
  { id: "unified", label: "Unified" },
  { id: "dividers", label: "Dividers" },
  { id: "flat", label: "Flat" },
  { id: "divided", label: "Divided (vertical)" },
];

const RING_STYLES: { id: RingStyle; label: string }[] = [
  { id: "classic", label: "Classic" },
  { id: "notched", label: "Notched" },
  { id: "dotted", label: "Dotted" },
  { id: "half-gauge", label: "Half-gauge" },
  { id: "layered", label: "Layered" },
  { id: "tick", label: "Tick" },
  { id: "notched-level", label: "Notched · Level" },
  { id: "notched-level-dense", label: "Notched · Level dense" },
  { id: "notched-level-active", label: "Notched · Level active" },
  { id: "notched-level-animated", label: "Notched · Level animated" },
  { id: "notched-level-deluxe", label: "Notched · Level deluxe" },
];

const SCORE_CARD_STYLES: { id: ScoreCardStyle; label: string }[] = [
  { id: "ring", label: "Ring" },
  { id: "guardian", label: "Guardian" },
  { id: "guardian-split", label: "Guardian · split" },
  { id: "competencies", label: "Competencies" },
  { id: "levels", label: "Levels" },
  { id: "bento", label: "Bento" },
  { id: "hero", label: "Hero · gradient" },
  { id: "hero-quiet", label: "Hero · quiet" },
  { id: "hero-arc", label: "Hero · arc" },
  { id: "hero-split", label: "Hero · split" },
  { id: "hero-edge", label: "Hero · edge" },
  { id: "hero-edge-split", label: "Hero · edge split" },
  { id: "hero-edge-balanced", label: "Hero · edge balanced" },
  { id: "hero-edge-stacked", label: "Hero · edge stacked" },
  { id: "hero-edge-tiles", label: "Hero · edge tiles" },
  { id: "hero-edge-rail", label: "Hero · edge rail" },
  { id: "hero-edge-minimal", label: "Hero · edge minimal" },
  { id: "journey", label: "Journey" },
  { id: "next-action", label: "Next action" },
  { id: "trajectory", label: "Trajectory" },
  { id: "milestone-gap", label: "Milestone gap" },
  { id: "skill-grid", label: "Skill grid" },
  { id: "credit-gauge", label: "Credit · gauge" },
  { id: "credit-breakdown", label: "Credit · breakdown" },
  { id: "credit-factors", label: "Credit · factors" },
  { id: "credit-peer", label: "Credit · peer" },
  { id: "credit-audit", label: "Credit · audit" },
  { id: "pdrc-breakdown", label: "PDRC · breakdown" },
  { id: "pdrc-pillars", label: "PDRC · pillars" },
  { id: "pdrc-radar", label: "PDRC · radar" },
  { id: "pdrc-cards", label: "PDRC · cards" },
  { id: "minimal-level", label: "Minimal · level" },
  { id: "minimal-score", label: "Minimal · score" },
  { id: "minimal-hero", label: "Minimal · hero" },
  { id: "minimal-twin", label: "Minimal · twin" },
  { id: "minimal-ladder", label: "Minimal · ladder" },
];

const PROGRESS_STYLES: { id: ProgressStyle; label: string }[] = [
  { id: "default", label: "Default" },
  { id: "quiet", label: "Quiet check" },
  { id: "muted", label: "Muted" },
  { id: "dots", label: "Dots" },
  { id: "minimal", label: "Minimal" },
  { id: "bar", label: "Bar only" },
];

const RAIL_STYLES: { id: RailStyle; label: string }[] = [
  { id: "default", label: "Default" },
  { id: "minimal", label: "Minimal" },
  { id: "grouped", label: "Grouped" },
  { id: "icons", label: "Icon tiles" },
  { id: "pills", label: "Floating pills" },
  { id: "numbered", label: "Numbered" },
  { id: "apple", label: "Apple sidebar" },
  { id: "editorial", label: "Editorial" },
  { id: "strip", label: "Color strip" },
  { id: "big-icons", label: "Big icons" },
  { id: "ghost", label: "Ghost accent" },
  { id: "tinted", label: "Tinted tiles" },
  { id: "menu", label: "Menu list" },
  { id: "hero", label: "Guardian hero" },
];

function DraggableConfigurator({
  currentVariant,
  currentRail,
  currentHeroStyle,
  currentProgressStyle,
  currentUnlocked,
  currentGuardianInHero,
  currentGoldTrophy,
  currentTeaserLayout,
  currentScoreCard,
  currentRingStyle,
  currentRightSide,
  currentTopBlurb,
  currentBadgeStyle,
  currentPreviewLevel,
  currentCheckStyle,
  currentFamilyStyle,
  onSelect,
  onSelectRail,
  onSelectHeroStyle,
  onSelectProgressStyle,
  onToggleUnlocked,
  onToggleGuardianInHero,
  onToggleGoldTrophy,
  onSelectTeaserLayout,
  onSelectScoreCard,
  onSelectRingStyle,
  onSelectRightSide,
  onSelectTopBlurb,
  onSelectBadgeStyle,
  onSelectPreviewLevel,
  onSelectCheckStyle,
  onSelectFamilyStyle,
}: {
  currentVariant: Variant;
  currentRail: RailStyle;
  currentHeroStyle: HeroStyle;
  currentProgressStyle: ProgressStyle;
  currentUnlocked: boolean;
  currentGuardianInHero: boolean;
  currentGoldTrophy: boolean;
  currentTeaserLayout: TeaserLayout;
  currentScoreCard: ScoreCardStyle;
  currentRingStyle: RingStyle;
  currentRightSide: RightSide;
  currentTopBlurb: TopBlurb;
  currentBadgeStyle: BadgeStyle;
  currentPreviewLevel: string | undefined;
  currentCheckStyle: CheckStyle;
  currentFamilyStyle: FamilyStyle;
  onSelect: (id: Variant) => void;
  onSelectRail: (id: RailStyle) => void;
  onSelectHeroStyle: (id: HeroStyle) => void;
  onSelectProgressStyle: (id: ProgressStyle) => void;
  onToggleUnlocked: (next: boolean) => void;
  onToggleGuardianInHero: (next: boolean) => void;
  onToggleGoldTrophy: (next: boolean) => void;
  onSelectTeaserLayout: (id: TeaserLayout) => void;
  onSelectScoreCard: (id: ScoreCardStyle) => void;
  onSelectRingStyle: (id: RingStyle) => void;
  onSelectRightSide: (id: RightSide) => void;
  onSelectTopBlurb: (id: TopBlurb) => void;
  onSelectBadgeStyle: (id: BadgeStyle) => void;
  onSelectPreviewLevel: (lvl: number | undefined) => void;
  onSelectCheckStyle: (id: CheckStyle) => void;
  onSelectFamilyStyle: (id: FamilyStyle) => void;
}) {
  const showRailPicker = currentVariant === "c-rail" || currentVariant === "c-rail-live";
  const showHeroStylePicker = showRailPicker && currentRail === "hero";
  const showProgressStylePicker = showHeroStylePicker;
  const showGuardianPlacementToggle = showHeroStylePicker;
  const showUnlockedToggle = currentVariant.startsWith("c-");
  const showTeaserLayoutPicker = currentVariant.startsWith("c-");
  const showScoreCardPicker = currentVariant === "e";
  const showRingStylePicker = currentVariant === "e" && currentScoreCard === "ring";
  const showRightSidePicker = currentVariant === "e" && currentScoreCard === "ring";
  const showTopBlurbPicker = currentVariant === "e" && currentScoreCard === "ring" && currentRightSide === "checklist";
  const showBadgeStylePicker = currentVariant === "e" && currentScoreCard === "ring";
  const showPreviewLevelPicker = currentVariant === "e" && currentScoreCard === "ring";
  const showCheckStylePicker = currentVariant === "e" && currentScoreCard === "ring" && currentRightSide === "checklist";
  const showFamilyStylePicker = currentVariant === "main";
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Clamp a candidate position to the current viewport so the panel can never
  // end up off-screen (e.g. after a saved position from a wider window, or a
  // resize). Keeps the top of the panel below the sticky Nav header.
  const clampToViewport = (x: number, y: number) => {
    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
    const vh = typeof window !== "undefined" ? window.innerHeight : 768;
    const width = panelRef.current?.offsetWidth ?? PANEL_WIDTH;
    const height = panelRef.current?.offsetHeight ?? 240;
    const maxX = Math.max(8, vw - width - 8);
    const maxY = Math.max(96, vh - height - 8);
    return {
      x: Math.min(Math.max(8, x), maxX),
      y: Math.min(Math.max(96, y), maxY),
    };
  };

  const defaultPosition = () => ({
    x: Math.max(16, (typeof window !== "undefined" ? window.innerWidth : 1024) - PANEL_WIDTH - 24),
    y: 96,
  });

  // Hydrate position + collapsed state from localStorage; fallback to top-right.
  // If the stored position is outside the current viewport (smaller window, OS
  // scaling change, etc.), snap back to the default top-right slot. Honors a
  // ?resetPanel=1 URL escape hatch in case storage is the cause of trouble.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("resetPanel") === "1") {
        window.localStorage.removeItem(CONFIGURATOR_POSITION_KEY);
        window.localStorage.removeItem(CONFIGURATOR_COLLAPSED_KEY);
        url.searchParams.delete("resetPanel");
        window.history.replaceState(null, "", url.toString());
        setPosition(defaultPosition());
        return;
      }
      const stored = window.localStorage.getItem(CONFIGURATOR_POSITION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as { x: number; y: number };
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const isInBounds =
          parsed.x >= 0 &&
          parsed.y >= 0 &&
          parsed.x <= vw - 80 &&
          parsed.y <= vh - 80;
        setPosition(isInBounds ? clampToViewport(parsed.x, parsed.y) : defaultPosition());
      } else {
        setPosition(defaultPosition());
      }
      const storedCollapsed = window.localStorage.getItem(CONFIGURATOR_COLLAPSED_KEY);
      if (storedCollapsed === "1") setCollapsed(true);
    } catch {
      setPosition(defaultPosition());
    }
  }, []);

  // Re-clamp on window resize so a shrinking viewport can't strand the panel.
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => (prev ? clampToViewport(prev.x, prev.y) : defaultPosition()));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Window-level pointer handlers while dragging
  useEffect(() => {
    if (!dragging) return;

    const handleMove = (e: PointerEvent) => {
      const width = panelRef.current?.offsetWidth ?? PANEL_WIDTH;
      const height = panelRef.current?.offsetHeight ?? 200;
      const nextX = Math.min(
        Math.max(8, e.clientX - dragOffsetRef.current.x),
        window.innerWidth - width - 8
      );
      // Minimum Y of 72px so the top handle stays clear of the sticky Nav header.
      const nextY = Math.min(
        Math.max(72, e.clientY - dragOffsetRef.current.y),
        window.innerHeight - height - 8
      );
      setPosition({ x: nextX, y: nextY });
    };

    const handleUp = () => {
      setDragging(false);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [dragging]);

  // Persist position when drag ends
  useEffect(() => {
    if (!dragging && position) {
      try {
        window.localStorage.setItem(CONFIGURATOR_POSITION_KEY, JSON.stringify(position));
      } catch {}
    }
  }, [dragging, position]);

  // Persist collapsed state
  useEffect(() => {
    try {
      window.localStorage.setItem(CONFIGURATOR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {}
  }, [collapsed]);

  if (!position) return null;

  const handleDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
    e.preventDefault();
  };

  const tagline = VARIANTS.find((x) => x.id === currentVariant)?.tagline;

  return (
    <div
      ref={panelRef}
      className="hidden md:block fixed z-[60] select-none"
      style={{
        left: position.x,
        top: position.y,
        width: PANEL_WIDTH,
        cursor: dragging ? "grabbing" : "default",
      }}
    >
      <div
        className={`relative rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.12)] transition-shadow ${
          dragging ? "shadow-[0_20px_50px_rgba(15,23,42,0.22)]" : ""
        }`}
      >
        {/* Edge drag strips — let the user grab any edge of the panel to move it, */}
        {/* useful when the top handle is off-screen or obscured. Sit above content  */}
        {/* so they receive pointer events before the buttons/scroll area do.        */}
        <div
          onPointerDown={handleDragStart}
          aria-hidden
          className="absolute top-0 left-0 h-full w-2 z-10"
          style={{ cursor: dragging ? "grabbing" : "grab", touchAction: "none" }}
        />
        <div
          onPointerDown={handleDragStart}
          aria-hidden
          className="absolute top-0 right-0 h-full w-2 z-10"
          style={{ cursor: dragging ? "grabbing" : "grab", touchAction: "none" }}
        />
        <div
          onPointerDown={handleDragStart}
          aria-hidden
          className="absolute bottom-0 left-0 w-full h-2 z-10"
          style={{ cursor: dragging ? "grabbing" : "grab", touchAction: "none" }}
        />

        {/* Drag handle */}
        <div
          onPointerDown={handleDragStart}
          className="flex items-center justify-between gap-2 px-3 pt-2.5 pb-2 border-b border-slate-100/80 rounded-t-2xl"
          style={{ cursor: dragging ? "grabbing" : "grab", touchAction: "none" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex flex-col gap-[3px]" aria-hidden>
              <div className="flex gap-[3px]">
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span className="h-1 w-1 rounded-full bg-slate-300" />
              </div>
              <div className="flex gap-[3px]">
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span className="h-1 w-1 rounded-full bg-slate-300" />
              </div>
              <div className="flex gap-[3px]">
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span className="h-1 w-1 rounded-full bg-slate-300" />
              </div>
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-violet-600">
              Home lab
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                try {
                  window.localStorage.removeItem(CONFIGURATOR_POSITION_KEY);
                  window.localStorage.removeItem(CONFIGURATOR_COLLAPSED_KEY);
                } catch {}
                setCollapsed(false);
                setPosition(defaultPosition());
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="text-[11px] font-medium text-slate-400 hover:text-slate-900 cursor-pointer px-1.5 py-0.5 rounded"
              title="Reset panel to top-right"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCollapsed((c) => !c);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="text-[11px] font-medium text-slate-400 hover:text-slate-900 cursor-pointer px-1.5 py-0.5 rounded"
            >
              {collapsed ? "Expand" : "Collapse"}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div className="p-3 max-h-[calc(100vh-180px)] overflow-y-auto home-lab-scroll">
            <p className="text-[11px] leading-snug text-slate-500 px-1 pb-2">{tagline}</p>
            <div className="flex flex-col gap-1">
              {VARIANTS.map((variant) => {
                const active = variant.id === currentVariant;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => onSelect(variant.id)}
                    className={`cursor-pointer text-left rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                      active
                        ? "bg-slate-900 text-white"
                        : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {variant.label}
                  </button>
                );
              })}
            </div>

            {showRailPicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Rail style
                </p>
                <div className="flex flex-wrap gap-1">
                  {RAIL_STYLES.map((style) => {
                    const active = style.id === currentRail;
                    return (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => onSelectRail(style.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-violet-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {style.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showHeroStylePicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Hero chrome
                </p>
                <div className="flex flex-wrap gap-1">
                  {HERO_STYLES.map((style) => {
                    const active = style.id === currentHeroStyle;
                    return (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => onSelectHeroStyle(style.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-teal-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {style.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showProgressStylePicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Progress style
                </p>
                <div className="flex flex-wrap gap-1">
                  {PROGRESS_STYLES.map((style) => {
                    const active = style.id === currentProgressStyle;
                    return (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => onSelectProgressStyle(style.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-amber-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {style.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showGuardianPlacementToggle && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <label className="flex items-center justify-between gap-2 cursor-pointer px-1">
                  <span className="text-[11px] font-medium text-slate-600">Guardian in hero row</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={currentGuardianInHero}
                    onClick={() => onToggleGuardianInHero(!currentGuardianInHero)}
                    className={`relative h-5 w-9 cursor-pointer rounded-full transition-colors ${
                      currentGuardianInHero ? "bg-violet-500" : "bg-slate-200"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                        currentGuardianInHero ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </label>
                <p className="text-[10px] text-slate-400 px-1 mt-1.5 leading-snug">
                  Moves Guardian points to the right of the welcome greeting.
                </p>

                <label className="flex items-center justify-between gap-2 cursor-pointer px-1 mt-3">
                  <span className="text-[11px] font-medium text-slate-600">Gold trophy icon</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={currentGoldTrophy}
                    onClick={() => onToggleGoldTrophy(!currentGoldTrophy)}
                    className={`relative h-5 w-9 cursor-pointer rounded-full transition-colors ${
                      currentGoldTrophy ? "bg-amber-500" : "bg-slate-200"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                        currentGoldTrophy ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </label>
                <p className="text-[10px] text-slate-400 px-1 mt-1.5 leading-snug">
                  Swaps the G logo for a gold trophy on both Guardian cards.
                </p>
              </div>
            )}

            {showScoreCardPicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Score card style
                </p>
                <div className="flex flex-wrap gap-1">
                  {SCORE_CARD_STYLES.map((style) => {
                    const active = style.id === currentScoreCard;
                    return (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => onSelectScoreCard(style.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-violet-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {style.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showRingStylePicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Ring style
                </p>
                <div className="flex flex-wrap gap-1">
                  {RING_STYLES.map((style) => {
                    const active = style.id === currentRingStyle;
                    return (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => onSelectRingStyle(style.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-fuchsia-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {style.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showRightSidePicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Right side
                </p>
                <div className="flex flex-wrap gap-1">
                  {([
                    { id: "checklist", label: "Checklist" },
                    { id: "level-moves", label: "Level + moves" },
                  ] as const).map((option) => {
                    const active = option.id === currentRightSide;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => onSelectRightSide(option.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showTopBlurbPicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Top blurb
                </p>
                <div className="flex flex-wrap gap-1">
                  {([
                    { id: "status", label: "Status" },
                    { id: "plain", label: "Plain" },
                    { id: "emphatic", label: "Emphatic" },
                    { id: "bar", label: "Bar" },
                    { id: "friendly", label: "Friendly" },
                    { id: "inline", label: "Inline" },
                    { id: "proud", label: "Proud" },
                    { id: "coach", label: "Coach" },
                    { id: "journey", label: "Journey" },
                    { id: "affirming", label: "Affirming" },
                  ] as const).map((option) => {
                    const active = option.id === currentTopBlurb;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => onSelectTopBlurb(option.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showBadgeStylePicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Badge style
                </p>
                <div className="flex flex-wrap gap-1">
                  {([
                    { id: "pill", label: "Pill" },
                    { id: "soft", label: "Soft" },
                    { id: "ghost", label: "Ghost" },
                    { id: "underline", label: "Underline" },
                    { id: "text", label: "Text" },
                    { id: "slate", label: "Slate" },
                    { id: "dot", label: "Dot" },
                    { id: "logo", label: "Logo" },
                  ] as const).map((option) => {
                    const active = option.id === currentBadgeStyle;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => onSelectBadgeStyle(option.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-rose-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showPreviewLevelPicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Preview level
                </p>
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => onSelectPreviewLevel(undefined)}
                    className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                      currentPreviewLevel === undefined
                        ? "bg-amber-600 text-white"
                        : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    Real
                  </button>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((lvl) => {
                    const active = String(lvl) === currentPreviewLevel;
                    return (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => onSelectPreviewLevel(lvl)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium tabular-nums transition-colors ${
                          active
                            ? "bg-amber-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {lvl}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showCheckStylePicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Check style
                </p>
                <div className="flex flex-wrap gap-1">
                  {([
                    { id: "filled", label: "Filled" },
                    { id: "outline", label: "Outline" },
                    { id: "ring", label: "Ring" },
                    { id: "square", label: "Square" },
                    { id: "tick", label: "Tick only" },
                    { id: "dot", label: "Dot" },
                    { id: "subtle", label: "Subtle" },
                  ] as const).map((option) => {
                    const active = option.id === currentCheckStyle;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => onSelectCheckStyle(option.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-teal-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showFamilyStylePicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Family widget
                </p>
                <div className="flex flex-wrap gap-1">
                  {([
                    { id: "card", label: "Card" },
                    { id: "fieldset", label: "Fieldset" },
                    { id: "outline", label: "Outline" },
                    { id: "floating", label: "Floating" },
                    { id: "tint", label: "Tint" },
                    { id: "underline", label: "Underline" },
                    { id: "bracket", label: "Bracket" },
                  ] as const).map((option) => {
                    const active = option.id === currentFamilyStyle;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => onSelectFamilyStyle(option.id)}
                        className={`cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-cyan-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showTeaserLayoutPicker && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 px-1 pb-1.5">
                  Teaser layout
                </p>
                <div className="flex gap-1">
                  {([
                    { id: "grid-2", label: "2 × 2" },
                    { id: "grid-3", label: "Row of 3" },
                  ] as const).map((layout) => {
                    const active = layout.id === currentTeaserLayout;
                    return (
                      <button
                        key={layout.id}
                        type="button"
                        onClick={() => onSelectTeaserLayout(layout.id)}
                        className={`flex-1 cursor-pointer rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-sky-600 text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {layout.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showUnlockedToggle && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <label className="flex items-center justify-between gap-2 cursor-pointer px-1">
                  <span className="text-[11px] font-medium text-slate-600">Preview unlocked state</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={currentUnlocked}
                    onClick={() => onToggleUnlocked(!currentUnlocked)}
                    className={`relative h-5 w-9 cursor-pointer rounded-full transition-colors ${
                      currentUnlocked ? "bg-emerald-500" : "bg-slate-200"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                        currentUnlocked ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </label>
                <p className="text-[10px] text-slate-400 px-1 mt-1.5 leading-snug">
                  Simulates course completion to preview the post-unlock layout.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

class VariantBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: unknown) { console.error("[home-lab] variant crashed:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="max-w-3xl mx-auto mt-8 px-4 sm:px-6">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
            <h2 className="text-lg font-semibold text-red-900">This variant crashed</h2>
            <pre className="mt-3 whitespace-pre-wrap text-xs text-red-800 font-mono">
              {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ─── Shared hooks ─────────────────────────────────────────────────── */

function useHomeData() {
  const { user: clerkUser } = useUser();
  const userQuery = clerkUser ? getUserFromClerkId(clerkUser.id) : { data: undefined };
  const dailyTipQuery = getDailyTip();
  const missionsQuery = useMissions();
  const scamOfTheDayQuery = getScamOfTheDay();

  const userName = userQuery?.data?.name || "there";
  const guardianPoints = Math.max(0, userQuery?.data?.exp ?? 0);
  const guardianLevel = Math.floor(guardianPoints / 100) + 1;
  const levelProgress = guardianPoints % 100;
  const levelProgressPercent = Math.min(100, Math.round((levelProgress / 100) * 100));

  return {
    userQuery,
    dailyTipQuery,
    missionsQuery,
    scamOfTheDayQuery,
    userName,
    guardianPoints,
    guardianLevel,
    levelProgress,
    levelProgressPercent,
  };
}

/* ─── Shared widgets ───────────────────────────────────────────────── */
/* useCourseProgress moved to @/hooks/useCourseProgress (imported above) so
   the home dashboard doesn't bundle every learning-path slide. */

function CourseHeroCard({
  market,
  size = "default",
}: {
  market?: string | null;
  size?: "default" | "large";
}) {
  const title = market === "US" || market === "CA" ? "Scam Defense Fundamentals" : "Scam Defence Fundamentals";
  const { resumeRoute, hasProgress, completedChapters, totalChapters } = useCourseProgress();
  const isLarge = size === "large";

  return (
    <Link
      to={resumeRoute}
      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
      className="group block overflow-hidden rounded-3xl transition-all hover:shadow-xl"
      style={{ backgroundImage: `url(${courseBg})`, backgroundSize: "cover" }}
    >
      <div className={`flex items-center gap-4 ${isLarge ? "p-7 sm:p-8" : "p-5 sm:p-6"}`}>
        <div className="flex-1 min-w-0 space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-4 py-1.5 text-sm font-semibold text-violet-700">
            <FaTrophy />
            Earn 50 Guardian Points
          </div>
          <h3 className={`font-semibold leading-tight text-slate-900 ${isLarge ? "text-2xl sm:text-3xl" : "text-xl"}`}>
            {title}
          </h3>
          <p className={`leading-relaxed text-slate-600 ${isLarge ? "text-base max-w-md" : "text-[15px] line-clamp-2"}`}>
            Build the skills to spot, stop, and respond to scams before they cause harm.
          </p>
          {totalChapters > 0 && (
            <div className="pt-1 space-y-1.5 max-w-[220px]">
              <span className="block text-sm font-medium text-slate-600">
                {completedChapters} of {totalChapters} chapters
              </span>
              <div className="h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
                <div
                  className="h-full rounded-full bg-violet-600 transition-all"
                  style={{ width: `${(completedChapters / totalChapters) * 100}%` }}
                />
              </div>
            </div>
          )}
          <span className={`inline-flex items-center gap-1.5 rounded-full bg-violet-600 font-semibold text-white transition group-hover:bg-violet-700 ${
            isLarge ? "px-5 py-2.5 text-base" : "px-4 py-2 text-sm"
          }`}>
            {hasProgress ? "Continue guide" : "Start guide"} <HiOutlineArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
        <div className={`hidden sm:block shrink-0 ${isLarge ? "w-[280px]" : "w-[220px]"}`}>
          <img
            src={courseGraphic}
            alt=""
            className="w-full object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </div>
    </Link>
  );
}

function ScamCheckerBar() {
  return (
    <Link
      to="/tools"
      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
      className="group flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.07)]"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-700">
        <SearchCheck className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold text-slate-900">Check something suspicious</p>
        <p className="text-sm text-slate-500">Paste a message, link, or describe a situation</p>
      </div>
      <HiOutlineChevronRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function ScamOfWeekLink({ scamOfTheDay }: { scamOfTheDay: ScamOfTheDay }) {
  const slug = scamOfTheDay.scam?.slug;
  const title = scamOfTheDay.scam?.title || scamOfTheDay.title;
  const description = scamOfTheDay.scam?.description || scamOfTheDay.shortDescription;
  const image = scamOfTheDay.scam?.image;

  const inner = (
    <>
      <div className="flex items-center gap-3">
        {image && (
          <img src={image} alt="" className="w-10 h-10 shrink-0 object-cover rounded-lg" />
        )}
        <h3 className="text-lg font-semibold text-slate-900 leading-snug line-clamp-2">{title}</h3>
      </div>
      <p className="text-base text-slate-500 mt-1.5 leading-relaxed line-clamp-4">{description}</p>
      <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-violet-600">
        Learn more <HiOutlineChevronRight className="h-3.5 w-3.5" />
      </span>
    </>
  );

  if (slug) {
    return (
      <Link
        to="/scam-library/$slug"
        params={{ slug }}
        search={{ invitationcode: undefined, __clerk_ticket: undefined }}
        className="block w-full text-left rounded-2xl transition-colors hover:bg-slate-50/60"
      >
        {inner}
      </Link>
    );
  }

  return (
    <Link
      to="/scam-library"
      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
      className="block w-full text-left rounded-2xl transition-colors hover:bg-slate-50/60"
    >
      {inner}
    </Link>
  );
}

function ScamRadarCard({
  scamOfTheDay,
  isLoading,
}: {
  scamOfTheDay: ScamOfTheDay | undefined;
  isLoading: boolean;
}) {
  return (
    <div className="rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] p-6 sm:p-7 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-red-50 text-red-600">
            <HiOutlineShieldExclamation className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Scam Radar</h2>
            <p className="text-sm text-slate-400">Threats to watch out for</p>
          </div>
        </div>
        <Link
          to="/scam-library"
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          View all
        </Link>
      </div>

      {isLoading ? (
        <div className="mt-6 animate-pulse">
          <div className="rounded-2xl bg-slate-50 p-5">
            <div className="h-5 w-4/5 rounded bg-slate-200/60 mb-2" />
            <div className="h-3.5 w-full rounded bg-slate-200/40 mb-2" />
            <div className="h-3.5 w-2/3 rounded bg-slate-200/40" />
          </div>
        </div>
      ) : scamOfTheDay ? (
        <div className="mt-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400 mb-3">
            Scam of the week
          </p>
          <ScamOfWeekLink scamOfTheDay={scamOfTheDay} />
        </div>
      ) : (
        <div className="mt-6 rounded-2xl bg-slate-50 px-5 py-8 text-center">
          <p className="text-sm text-slate-500">No scams to display yet</p>
        </div>
      )}
    </div>
  );
}

function SecondaryLinksRow() {
  const items = [
    { label: "Breach Monitor", to: "/footprint" },
    { label: "Report a Scam", to: "/report-a-scam" },
    { label: "Ask for Advice", to: "/contact-support" },
    { label: "Saved", to: "/saved" },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-[0_0_0_1px_rgba(0,0,0,0.04)] transition-colors hover:bg-slate-900 hover:text-white"
        >
          {item.label}
        </Link>
      ))}
    </div>
  );
}

// A small dashboard-style preview for the Breach Monitor card. Shows overall
// coverage (x of y accounts protected) with a progress bar, plus the single most
// important action item. Reassures when clear, triages when not.
function BreachMonitorPreview() {
  // Mocked demo state — protected count + single action. Wire up to real data later.
  const protectedCount = 3;
  const totalAccounts = 4;
  const pct = Math.round((protectedCount / totalAccounts) * 100);
  const actionItem = { label: "Rotate Chase password", urgency: "amber" as "amber" | "red" | null };

  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[20px] font-semibold leading-none text-slate-900 tabular-nums tracking-tight">
          {protectedCount}
          <span className="text-[13px] font-medium text-slate-400"> / {totalAccounts}</span>
        </p>
        <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap">Protected</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 transition-[width] duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      {actionItem ? (
        <div className="flex items-start gap-1.5 pt-0.5">
          <span
            className={`mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full ${
              actionItem.urgency === "red" ? "bg-red-500" : "bg-amber-500"
            }`}
          />
          <p className="text-[12px] leading-tight text-slate-700 font-medium line-clamp-2">
            {actionItem.label}
          </p>
        </div>
      ) : (
        <p className="text-[12px] text-slate-500 pt-0.5">No action needed</p>
      )}
    </div>
  );
}

function UnlockedTeaserCard({
  title,
  subtitle,
  to,
  icon,
  tone,
  orientation = "horizontal",
  preview,
}: {
  title: string;
  subtitle: string;
  to: string;
  icon: ReactNode;
  tone: string;
  orientation?: "horizontal" | "vertical";
  preview?: ReactNode;
}) {
  const isVertical = orientation === "vertical";
  return (
    <Link
      to={to}
      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
      className={`group relative block rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.07)] ${
        isVertical ? "p-5 h-full" : "p-6 sm:p-7"
      }`}
    >
      {isVertical ? (
        <div className="flex h-full flex-col">
          <div className={`flex h-12 w-12 items-center justify-center rounded-[14px] ${tone}`}>
            <span className="[&_svg]:h-[22px] [&_svg]:w-[22px]">{icon}</span>
          </div>
          <h2 className="mt-4 text-[17px] font-semibold text-slate-900 leading-tight tracking-tight">
            {title}
          </h2>
          {preview ? (
            <div className="mt-2 flex-1">{preview}</div>
          ) : (
            <p className="mt-1 text-[13px] text-slate-500 flex-1">{subtitle}</p>
          )}
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
            Open
            <HiOutlineChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-[13px] ${tone}`}>
              {icon}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
              <p className="text-sm text-slate-500">{subtitle}</p>
            </div>
          </div>
          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
            Open
            <HiOutlineChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </>
      )}
    </Link>
  );
}

function LockedCard({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="relative rounded-2xl bg-white/60 p-6 sm:p-7 shadow-[0_0_0_1px_rgba(0,0,0,0.03)] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-slate-100/50 backdrop-blur-[2px]" />
      <div className="relative">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-slate-100 text-slate-400">
            <HiOutlineLockClosed className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-500">{title}</h2>
            <p className="text-sm text-slate-400">{subtitle}</p>
          </div>
        </div>
        <p className="mt-5 text-sm text-slate-400">Unlocks when you finish Scam Fundamentals.</p>
      </div>
    </div>
  );
}

/* ─── Variant: Current (baseline) ─────────────────────────────────── */

function VariantCurrent() {
  const { userQuery, dailyTipQuery, scamOfTheDayQuery, userName, guardianPoints, guardianLevel, levelProgress, levelProgressPercent } = useHomeData();

  return (
    <div className="max-w-6xl mx-auto pt-8 pb-16 px-4 sm:px-6">
      <div className="space-y-10">
        <section className="py-1">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-8">
            <div className="max-w-2xl flex-1 min-w-0">
              <h1 className="text-[clamp(1.95rem,4.1vw,3rem)] font-semibold leading-[1.06] tracking-tight text-slate-900">
                Welcome back, {userName}
              </h1>
              <p className="mt-3 text-[clamp(1rem,1.75vw,1.125rem)] leading-relaxed text-slate-600">
                <span className="font-semibold text-slate-900">Daily tip:</span>{" "}
                <span className="text-slate-600">
                  {dailyTipQuery.data?.shortDescription || "Small, practical steps that keep you a step ahead."}
                </span>
              </p>
            </div>
            <GuardianPointsCard
              points={guardianPoints}
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
            />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">Quick actions</h2>
          <QuickActionsCard />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-6">
          <div className="rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] p-6 sm:p-7 flex flex-col">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-violet-50 text-violet-600">
                <HiOutlineAcademicCap className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Up Next</h2>
                <p className="text-sm text-slate-400">Your next steps</p>
              </div>
            </div>
            <div className="mt-6">
              <CourseHeroCard market={userQuery?.data?.market} />
            </div>
          </div>
          <ScamRadarCard scamOfTheDay={scamOfTheDayQuery.data ?? undefined} isLoading={scamOfTheDayQuery.isLoading} />
        </section>

        <section>
          <ThreatFeedCard />
        </section>
      </div>
    </div>
  );
}

/* ─── Variant A: Up Next First ────────────────────────────────────── */

function VariantA() {
  const { userQuery, dailyTipQuery, scamOfTheDayQuery, userName } = useHomeData();

  return (
    <div className="max-w-6xl mx-auto pt-8 pb-16 px-4 sm:px-6">
      <div className="space-y-8">
        {/* Slim hero */}
        <section>
          <h1 className="text-[clamp(1.75rem,3.6vw,2.5rem)] font-semibold leading-tight tracking-tight text-slate-900">
            Welcome back, {userName}
          </h1>
          <p className="mt-2 text-base text-slate-500">
            {dailyTipQuery.data?.shortDescription || "Small, practical steps that keep you a step ahead."}
          </p>
        </section>

        {/* Course as hero */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <HiOutlineAcademicCap className="h-5 w-5 text-violet-600" />
            <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-violet-600">Up next for you</h2>
          </div>
          <CourseHeroCard market={userQuery?.data?.market} size="large" />
        </section>

        {/* Slim scam checker utility */}
        <section>
          <ScamCheckerBar />
        </section>

        {/* Radar + secondary */}
        <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
          <ScamRadarCard scamOfTheDay={scamOfTheDayQuery.data ?? undefined} isLoading={scamOfTheDayQuery.isLoading} />
          <div className="rounded-2xl bg-white p-6 sm:p-7 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
            <h2 className="text-lg font-semibold text-slate-900">More tools</h2>
            <p className="text-sm text-slate-400 mt-1">Reach for these when you need them</p>
            <div className="mt-5 divide-y divide-slate-100">
              {[
                { label: "Breach Monitor", desc: "See if your data has leaked", to: "/footprint" },
                { label: "Report a Scam", desc: "Warn others about what you've seen", to: "/report-a-scam" },
                { label: "Ask for Advice", desc: "Talk to a security expert", to: "/contact-support" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                  className="group flex items-center justify-between py-3.5 first:pt-0 last:pb-0"
                >
                  <div>
                    <p className="text-[15px] font-semibold text-slate-900 group-hover:text-violet-700 transition-colors">{item.label}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                  <HiOutlineChevronRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ─── Variant B: Single Column ─────────────────────────────────────── */

function VariantB() {
  const { userQuery, dailyTipQuery, scamOfTheDayQuery, userName } = useHomeData();

  return (
    <div className="max-w-2xl mx-auto pt-8 pb-16 px-4 sm:px-6">
      <div className="space-y-8">
        <section>
          <p className="text-sm font-medium text-slate-500">Welcome back</p>
          <h1 className="text-[clamp(1.75rem,3.6vw,2.25rem)] font-semibold leading-tight tracking-tight text-slate-900 mt-1">
            {userName}
          </h1>
          {dailyTipQuery.data && (
            <div className="mt-5 rounded-2xl bg-white px-5 py-4 shadow-[0_0_0_1px_rgba(0,0,0,0.03)]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-amber-600">Daily tip</p>
              <p className="text-sm text-slate-700 mt-1 leading-relaxed">{dailyTipQuery.data.shortDescription}</p>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <HiOutlineAcademicCap className="h-5 w-5 text-violet-600" />
            <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-violet-600">Up next for you</h2>
          </div>
          <CourseHeroCard market={userQuery?.data?.market} size="large" />
        </section>

        <section>
          <ScamCheckerBar />
        </section>

        <section>
          <ScamRadarCard scamOfTheDay={scamOfTheDayQuery.data ?? undefined} isLoading={scamOfTheDayQuery.isLoading} />
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-slate-400">More tools</h2>
          <SecondaryLinksRow />
        </section>
      </div>
    </div>
  );
}

/* ─── Variant C: Focus Mode (pure + teaser + rail) ─────────────────── */

type RailItem = {
  label: string;
  short: string;
  desc: string;
  to: string;
  icon: ReactNode;
  tone: string;
  iconColor: string;
  bar: string;
  dot: string;
  tint: string;
  group: "check" | "monitor" | "act";
};

const RAIL_ITEMS: RailItem[] = [
  { label: "Scam Checker", short: "Check", desc: "Check suspicious content", to: "/tools", icon: <SearchCheck className="h-4 w-4" />, tone: "bg-blue-50 text-blue-600", iconColor: "text-blue-600", bar: "bg-blue-500", dot: "bg-blue-500", tint: "bg-blue-50/80", group: "check" },
  { label: "Breach Monitor", short: "Breaches", desc: "See exposed data", to: "/footprint", icon: <FiShield className="h-4 w-4" />, tone: "bg-teal-50 text-teal-600", iconColor: "text-teal-600", bar: "bg-teal-500", dot: "bg-teal-500", tint: "bg-teal-50/80", group: "monitor" },
  { label: "Scam Library", short: "Library", desc: "Browse known scams", to: "/scam-library", icon: <HiOutlineShieldExclamation className="h-4 w-4" />, tone: "bg-orange-50 text-orange-600", iconColor: "text-orange-600", bar: "bg-orange-500", dot: "bg-orange-500", tint: "bg-orange-50/80", group: "monitor" },
  { label: "Report a Scam", short: "Report", desc: "Flag suspicious activity", to: "/report-a-scam", icon: <FiAlertTriangle className="h-4 w-4" />, tone: "bg-red-50 text-red-500", iconColor: "text-red-500", bar: "bg-red-500", dot: "bg-red-500", tint: "bg-red-50/80", group: "act" },
  { label: "Ask for Advice", short: "Ask", desc: "Talk to our experts", to: "/contact-support", icon: <FiMessageCircle className="h-4 w-4" />, tone: "bg-violet-50 text-violet-600", iconColor: "text-violet-600", bar: "bg-violet-500", dot: "bg-violet-500", tint: "bg-violet-50/80", group: "act" },
  { label: "Saved", short: "Saved", desc: "Your saved items", to: "/saved", icon: <HiOutlineAcademicCap className="h-4 w-4" />, tone: "bg-slate-50 text-slate-600", iconColor: "text-slate-600", bar: "bg-slate-400", dot: "bg-slate-400", tint: "bg-slate-50/80", group: "act" },
];

function QuickActionsRail() {
  const { rail } = Route.useSearch();
  if (rail === "minimal") return <RailMinimal />;
  if (rail === "grouped") return <RailGrouped />;
  if (rail === "icons") return <RailIcons />;
  if (rail === "pills") return <RailPills />;
  if (rail === "numbered") return <RailNumbered />;
  if (rail === "apple") return <RailApple />;
  if (rail === "editorial") return <RailEditorial />;
  if (rail === "strip") return <RailStrip />;
  if (rail === "big-icons") return <RailBigIcons />;
  if (rail === "ghost") return <RailGhost />;
  if (rail === "tinted") return <RailTinted />;
  if (rail === "menu") return <RailMenu />;
  if (rail === "hero") return <RailHero />;
  return <RailDefault />;
}

const RAIL_HEADER = "text-[14px] font-semibold text-slate-900 tracking-tight";

function RailDefault() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.06)] p-3.5">
      <p className={`${RAIL_HEADER} px-2 pt-1 pb-2.5`}>Quick actions</p>
      <div className="flex flex-col gap-0.5">
        {RAIL_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition-colors hover:bg-slate-50"
          >
            <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${item.tone}`}>
              {item.icon}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold text-slate-900 leading-tight truncate">{item.label}</p>
              <p className="text-[12px] text-slate-500 leading-tight truncate mt-0.5">{item.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RailMinimal() {
  return (
    <div className="rounded-2xl bg-white/60 border border-slate-200/60 px-2 py-2">
      <p className={`${RAIL_HEADER} px-3 pt-2 pb-3`}>Quick actions</p>
      <div className="flex flex-col">
        {RAIL_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group flex items-center justify-between rounded-lg px-3 py-3 transition-colors hover:bg-slate-900/[0.04]"
          >
            <span className="text-[16px] font-medium text-slate-800 group-hover:text-slate-900">{item.label}</span>
            <HiOutlineChevronRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function RailGrouped() {
  const groups = [
    { key: "check", title: "Detect" },
    { key: "monitor", title: "Protect" },
    { key: "act", title: "Respond" },
  ] as const;
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.06)] overflow-hidden">
      {groups.map((group, gi) => {
        const items = RAIL_ITEMS.filter((i) => i.group === group.key);
        return (
          <div key={group.key} className={gi > 0 ? "border-t border-slate-100" : ""}>
            <p className="text-[14px] font-semibold text-slate-900 tracking-tight px-4 pt-4 pb-2">
              {group.title}
            </p>
            <div className="flex flex-col pb-3">
              {items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                  className="group flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50"
                >
                  <span className={`${item.iconColor} flex-shrink-0 transition-transform group-hover:scale-110`}>
                    {item.icon}
                  </span>
                  <span className="text-[15px] font-semibold text-slate-900 flex-1 truncate tracking-tight">
                    {item.label}
                  </span>
                  <HiOutlineChevronRight className="h-3.5 w-3.5 text-slate-300 opacity-0 -translate-x-0.5 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RailIcons() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.06)] p-3">
      <p className={`${RAIL_HEADER} px-1 pt-1 pb-2.5`}>Quick actions</p>
      <div className="grid grid-cols-2 gap-2">
        {RAIL_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group flex flex-col items-center justify-center gap-2 rounded-xl aspect-square p-2.5 transition-colors hover:bg-slate-50 border border-transparent hover:border-slate-200/60"
            title={item.desc}
          >
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${item.tone}`}>
              {item.icon}
            </div>
            <span className="text-[13px] font-semibold text-slate-700 text-center leading-tight">
              {item.short}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RailPills() {
  return (
    <div className="flex flex-col gap-2">
      <p className={`${RAIL_HEADER} px-3 pt-1 pb-1`}>Quick actions</p>
      {RAIL_ITEMS.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="group flex items-center gap-3 rounded-full bg-white/90 backdrop-blur border border-slate-200/80 px-4 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] hover:-translate-y-[1px]"
        >
          <span className={`${item.iconColor} flex-shrink-0`}>{item.icon}</span>
          <span className="text-[15px] font-semibold text-slate-800 group-hover:text-slate-900 truncate">
            {item.label}
          </span>
        </Link>
      ))}
    </div>
  );
}

function RailNumbered() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.06)] p-3">
      <p className={`${RAIL_HEADER} px-2 pt-1 pb-2.5`}>Quick actions</p>
      <div className="flex flex-col">
        {RAIL_ITEMS.map((item, i) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group flex items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-slate-50"
          >
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-[12px] font-semibold text-slate-400 tabular-nums group-hover:text-slate-600">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-[15px] font-medium text-slate-800 flex-1 truncate group-hover:text-slate-900">
              {item.label}
            </span>
            <HiOutlineChevronRight className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ─── New rail designs ─────────────────────────────────────────────── */

// Apple sidebar — native macOS Finder/Mail feel. Subtle tinted hover, small mono icon
// in slate-500, generous vertical rhythm, large labels.
function RailApple() {
  return (
    <div className="rounded-xl bg-slate-100/60 p-1.5">
      <p className={`${RAIL_HEADER} px-3 pt-2 pb-1.5`}>Quick actions</p>
      <div className="flex flex-col">
        {RAIL_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group flex items-center gap-3 rounded-[10px] px-3 py-2.5 transition-colors hover:bg-white"
          >
            <span className="text-slate-500 group-hover:text-slate-700 flex-shrink-0">{item.icon}</span>
            <span className="text-[15px] font-medium text-slate-800 truncate">{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Editorial — no card chrome, no icons. Serif-ish weight on labels via tracking-tight,
// large colored numeral accents on the left. Magazine chapter list feel.
function RailEditorial() {
  return (
    <div className="px-1">
      <p className={`${RAIL_HEADER} px-1 pt-1 pb-3`}>Quick actions</p>
      <div className="flex flex-col">
        {RAIL_ITEMS.map((item, i) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group flex items-baseline gap-3 py-3 border-b border-slate-200/60 last:border-b-0 transition-colors"
          >
            <span className={`${item.iconColor} text-[18px] font-semibold tabular-nums shrink-0 leading-none`}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-[16px] font-semibold text-slate-900 tracking-tight leading-tight truncate group-hover:text-slate-700">
              {item.label}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Color strip — each item gets a colored vertical bar on the left matching its
// category. Label is large and dominant. Bar grows on hover. Card chrome is minimal.
function RailStrip() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.06)] p-3">
      <p className="text-[14px] font-semibold text-slate-900 tracking-tight px-3 pt-2 pb-3">
        Quick actions
      </p>
      <div className="flex flex-col gap-0.5">
        {RAIL_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group relative flex items-center gap-3 rounded-lg pl-4 pr-3 py-3 transition-colors hover:bg-slate-50"
          >
            <span
              className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full ${item.bar} transition-all h-5 group-hover:h-8 group-hover:w-[4px]`}
            />
            <span className="text-[15px] font-semibold text-slate-900 tracking-tight truncate flex-1">
              {item.label}
            </span>
            <HiOutlineChevronRight className="h-3.5 w-3.5 text-slate-300 opacity-0 -translate-x-0.5 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// Big icons — confident and punchy. Larger colored icon tiles, bigger labels,
// descriptions hidden until hover to keep scan-line tight.
function RailBigIcons() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.06)] p-3">
      <p className={`${RAIL_HEADER} px-2 pt-1 pb-2.5`}>Quick actions</p>
      <div className="flex flex-col gap-1">
        {RAIL_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group flex items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-slate-50"
          >
            <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${item.tone}`}>
              <span className="[&_svg]:h-5 [&_svg]:w-5">{item.icon}</span>
            </div>
            <span className="text-[16px] font-semibold text-slate-900 tracking-tight truncate">
              {item.label}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Ghost accent — no container, no background. Each item is just a bolder text label
// with a small colored dot marker on the left. Maximum restraint, minimum chrome.
function RailGhost() {
  return (
    <div>
      <p className={`${RAIL_HEADER} px-1 pt-1 pb-3`}>Quick actions</p>
      <div className="flex flex-col">
        {RAIL_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="group flex items-center gap-3 py-2.5 transition-colors"
          >
            <span className={`h-2 w-2 rounded-full ${item.dot} flex-shrink-0`} />
            <span className="text-[16px] font-semibold text-slate-800 truncate group-hover:text-slate-950">
              {item.label}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Tinted tiles — each item gets its own faint color-washed card. Colored icons,
// distinct visual identity per action. Most colorful option, still tasteful.
function RailTinted() {
  return (
    <div className="flex flex-col gap-2">
      <p className={`${RAIL_HEADER} px-2 pt-1 pb-0.5`}>Quick actions</p>
      {RAIL_ITEMS.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className={`group flex items-center gap-3 rounded-xl ${item.tint} px-3.5 py-3 border border-white/60 transition-all hover:-translate-y-[1px] hover:shadow-[0_6px_16px_rgba(15,23,42,0.06)]`}
        >
          <span className={`${item.iconColor} flex-shrink-0`}>{item.icon}</span>
          <span className="text-[15px] font-semibold text-slate-900 truncate">{item.label}</span>
        </Link>
      ))}
    </div>
  );
}

// Menu list — looks like a polished dropdown menu. Always-visible right chevron,
// tighter vertical rhythm, clean dividers. Professional and compact.
function RailMenu() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-[0_4px_16px_rgba(15,23,42,0.08)] overflow-hidden">
      <p className={`${RAIL_HEADER} px-4 pt-3 pb-2 border-b border-slate-100`}>Quick actions</p>
      <div className="flex flex-col">
        {RAIL_ITEMS.map((item, i) => (
          <Link
            key={item.to}
            to={item.to}
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className={`group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50 ${
              i > 0 ? "border-t border-slate-100/80" : ""
            }`}
          >
            <span className={`${item.iconColor} flex-shrink-0`}>{item.icon}</span>
            <span className="text-[15px] font-medium text-slate-900 flex-1 truncate">{item.label}</span>
            <HiOutlineChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// Returns the wrapper class for a single tile based on the chosen hero chrome style.
function heroTileClass(style: HeroStyle, includePadding = true): string {
  const pad = includePadding ? "p-4" : "";
  switch (style) {
    case "chrome":
      return `rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] ${pad}`;
    case "outline":
      return `rounded-2xl border border-slate-200 ${pad}`;
    case "flat":
      return `rounded-2xl bg-slate-50/70 ${pad}`;
    case "unified":
    case "dividers":
    case "ghost":
    case "divided":
      return pad; // no per-tile chrome — dividers/unified wrap externally, ghost/divided are transparent
  }
}

function heroTileHoverClass(style: HeroStyle): string {
  switch (style) {
    case "chrome":
      return "transition-shadow hover:shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.07)]";
    case "outline":
      return "transition-colors hover:bg-slate-50/60";
    case "flat":
      return "transition-colors hover:bg-slate-100/70";
    case "unified":
      return "transition-colors hover:bg-slate-50/60";
    case "dividers":
      return "transition-colors hover:bg-slate-50/40";
    case "ghost":
    case "divided":
      return "transition-opacity hover:opacity-80";
  }
}

// Guardian hero — rail is context only. Guardian points on top, Progress checklist
// in the middle, Family presence below. Chrome/styling varies by heroStyle param.
function RailHero() {
  const { heroStyle, guardianInHero, goldTrophy } = Route.useSearch();
  const skipGuardian = guardianInHero === "1";
  const useGoldTrophy = goldTrophy === "1";
  const { userQuery, missionsQuery, guardianPoints, guardianLevel, levelProgress, levelProgressPercent } = useHomeData();
  const { user: clerkUser } = useUser();
  const courseProgress = useCourseProgress();
  const isSponsor = !!userQuery?.data && !userQuery.data.parentUserId;
  const familyQuery = useFamilyProgress({ enabled: isSponsor });
  const hasFamilyPlan =
    isSponsor && !!familyQuery.data &&
    (familyQuery.data.members.length > 0 ||
      familyQuery.data.pendingInvitations.length > 0 ||
      familyQuery.data.totalSeats > familyQuery.data.usedSeats);

  const guardianNode = skipGuardian ? null : (
    <Link
      to="/account"
      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
      className={`relative block ${heroTileClass(heroStyle)} ${heroTileHoverClass(heroStyle)}`}
    >
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-slate-500">Guardian points</p>
          <p className="mt-1 text-[30px] font-semibold leading-none text-slate-900 tabular-nums tracking-tight">
            {guardianPoints}
          </p>
        </div>
        {useGoldTrophy ? (
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px] border border-amber-200/70 bg-gradient-to-br from-amber-50/80 via-white/90 to-yellow-100/60 shadow-[inset_2px_2px_5px_rgb(255_255_255_/_32%),inset_-2px_-2px_6px_rgb(254_215_170_/_65%)]">
            <FaTrophy className="h-[20px] w-[20px] text-amber-500" />
          </div>
        ) : (
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px] border border-violet-100/65 bg-gradient-to-br from-violet-50/70 via-white/90 to-indigo-50/65 shadow-[inset_2px_2px_5px_rgb(255_255_255_/_24%),inset_-2px_-2px_6px_rgb(230_232_255_/_72%)]">
            <img src={gLogoViolet} alt="" className="h-[22px] w-[22px] object-contain" />
          </div>
        )}
      </div>

      <div className="relative mt-2.5">
        <div className={`h-px w-full bg-gradient-to-r ${useGoldTrophy ? "from-amber-200/60 via-yellow-200/35 to-transparent" : "from-violet-200/50 via-indigo-200/35 to-transparent"}`} />
      </div>

      <div className="relative pt-3">
        <div className="flex items-center justify-between text-[12px]">
          <span className="font-semibold text-slate-900">Level {guardianLevel}</span>
          <span className="text-slate-500">{levelProgress}/100</span>
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-slate-200/80 overflow-hidden">
          <div
            className={`h-full rounded-full transition-[width] duration-700 ease-out ${
              useGoldTrophy
                ? "bg-gradient-to-r from-amber-400 to-yellow-500"
                : "bg-gradient-to-r from-violet-500 to-indigo-500"
            }`}
            style={{ width: `${levelProgressPercent}%` }}
          />
        </div>
      </div>
    </Link>
  );

  const progressNode = (
    <ProgressInline
      chrome={heroStyle}
      user={userQuery?.data}
      missions={missionsQuery.data}
      courseComplete={courseProgress.isComplete}
      completedChapters={courseProgress.completedChapters}
      totalChapters={courseProgress.totalChapters}
    />
  );

  const familyNode = hasFamilyPlan && familyQuery.data ? (
    <FamilyInline
      chrome={heroStyle}
      sponsorEmail={clerkUser?.emailAddresses?.[0]?.emailAddress || ""}
      sponsorAvatarConfig={userQuery?.data?.avatarConfig}
      sponsorImageUrl={clerkUser?.imageUrl}
      members={familyQuery.data.members}
      pendingInvitations={familyQuery.data.pendingInvitations}
      totalSeats={familyQuery.data.totalSeats}
      usedSeats={familyQuery.data.usedSeats}
    />
  ) : null;

  // Unified: one outer card, tiles separated by internal border-t lines.
  if (heroStyle === "unified") {
    return (
      <div className="rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] overflow-hidden divide-y divide-slate-100">
        {guardianNode}
        {progressNode}
        {familyNode}
      </div>
    );
  }

  // Dividers: no cards, just hairline divider lines between tiles.
  if (heroStyle === "dividers") {
    return (
      <div className="flex flex-col divide-y divide-slate-200/70">
        {guardianNode}
        {progressNode}
        {familyNode}
      </div>
    );
  }

  // Ghost / Divided: more generous spacing to compensate for no chrome between tiles.
  const gap = heroStyle === "ghost" || heroStyle === "divided" ? "gap-6" : "gap-3";
  return (
    <div className={`flex flex-col ${gap}`}>
      {guardianNode}
      {progressNode}
      {familyNode}
    </div>
  );
}

type ProgressItem = {
  id: string;
  label: string;
  completed: boolean;
  detail?: string;
};

function ProgressInline({
  chrome = "chrome",
  user,
  missions,
  courseComplete,
  completedChapters,
  totalChapters,
}: {
  chrome?: HeroStyle;
  user: User | undefined;
  missions: Mission[] | undefined;
  courseComplete: boolean;
  completedChapters: number;
  totalChapters: number;
}) {
  const { progressStyle } = Route.useSearch();
  const setupMission = missions?.find((m) => m.title === "Complete setup");
  const tourMission = missions?.find((m) => m.title === "Complete the tour");
  const setupCompleted = setupMission?.status === "COMPLETED" || user?.onboardingStep === "COMPLETE";
  const tourCompleted = tourMission?.status === "COMPLETED";

  const items: ProgressItem[] = [
    { id: "setup", label: "Account setup", completed: !!setupCompleted },
    { id: "tour", label: "Quick tour", completed: !!tourCompleted },
    {
      id: "course",
      label: "Scam Fundamentals",
      completed: courseComplete,
      detail:
        totalChapters > 0 && !courseComplete
          ? `${completedChapters} of ${totalChapters} chapters`
          : undefined,
    },
  ];
  const completed = items.filter((i) => i.completed).length;
  const nextIndex = items.findIndex((i) => !i.completed);

  // Wrapper chrome matches the Guardian tile's chrome so the rail feels cohesive.
  const wrapper = (() => {
    switch (chrome) {
      case "chrome":
        return "rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] px-3.5 py-3.5";
      case "outline":
        return "rounded-2xl border border-slate-200 px-3.5 py-3.5";
      case "flat":
        return "rounded-2xl bg-slate-50/70 px-3.5 py-3.5";
      case "unified":
      case "dividers":
        return "px-3.5 py-3.5";
      case "ghost":
      case "divided":
        return "";
    }
  })();

  return (
    <div className={wrapper}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[15px] font-semibold text-slate-900 tracking-tight">Your progress</p>
        <span className="text-[12px] font-medium text-slate-400 tabular-nums">
          {completed} of {items.length}
        </span>
      </div>
      {progressStyle === "bar" ? (
        <ProgressBarBody items={items} completed={completed} />
      ) : (
        <ul className="flex flex-col gap-2.5">
          {items.map((item, i) => (
            <ProgressItemRow key={item.id} item={item} isNext={i === nextIndex} style={progressStyle} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ProgressItemRow({
  item,
  isNext,
  style,
}: {
  item: ProgressItem;
  isNext: boolean;
  style: ProgressStyle;
}) {
  const labelClass = (() => {
    if (item.completed) return "text-slate-400 line-through decoration-slate-300 font-normal";
    if (isNext) return "text-slate-900 font-medium";
    return "text-slate-500 font-normal";
  })();

  return (
    <li className="flex items-start gap-2.5">
      <ProgressMarker item={item} isNext={isNext} style={style} />
      <div className="min-w-0 flex-1">
        <p className={`text-[14px] leading-tight ${labelClass}`}>{item.label}</p>
        {item.detail && !item.completed && (
          <p className="text-[11px] text-slate-400 mt-0.5 leading-tight">{item.detail}</p>
        )}
      </div>
    </li>
  );
}

function ProgressMarker({
  item,
  isNext,
  style,
}: {
  item: ProgressItem;
  isNext: boolean;
  style: ProgressStyle;
}) {
  // Minimal: no marker at all, label styling conveys state
  if (style === "minimal") return null;

  // Quiet check — small emerald glyph, no filled circle
  if (style === "quiet") {
    if (item.completed) {
      return (
        <svg
          viewBox="0 0 16 16"
          className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 8.5l3.5 3.5L13 4" />
        </svg>
      );
    }
    return (
      <span
        className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-[1.5px] ${
          isNext ? "border-slate-400" : "border-slate-200"
        }`}
      />
    );
  }

  // Muted — slate check glyph, zero color
  if (style === "muted") {
    if (item.completed) {
      return (
        <svg
          viewBox="0 0 16 16"
          className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 8.5l3.5 3.5L13 4" />
        </svg>
      );
    }
    return (
      <span
        className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-[1.5px] ${
          isNext ? "border-slate-400" : "border-slate-200"
        }`}
      />
    );
  }

  // Dots — solid filled slate dot for done, empty ring for todo, no checkmark
  if (style === "dots") {
    if (item.completed) {
      return <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-slate-400" />;
    }
    return (
      <span
        className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full border ${
          isNext ? "border-slate-500" : "border-slate-300"
        }`}
      />
    );
  }

  // Default — emerald filled circle with white check (original)
  if (item.completed) {
    return (
      <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500">
        <svg
          viewBox="0 0 16 16"
          className="h-2.5 w-2.5 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 8.5l3.5 3.5L13 4" />
        </svg>
      </span>
    );
  }
  return (
    <span
      className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-[1.5px] ${
        isNext ? "border-violet-400 bg-violet-50" : "border-slate-200 bg-white"
      }`}
    />
  );
}

function ProgressBarBody({
  items,
  completed,
}: {
  items: ProgressItem[];
  completed: number;
}) {
  const pct = items.length > 0 ? Math.round((completed / items.length) * 100) : 0;
  const nextItem = items.find((i) => !i.completed);
  return (
    <div>
      <div className="h-1.5 w-full rounded-full bg-slate-200/80 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-[width] duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      {nextItem ? (
        <p className="mt-2.5 text-[13px] text-slate-700">
          <span className="text-slate-400">Up next · </span>
          <span className="font-semibold text-slate-900">{nextItem.label}</span>
        </p>
      ) : (
        <p className="mt-2.5 text-[13px] font-semibold text-emerald-700">All milestones complete</p>
      )}
    </div>
  );
}

function FamilyInline({
  chrome = "chrome",
  sponsorEmail,
  sponsorAvatarConfig,
  sponsorImageUrl,
  members,
  pendingInvitations,
  totalSeats,
  usedSeats,
}: {
  chrome?: HeroStyle;
  sponsorEmail: string;
  sponsorAvatarConfig: unknown;
  sponsorImageUrl: string | null | undefined;
  members: Array<{ email: string | null; avatarConfig?: unknown; imageUrl?: string | null; name?: string | null; lastActivityDate?: string | null }>;
  pendingInvitations: Array<{ invitedEmail: string }>;
  totalSeats: number;
  usedSeats: number;
}) {
  const openSeats = Math.max(0, totalSeats - usedSeats);

  // Build avatar slots: sponsor → active members → pending invitations → empty seats.
  // Show every seat as its own circle so the rail mirrors the full family plan.
  type Slot =
    | { kind: "sponsor" }
    | { kind: "member"; email: string; avatarConfig?: unknown; imageUrl?: string | null; lastActivityDate?: string | null }
    | { kind: "pending"; email: string }
    | { kind: "empty"; index: number };

  const slots: Slot[] = [
    { kind: "sponsor" },
    ...members.map((m) => ({
      kind: "member" as const,
      email: m.email ?? m.name ?? "member",
      avatarConfig: m.avatarConfig,
      imageUrl: m.imageUrl,
      lastActivityDate: m.lastActivityDate,
    })),
    ...pendingInvitations.map((p) => ({ kind: "pending" as const, email: p.invitedEmail })),
    ...Array.from({ length: openSeats }, (_, i) => ({ kind: "empty" as const, index: i })),
  ];

  const wrapper = (() => {
    switch (chrome) {
      case "chrome":
        return "group block rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] px-3.5 py-3 transition-shadow hover:shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.07)]";
      case "outline":
        return "group block rounded-2xl border border-slate-200 px-3.5 py-3 transition-colors hover:bg-slate-50/60";
      case "flat":
        return "group block rounded-2xl bg-slate-50/70 px-3.5 py-3 transition-colors hover:bg-slate-100/70";
      case "unified":
      case "dividers":
        return "group block px-3.5 py-3 transition-colors hover:bg-slate-50/40";
      case "ghost":
      case "divided":
        return "group block transition-opacity hover:opacity-80";
    }
  })();

  return (
    <Link
      to="/account"
      search={{ tab: "family", invitationcode: undefined, __clerk_ticket: undefined } as never}
      className={wrapper}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[15px] font-semibold text-slate-900 tracking-tight">Family</p>
        <span className="text-[12px] font-medium text-slate-400 tabular-nums">
          {usedSeats} of {totalSeats}
        </span>
      </div>
      <div className="flex items-center flex-wrap gap-2.5">
        {slots.map((slot, i) => {
          if (slot.kind === "sponsor") {
            return (
              <div key="sponsor" className="relative">
                <AvatarDisplay
                  email={sponsorEmail}
                  avatarConfig={sponsorAvatarConfig as Record<string, unknown> | null | undefined}
                  imageUrl={sponsorImageUrl ?? undefined}
                  size={44}
                  className="border-2 border-white shadow-sm ring-2 ring-teal-200/60"
                />
                <div className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-teal-500 border border-white">
                  <Shield className="h-2 w-2 text-white" fill="currentColor" />
                </div>
              </div>
            );
          }
          if (slot.kind === "member") {
            const active = slot.lastActivityDate
              ? Date.now() - new Date(slot.lastActivityDate).getTime() < 24 * 60 * 60 * 1000
              : false;
            const recent = slot.lastActivityDate
              ? Date.now() - new Date(slot.lastActivityDate).getTime() < 7 * 24 * 60 * 60 * 1000
              : false;
            const dotColor = active ? "bg-emerald-500" : recent ? "bg-amber-500" : "bg-slate-300";
            return (
              <div key={`m-${slot.email}-${i}`} className="relative">
                <AvatarDisplay
                  email={slot.email}
                  avatarConfig={slot.avatarConfig as Record<string, unknown> | null | undefined}
                  imageUrl={slot.imageUrl ?? undefined}
                  size={44}
                  className="border-2 border-white shadow-sm"
                />
                {slot.lastActivityDate && (
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${dotColor}`}
                  />
                )}
              </div>
            );
          }
          if (slot.kind === "pending") {
            return (
              <div
                key={`p-${slot.email}-${i}`}
                className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-amber-300 bg-amber-50 opacity-70"
                title="Invitation pending"
              >
                <Avvvatars value={slot.email} style="shape" size={40} />
              </div>
            );
          }
          return (
            <div
              key={`empty-${slot.index}`}
              className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-dashed border-slate-200 bg-slate-50/60 transition-colors group-hover:border-teal-300 group-hover:bg-teal-50"
            >
              <UserPlus className="h-4 w-4 text-slate-300 group-hover:text-teal-500" />
            </div>
          );
        })}
      </div>
    </Link>
  );
}

function VariantC({ mode }: { mode: "pure" | "teaser" | "rail" | "rail-live" }) {
  const { userQuery, scamOfTheDayQuery, userName, guardianPoints, guardianLevel, levelProgress, levelProgressPercent } = useHomeData();
  const { user: clerkUser } = useUser();
  const { rail, heroStyle, unlocked, guardianInHero, goldTrophy, teaserLayout } = Route.useSearch();
  const { isComplete } = useCourseProgress();
  const previewUnlocked = unlocked === "1";
  const useGoldTrophy = goldTrophy === "1";
  const showVerticalSeparator = rail === "hero" && heroStyle === "divided";
  const teaserGridClass = teaserLayout === "grid-3"
    ? "grid grid-cols-1 sm:grid-cols-3 gap-4"
    : "grid grid-cols-1 sm:grid-cols-2 gap-4";
  const showThree = teaserLayout === "grid-3";
  const showRail = mode === "rail" || mode === "rail-live";
  const showTeaser = mode === "teaser" || mode === "rail" || mode === "rail-live";
  const useLiveChecker = mode === "rail-live";

  const isSponsor = !!userQuery?.data && !userQuery.data.parentUserId;
  const familyQuery = useFamilyProgress({ enabled: isSponsor && showRail });
  const hasFamilyPlan =
    isSponsor && !!familyQuery.data &&
    (familyQuery.data.members.length > 0 ||
      familyQuery.data.pendingInvitations.length > 0 ||
      familyQuery.data.totalSeats > familyQuery.data.usedSeats);
  const sponsorProps = {
    name: userQuery?.data?.name,
    email: clerkUser?.emailAddresses?.[0]?.emailAddress || "",
    avatarConfig: userQuery?.data?.avatarConfig,
    imageUrl: clerkUser?.imageUrl,
  };
  // Guardian-hero rail has its own inline family section; don't duplicate with the far-left column.
  const showFamilyColumn = hasFamilyPlan && rail !== "hero";

  // After course completion, everyone sees the full dashboard
  if (isComplete) {
    return (
      <div className="max-w-6xl mx-auto pt-8 pb-16 px-4 sm:px-6">
        <div className="space-y-8">
          <section>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 mb-3">
              <HiOutlineAcademicCap className="h-4 w-4" /> Fundamentals complete
            </div>
            <h1 className="text-[clamp(1.75rem,3.6vw,2.5rem)] font-semibold leading-tight tracking-tight text-slate-900">
              Welcome back, {userName}
            </h1>
            <p className="mt-2 text-base text-slate-500">Your dashboard is unlocked. Keep exploring.</p>
          </section>

          <section>
            <ScamCheckerBar />
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
            <ScamRadarCard scamOfTheDay={scamOfTheDayQuery.data ?? undefined} isLoading={scamOfTheDayQuery.isLoading} />
            <div className="rounded-2xl bg-white p-6 sm:p-7 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
              <h2 className="text-lg font-semibold text-slate-900">Next chapter</h2>
              <p className="text-sm text-slate-400 mt-1">You've built the fundamentals. Dive deeper.</p>
              <div className="mt-4">
                <CourseHeroCard market={userQuery?.data?.market} />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-slate-400">Tools</h2>
            <SecondaryLinksRow />
          </section>
        </div>
      </div>
    );
  }

  const showGuardianInHero = guardianInHero === "1" && rail === "hero";

  // Course not complete: focus on the course
  const focusBody = (
    <div className="space-y-8">
      <section>
        {showGuardianInHero ? (
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-6">
            <div className="max-w-xl flex-1 min-w-0">
              <h1 className="text-[clamp(1.75rem,3.6vw,2.5rem)] font-semibold leading-tight tracking-tight text-slate-900">
                Welcome, {userName}
              </h1>
              <p className="mt-2 text-base text-slate-500">
                Start here. Finish Scam Fundamentals to unlock your full dashboard.
              </p>
            </div>
            {/* Compact Guardian card — same visual language as the full one, smaller footprint */}
            <div className="w-full md:w-[260px] shrink-0 rounded-2xl bg-white p-4 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[12px] font-medium text-slate-500">Guardian points</p>
                  <p className="mt-1 text-[28px] font-semibold leading-none text-slate-900 tabular-nums tracking-tight">
                    {guardianPoints}
                  </p>
                </div>
                {useGoldTrophy ? (
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-amber-200/70 bg-gradient-to-br from-amber-50/80 via-white/90 to-yellow-100/60 shadow-[inset_2px_2px_5px_rgb(255_255_255_/_32%),inset_-2px_-2px_6px_rgb(254_215_170_/_65%)]">
                    <FaTrophy className="h-[18px] w-[18px] text-amber-500" />
                  </div>
                ) : (
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-violet-100/65 bg-gradient-to-br from-violet-50/70 via-white/90 to-indigo-50/65 shadow-[inset_2px_2px_5px_rgb(255_255_255_/_24%),inset_-2px_-2px_6px_rgb(230_232_255_/_72%)]">
                    <img src={gLogoViolet} alt="" className="h-[20px] w-[20px] object-contain" />
                  </div>
                )}
              </div>
              <div className={`mt-2.5 h-px w-full bg-gradient-to-r ${useGoldTrophy ? "from-amber-200/60 via-yellow-200/35 to-transparent" : "from-violet-200/50 via-indigo-200/35 to-transparent"}`} />
              <div className="pt-2.5">
                <div className="flex items-center justify-between text-[12px]">
                  <span className="font-semibold text-slate-900">Level {guardianLevel}</span>
                  <span className="text-slate-500">{levelProgress}/100</span>
                </div>
                <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-200/80 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-[width] duration-700 ease-out ${
                      useGoldTrophy
                        ? "bg-gradient-to-r from-amber-400 to-yellow-500"
                        : "bg-gradient-to-r from-violet-500 to-indigo-500"
                    }`}
                    style={{ width: `${levelProgressPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-[clamp(1.75rem,3.6vw,2.5rem)] font-semibold leading-tight tracking-tight text-slate-900">
              Welcome, {userName}
            </h1>
            <p className="mt-2 text-base text-slate-500">
              Start here. Finish Scam Fundamentals to unlock your full dashboard.
            </p>
          </>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <HiOutlineAcademicCap className="h-5 w-5 text-violet-600" />
          <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-violet-600">Start here</h2>
        </div>
        <CourseHeroCard market={userQuery?.data?.market} size="large" />
      </section>

      <section>
        {useLiveChecker ? <InlineScamCheckerCard /> : <ScamCheckerBar />}
      </section>

      {showTeaser && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-slate-400">
            {previewUnlocked ? "Explore more" : "Unlocks after the course"}
          </h2>
          <div className={teaserGridClass}>
            {previewUnlocked ? (
              <>
                <UnlockedTeaserCard
                  title="Scam Radar"
                  subtitle="Weekly threats to watch"
                  to="/scam-library"
                  icon={<HiOutlineShieldExclamation className="h-5 w-5" />}
                  tone="bg-red-50 text-red-500"
                  orientation={showThree ? "vertical" : "horizontal"}
                  preview={
                    showThree ? (
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                          </span>
                          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-red-600">
                            Live
                          </span>
                        </div>
                        <p className="mt-1.5 text-[13px] text-slate-600 line-clamp-2 leading-snug">
                          USPS delivery text scams are surging this week
                        </p>
                      </div>
                    ) : undefined
                  }
                />
                <UnlockedTeaserCard
                  title="Breach Monitor"
                  subtitle="Your data exposure"
                  to="/footprint"
                  icon={<FiShield className="h-5 w-5" />}
                  tone="bg-teal-50 text-teal-600"
                  orientation={showThree ? "vertical" : "horizontal"}
                  preview={showThree ? <BreachMonitorPreview /> : undefined}
                />
                <UnlockedTeaserCard
                  title="Expert Advice"
                  subtitle="Ask our security team"
                  to="/contact-support"
                  icon={<FiMessageCircle className="h-5 w-5" />}
                  tone="bg-violet-50 text-violet-600"
                  orientation={showThree ? "vertical" : "horizontal"}
                  preview={
                    showThree ? (
                      <div>
                        <p className="text-[13px] text-slate-600 leading-snug">
                          Real humans reply in under an hour
                        </p>
                      </div>
                    ) : undefined
                  }
                />
                {!showThree && (
                  <UnlockedTeaserCard
                    title="Report a Scam"
                    subtitle="Warn others"
                    to="/report-a-scam"
                    icon={<FiAlertTriangle className="h-5 w-5" />}
                    tone="bg-orange-50 text-orange-600"
                  />
                )}
              </>
            ) : (
              <>
                <LockedCard title="Scam Radar" subtitle="Weekly threats to watch" />
                <LockedCard title="Breach Monitor" subtitle="Your data exposure" />
                <LockedCard title="Expert Advice" subtitle="Ask our security team" />
                {!showThree && <LockedCard title="Report a Scam" subtitle="Warn others" />}
              </>
            )}
          </div>
          {showThree && previewUnlocked && (
            <div className="pt-1">
              <Link
                to="/report-a-scam"
                search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
              >
                Also: report a scam
                <HiOutlineChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </section>
      )}

      {mode === "pure" && (
        <section className="rounded-2xl border border-dashed border-slate-200 px-5 py-6 text-center">
          <HiOutlineSearch className="mx-auto h-5 w-5 text-slate-400" />
          <p className="mt-2 text-sm text-slate-500">
            Looking for something specific? Use the Scam Checker above, or finish the course to unlock the full dashboard.
          </p>
        </section>
      )}
    </div>
  );

  if (showRail) {
    const gridCols = showFamilyColumn
      ? "lg:grid-cols-[72px_220px_minmax(0,1fr)] xl:grid-cols-[72px_240px_minmax(0,1fr)]"
      : "lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)]";
    return (
      <div className="max-w-6xl mx-auto pt-8 pb-16 px-4 sm:px-6">
        <div className={`grid grid-cols-1 ${gridCols} gap-6 lg:gap-6`}>
          {/* Family avatar rail — far left (hidden for hero rail, which inlines family) */}
          {showFamilyColumn && (
            <div className="hidden lg:block">
              <FamilyAvatarRail sponsor={sponsorProps} />
            </div>
          )}
          {/* Quick-actions rail (with optional vertical separator on hero+divided) */}
          <aside
            className={`lg:sticky lg:top-24 self-start ${
              showVerticalSeparator ? "lg:border-r lg:border-slate-200/70 lg:pr-6" : ""
            }`}
          >
            <QuickActionsRail />
          </aside>
          {/* Centered focus content — wider when Guardian sits in the hero row */}
          <div className={`min-w-0 lg:mx-auto w-full ${showGuardianInHero ? "lg:max-w-3xl" : "lg:max-w-2xl"}`}>
            {focusBody}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pt-8 pb-16 px-4 sm:px-6">
      {focusBody}
    </div>
  );
}

/* ─── Variant D: Home-3 Style ──────────────────────────────────────── */

function InlineScamCheckerCard() {
  const router = useRouter();
  const [scamInput, setScamInput] = useState("");

  const handleScamCheck = () => {
    if (!scamInput.trim()) return;
    const pending = queuePendingTextScamCheck({
      input: scamInput.trim(),
      inputType: "TEXT",
      mode: "message",
    });
    setScamInput("");
    router.navigate({
      to: "/tools/scam-check/processing",
      search: {
        requestId: pending.id,
        invitationcode: undefined,
        __clerk_ticket: undefined,
      },
    });
  };

  return (
    <div className="rounded-3xl border border-white/70 bg-white/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-4 sm:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-3">
        <div className="flex items-center gap-3 lg:flex-shrink-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50">
            <FiSearch className="h-4 w-4 text-blue-500" />
          </div>
          <h2 className="text-base sm:text-lg font-semibold tracking-tight text-slate-900">Scam Checker</h2>
        </div>
        <div className="flex gap-2 lg:flex-1 lg:min-w-0">
          <input
            type="text"
            value={scamInput}
            onChange={(e) => setScamInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleScamCheck();
            }}
            placeholder="Paste a suspicious link, text, or email..."
            className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm sm:text-base text-slate-900 placeholder-slate-400 outline-none transition-all focus:border-slate-300 focus:ring-2 focus:ring-slate-200"
          />
          {scamInput.trim() && (
            <button
              type="button"
              onClick={handleScamCheck}
              className="rounded-xl bg-slate-900 px-4 sm:px-5 py-2.5 text-sm sm:text-base font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 whitespace-nowrap cursor-pointer"
            >
              Analyse
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// MainInlineScamCheckerCard — same paste-and-analyse behaviour as
// InlineScamCheckerCard but wearing Variant Main's flat-white card chrome
// (rounded-2xl, soft shadow, no backdrop-blur) so it sits naturally beside
// the QuickActionCardE tiles in the Quick Actions row.
export function MainInlineScamCheckerCard() {
  const router = useRouter();
  const [scamInput, setScamInput] = useState("");

  const handleScamCheck = () => {
    if (!scamInput.trim()) return;
    const pending = queuePendingTextScamCheck({
      input: scamInput.trim(),
      inputType: "TEXT",
      mode: "message",
    });
    setScamInput("");
    router.navigate({
      to: "/tools/scam-check/processing",
      search: {
        requestId: pending.id,
        invitationcode: undefined,
        __clerk_ticket: undefined,
      },
    });
  };

  return (
    <div className="flex min-h-[112px] items-center gap-4 rounded-[24px] border border-slate-200/80 bg-white/95 px-5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-blue-100 bg-blue-50 text-blue-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] [&_svg]:h-6 [&_svg]:w-6">
        <SearchCheck />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[17px] font-semibold leading-tight tracking-tight text-slate-900">Scam Checker</p>
        <div className="mt-1.5 flex gap-2">
          <input
            type="text"
            value={scamInput}
            onChange={(e) => setScamInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleScamCheck();
            }}
            placeholder="Paste a link, message, or email..."
            className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[14.5px] text-slate-900 placeholder-slate-400 outline-none transition-all focus:border-slate-300 focus:ring-2 focus:ring-slate-200"
          />
          {scamInput.trim() && (
            <button
              type="button"
              onClick={handleScamCheck}
              className="rounded-xl bg-slate-900 px-3.5 py-2 text-[14.5px] font-semibold text-white transition-colors hover:bg-slate-800 whitespace-nowrap cursor-pointer"
            >
              Analyse
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function D_ActionCard({
  to,
  title,
  description,
  icon,
  iconTone,
}: {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  iconTone: string;
}) {
  return (
    <Link
      to={to}
      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
      className="flex items-center gap-3 rounded-3xl border border-white/70 bg-white/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.08)] px-5 py-4 hover:shadow-[0_14px_34px_rgba(15,23,42,0.10)] transition-all group"
    >
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl flex-shrink-0 ${iconTone}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-semibold text-slate-900 leading-tight">{title}</p>
        <p className="text-sm text-slate-500 leading-tight mt-1">{description}</p>
      </div>
    </Link>
  );
}

function D_MissionsWidget({ market }: { market?: string | null }) {
  return (
    <div className="rounded-3xl border border-white/70 bg-white/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-6 sm:p-7 flex flex-col">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-50">
          <HiOutlineAcademicCap className="h-5 w-5 text-violet-500" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 tracking-tight leading-tight">Up Next</h2>
          <p className="text-sm text-slate-400 leading-tight">Your next steps</p>
        </div>
      </div>

      <div className="mt-6">
        <CourseHeroCard market={market} />
      </div>

      <p className="mt-4 text-[13px] text-slate-400 px-1">New steps appear as you progress</p>
    </div>
  );
}

function D_ScamRadarWidget({
  scamOfTheDay,
  scams,
  isLoading,
}: {
  scamOfTheDay: ScamOfTheDay | undefined;
  scams: Scam[] | undefined;
  isLoading: boolean;
}) {
  const trendingScams = useMemo(() => {
    if (!scams) return [];
    const desiredSlugs = ["fake-charity-scams", "health-insurance-scams", "tax-scams"];
    const bySlug = desiredSlugs
      .map((slug) => scams.find((s) => s.slug === slug))
      .filter(Boolean) as Scam[];
    if (bySlug.length >= 3) return bySlug.slice(0, 3);
    const pickedIds = new Set(bySlug.map((s) => s.id));
    const sorted = [...scams]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .filter((s) => !pickedIds.has(s.id));
    return [...bySlug, ...sorted].slice(0, 3);
  }, [scams]);

  const weeklyLinkedScam = scamOfTheDay?.scam;
  const weeklySlug = weeklyLinkedScam?.slug;

  return (
    <div className="rounded-3xl border border-white/70 bg-white/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-6 sm:p-7 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50">
            <FiShield className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 tracking-tight">Scam Radar</h2>
            <p className="text-sm text-slate-400 leading-tight">Threats to watch out for</p>
          </div>
        </div>
        <Link
          to="/scam-library"
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          View all
        </Link>
      </div>

      {isLoading ? (
        <div className="mt-6 space-y-3">
          <div className="h-[120px] rounded-2xl bg-slate-50 animate-pulse" />
          <div className="h-[52px] rounded-xl bg-slate-50 animate-pulse" />
          <div className="h-[52px] rounded-xl bg-slate-50 animate-pulse" />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-5">
          {scamOfTheDay && (
            weeklySlug ? (
              <Link
                to="/scam-library/$slug"
                params={{ slug: weeklySlug }}
                search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                className="block rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100/50 p-4 sm:p-5 transition-colors hover:from-slate-100/80 hover:to-slate-100/70 cursor-pointer overflow-hidden"
              >
                {weeklyLinkedScam?.image && (
                  <img src={weeklyLinkedScam.image} alt="" className="w-full h-28 object-cover rounded-xl mb-3 -mt-1" />
                )}
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Scam of the week</p>
                <h3 className="text-[15px] font-semibold text-slate-900 leading-snug mt-2 line-clamp-1">
                  {weeklyLinkedScam?.title || scamOfTheDay.title}
                </h3>
                <p className="text-[13px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                  {weeklyLinkedScam?.description || scamOfTheDay.shortDescription}
                </p>
              </Link>
            ) : (
              <div className="block rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100/50 p-4 sm:p-5 overflow-hidden">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">Scam of the week</p>
                <h3 className="text-[15px] font-semibold text-slate-900 leading-snug mt-2 line-clamp-1">
                  {scamOfTheDay.title}
                </h3>
                <p className="text-[13px] text-slate-500 mt-1 leading-relaxed line-clamp-2">
                  {scamOfTheDay.shortDescription}
                </p>
              </div>
            )
          )}

          {trendingScams.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400 px-1 mb-2 flex items-center gap-1.5">
                <HiOutlineTrendingUp className="h-3.5 w-3.5" />
                Trending
              </p>
              <div className="space-y-0.5">
                {trendingScams.map((scam) => (
                  <Link
                    key={scam.id}
                    to="/scam-library/$slug"
                    params={{ slug: scam.slug || scam.id }}
                    search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-slate-50 transition-colors group"
                  >
                    {scam.image ? (
                      <img src={scam.image} alt="" className="h-9 w-9 rounded-[10px] object-cover flex-shrink-0" />
                    ) : (
                      <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-slate-100 text-sm flex-shrink-0">
                        {scam.icon || "⚠️"}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 line-clamp-1">{scam.title}</p>
                      {scam.scamCategory && (
                        <p className="text-xs text-slate-400 mt-0.5">{scam.scamCategory.name}</p>
                      )}
                    </div>
                    <HiOutlineChevronRight className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {trendingScams.length === 0 && !scamOfTheDay && (
            <div className="rounded-2xl bg-slate-50 px-5 py-8 text-center">
              <p className="text-sm text-slate-500">No scams to display yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VariantD() {
  const {
    userQuery,
    dailyTipQuery,
    scamOfTheDayQuery,
    userName,
    guardianPoints,
    guardianLevel,
    levelProgress,
    levelProgressPercent,
  } = useHomeData();
  const scamsQuery = getScams();

  const tipShortDescription =
    dailyTipQuery.data?.shortDescription ||
    "Small, practical steps that keep you a step ahead.";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto pt-10 pb-20 px-4 sm:px-6 lg:px-6">
        <div className="space-y-8">
          {/* Hero + Guardian points */}
          <section className="px-1 py-1">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="max-w-2xl">
                <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-2">Guardian Hub</p>
                <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-slate-900">
                  Welcome back, {userName}
                </h1>
                <p className="text-base sm:text-lg text-slate-600 mt-3 leading-relaxed">
                  <span className="font-semibold text-slate-900">Tip:</span> {tipShortDescription}
                </p>
              </div>
              <GuardianPointsCard
                points={guardianPoints}
                level={guardianLevel}
                levelProgress={levelProgress}
                levelProgressPercent={levelProgressPercent}
              />
            </div>
          </section>

          {/* Top row: inline scam checker + action cards */}
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-6">
              <InlineScamCheckerCard />
            </div>
            <div className="lg:col-span-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <D_ActionCard
                to="/report-a-scam"
                title="Report a Scam"
                description="Flag something suspicious."
                icon={<FiAlertTriangle className="h-5 w-5" />}
                iconTone="bg-red-50 text-red-400"
              />
              <D_ActionCard
                to="/contact-support"
                title="Ask for Advice"
                description="Get help from our team."
                icon={<FiMessageCircle className="h-5 w-5" />}
                iconTone="bg-violet-50 text-violet-500"
              />
            </div>
          </section>

          {/* Up Next + Scam Radar */}
          <section className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-6">
            <D_MissionsWidget market={userQuery?.data?.market} />
            <D_ScamRadarWidget
              scamOfTheDay={scamOfTheDayQuery.data ?? undefined}
              scams={scamsQuery.data}
              isLoading={scamOfTheDayQuery.isLoading || scamsQuery.isLoading}
            />
          </section>
        </div>
      </div>
    </div>
  );
}

/* ─── Variant E: Protection Score ────────────────────────────────── */
//
// Mockup-driven layout. Score is mocked-but-plausible: it reads the user's
// existing course progress as one signal so the number isn't pure fiction,
// then layers two demo signals (password habits, device check). Once the
// Defence Tracker spec lands we should swap the inputs for that source of
// truth so the homepage and the Defences page agree.

/* ─── Variant Main: Recommended ship candidate ────────────────────── */
//
// Locked snapshot of the Protection Score variant with the combination
// the team converged on after extensive iteration:
//   • scoreCard = ring
//   • ringStyle = notched (animated ticks)
//   • rightSide = checklist
//   • topBlurb  = proud
//   • badgeStyle = slate
//   • checkStyle = subtle
//
// Configurator toggles do not apply here — this variant is meant to be
// the stable, reviewable reference for replacing the live home.

// FamilyPlanContainer — seven container treatments for the family widget
// in the welcome strip's right slot. Each one wraps the avatar rail with
// different chrome (card / outline / floating / tint / underline / etc).
// The "fieldset" variant is the standout — it embeds the "Family Plan"
// label INSIDE the top border like an HTML <fieldset><legend> pair.
function FamilyPlanContainer({
  variant,
  children,
}: {
  variant: FamilyStyle;
  children: ReactNode;
}) {
  const labelInline = (
    <p className="text-[12.5px] font-medium text-slate-600 inline-flex items-center gap-1.5">
      <FiUsers className="h-3.5 w-3.5 text-slate-400" />
      Family Plan
    </p>
  );

  if (variant === "fieldset") {
    // Label embedded in the top border. The label has its own bg-slate-50
    // (matching the page bg) so it visually "cuts" the border behind it.
    return (
      <div className="relative inline-flex flex-col rounded-2xl border border-slate-200 px-4 pt-5 pb-3">
        <span className="absolute -top-2.5 left-3 inline-flex items-center gap-1.5 bg-[#f5f5f7] px-2 text-[11.5px] font-medium text-slate-600">
          <FiUsers className="h-3.5 w-3.5 text-slate-400" />
          Family Plan
        </span>
        {children}
      </div>
    );
  }

  if (variant === "outline") {
    return (
      <div className="inline-flex flex-col rounded-2xl border border-slate-200 px-4 py-3">
        <div className="mb-2">{labelInline}</div>
        {children}
      </div>
    );
  }

  if (variant === "floating") {
    return (
      <div className="inline-flex flex-col rounded-2xl bg-white px-4 py-3 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="mb-2">{labelInline}</div>
        {children}
      </div>
    );
  }

  if (variant === "tint") {
    return (
      <div className="inline-flex flex-col rounded-2xl bg-violet-50/40 px-4 py-3">
        <div className="mb-2">{labelInline}</div>
        {children}
      </div>
    );
  }

  if (variant === "underline") {
    return (
      <div className="inline-flex flex-col border-b border-slate-200 px-1 pt-2 pb-3">
        <div className="mb-2">{labelInline}</div>
        {children}
      </div>
    );
  }

  if (variant === "bracket") {
    // Two corner brackets ⌐ ⌐ at top corners, none at bottom. Minimalist.
    return (
      <div className="relative inline-flex flex-col px-4 pt-4 pb-2">
        <span className="absolute left-0 top-0 h-3 w-3 border-l border-t border-slate-300" aria-hidden />
        <span className="absolute right-0 top-0 h-3 w-3 border-r border-t border-slate-300" aria-hidden />
        <div className="mb-2">{labelInline}</div>
        {children}
      </div>
    );
  }

  // CARD — default: subtle white wash + thin border + tiny shadow
  return (
    <div className="inline-flex flex-col rounded-2xl bg-white/55 border border-slate-200/60 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)] backdrop-blur-[2px]">
      <div className="mb-2">{labelInline}</div>
      {children}
    </div>
  );
}

function VariantMain() {
  const { user: clerkUser } = useUser();
  const { userQuery, dailyTipQuery, scamOfTheDayQuery, missionsQuery, userName, guardianPoints: realPoints, guardianLevel: realLevel, levelProgress: realLevelProgress, levelProgressPercent: realLevelProgressPercent } = useHomeData();
  const courseProgress = useCourseProgress();
  const { previewLevel, familyStyle } = Route.useSearch();

  // Family plan signals — moved into the welcome strip's top-right slot
  // (replacing the absolute-positioned xl-only rail). Adapts per user role.
  // Sponsors who have paid for additional seats see the inline rail in the
  // hero strip. Invited family members (parentUserId set) see the full
  // FamilyWidgetChild card lower on the page so they know which plan they
  // belong to. Solo users see neither.
  const isSponsor = !!userQuery?.data && !userQuery.data.parentUserId;
  const isChild = !!userQuery?.data?.parentUserId;
  const familyQuery = useFamilyProgress({ enabled: isSponsor });
  const hasFamilyPlan =
    isSponsor && !!familyQuery.data && familyQuery.data.totalSeats > 1;
  const familyRailProps = {
    sponsor: {
      name: userQuery.data?.name,
      email: clerkUser?.emailAddresses?.[0]?.emailAddress || "",
      avatarConfig: userQuery.data?.avatarConfig,
      imageUrl: clerkUser?.imageUrl,
    },
  };

  const previewLevelNum = previewLevel ? Number(previewLevel) : undefined;
  const guardianLevel = previewLevelNum ?? realLevel;
  const levelProgress = previewLevelNum !== undefined ? 30 : realLevelProgress;
  const levelProgressPercent = previewLevelNum !== undefined ? 30 : realLevelProgressPercent;
  const guardianPoints = previewLevelNum !== undefined ? (previewLevelNum - 1) * 100 + 30 : realPoints;

  const tipShortDescription =
    dailyTipQuery.data?.shortDescription ||
    "Take a few minutes to check for new data breaches.";

  const setupMission = missionsQuery.data?.find((m) => m.title === "Complete setup");
  const tourMission = missionsQuery.data?.find((m) => m.title === "Complete the tour");
  const setupComplete =
    setupMission?.status === "COMPLETED" || userQuery?.data?.onboardingStep === "COMPLETE";
  const tourComplete = tourMission?.status === "COMPLETED";
  const coursePct = Math.round(
    (courseProgress.completedChapters / Math.max(1, courseProgress.totalChapters)) * 100,
  );

  const score = useMemo(() => {
    const setupShare = setupComplete ? 33 : 0;
    const tourShare = tourComplete ? 33 : 0;
    const courseShare = Math.round((coursePct / 100) * 34);
    return Math.min(100, setupShare + tourShare + courseShare);
  }, [setupComplete, tourComplete, coursePct]);

  const scoreLabel =
    score >= 85 ? "Strong defences" : score >= 65 ? "Good foundation" : score >= 40 ? "Getting started" : "Just beginning";
  const scoreSummary =
    score >= 85
      ? "Your habits and tools are working together. Keep it up."
      : score >= 65
        ? "You're on the right track. Keep up the good habits to stay safer."
        : score >= 40
          ? "You've made a start. A few more steps will lift your protection."
          : "Let's set up the basics together so you're harder to target.";

  const checklist: { label: string; status: "good" | "progress" | "info"; percent?: number }[] = [
    { label: "Account setup", status: setupComplete ? "good" : "info" },
    { label: "Guided walkthrough", status: tourComplete ? "good" : "info" },
    {
      label: "Fundamentals guide",
      status: courseProgress.isComplete ? "good" : "progress",
      percent: coursePct,
    },
  ];

  const redFlags = [
    "Unexpected delivery texts or emails",
    "Links that ask for personal information",
    "Urgent language or threats about delivery",
  ];

  return (
    <div className="max-w-6xl mx-auto pt-4 pb-16 px-4 sm:px-6">
      <div className="space-y-4">
        {/* Welcome strip + daily tip on the left, labeled family rail on the
            right (when sponsor with a family plan). Vertically centered so
            the rail anchors against the middle of the welcome content. */}
        <section className="px-1 pt-3 pb-4">
          {/* Greeting + family rail. 2-col grid from 480px up so the rail
              stays anchored top-right on every tablet (incl. iPad Mini at
              744px). Only wraps to single-column on phone-portrait widths.
              The greeting column uses minmax(0,1fr) so it can shrink and
              wrap text as needed; the rail column auto-sizes to its
              compact intrinsic width. */}
          <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-[minmax(0,1fr)_auto] min-[480px]:items-center min-[480px]:gap-5 sm:gap-6 lg:gap-8">
            <div className="min-w-0">
              <h1 className="text-[clamp(1.6rem,4vw,3rem)] font-semibold leading-[1.06] tracking-tight text-slate-900">
                Welcome back, {userName}!
              </h1>
              <p className="mt-2 max-w-[60ch] text-[15px] sm:text-[15.5px] leading-relaxed text-slate-600">
                <span className="font-semibold text-slate-900">Daily tip:</span>{" "}
                {tipShortDescription}{" "}
                {dailyTipQuery.data && (
                  <Link
                    to="/learn"
                    search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
                    className="font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
                  >
                    Learn more
                  </Link>
                )}
              </p>
            </div>
            {hasFamilyPlan && (
              <div className="min-[480px]:justify-self-end">
                <FamilyPlanContainer variant={familyStyle}>
                  <FamilyAvatarRail {...familyRailProps} horizontal />
                </FamilyPlanContainer>
              </div>
            )}
            {/* Invited family members get the same compact rail in the same
                slot, but rendered in member mode so it shows their sponsor +
                co-members and skips the invite controls. */}
            {isChild && (
              <div className="min-[480px]:justify-self-end">
                <FamilyPlanContainer variant={familyStyle}>
                  <FamilyAvatarRail mode="member" horizontal />
                </FamilyPlanContainer>
              </div>
            )}
          </div>
        </section>

        {/* Hero: locked Guardian Level + Up Next.
            Stacked on tablet so older-adult readers get full-width cards
            with breathing room and bigger type. Two columns at lg+ where
            the desktop has space for side-by-side. */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,0.96fr)_minmax(0,1.04fr)]">
          <ProtectionScoreCard
            score={score}
            label={scoreLabel}
            summary={scoreSummary}
            checklist={checklist}
            ringStyle="notched"
            level={guardianLevel}
            levelProgress={levelProgress}
            levelProgressPercent={levelProgressPercent}
            points={guardianPoints}
            rightSide="checklist"
            topBlurb="proud"
            userName={userName}
            badgeStyle="slate"
            checkStyle="subtle"
          />
          <UpNextCardE market={userQuery?.data?.market} />
        </section>

        {/* Quick Actions — Scam Checker takes a wider slot with an inline
            input (paste-and-analyse) so the most-used tool is one keystroke
            away. The other two stay as small cards on the right. */}
        <section className="space-y-3">
          <h2 className="px-1 text-[1.05rem] font-semibold tracking-tight text-slate-900">Quick Actions</h2>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <MainInlineScamCheckerCard />
            </div>
            <div className="lg:col-span-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <QuickActionCardE
                to="/footprint"
                icon={<HiOutlineShieldCheck className="h-6 w-6" />}
                tone="border border-violet-100 bg-violet-50 text-violet-700"
                title="Breach Monitor"
                description="See if your data has been leaked."
              />
              <QuickActionCardE
                to="/contact-support"
                icon={<HiOutlineChatAlt2 className="h-6 w-6" />}
                tone="border border-emerald-100 bg-emerald-50 text-emerald-600"
                title="Ask for Advice"
                description="Talk to a real security expert."
              />
            </div>
          </div>
        </section>

        {/* Scam Radar */}
        <ScamRadarWithFlagsCard
          scamOfTheDay={scamOfTheDayQuery.data ?? undefined}
          isLoading={scamOfTheDayQuery.isLoading}
          redFlags={redFlags}
        />
      </div>
    </div>
  );
}

function VariantE() {
  const { userQuery, dailyTipQuery, scamOfTheDayQuery, missionsQuery, userName, guardianPoints: realPoints, guardianLevel: realLevel, levelProgress: realLevelProgress, levelProgressPercent: realLevelProgressPercent } = useHomeData();
  const courseProgress = useCourseProgress();
  const { scoreCard, ringStyle, rightSide, topBlurb, badgeStyle, checkStyle, previewLevel } = Route.useSearch();

  // Preview-level override: when set, swap the real Guardian signals for a
  // mocked snapshot of an earlier (or later) level so we can see how copy
  // and visuals read at different stages without having to rewind progress.
  // Mock progress sits at 30/100 within the chosen level.
  const previewLevelNum = previewLevel ? Number(previewLevel) : undefined;
  const guardianLevel = previewLevelNum ?? realLevel;
  const levelProgress = previewLevelNum !== undefined ? 30 : realLevelProgress;
  const levelProgressPercent = previewLevelNum !== undefined ? 30 : realLevelProgressPercent;
  const guardianPoints = previewLevelNum !== undefined ? (previewLevelNum - 1) * 100 + 30 : realPoints;

  const tipShortDescription =
    dailyTipQuery.data?.shortDescription ||
    "Take a few minutes to check for new data breaches.";

  // Real progress signals — setup, tour, and fundamentals course progress.
  // Once Defence Tracker lands, swap in those signals here too.
  const setupMission = missionsQuery.data?.find((m) => m.title === "Complete setup");
  const tourMission = missionsQuery.data?.find((m) => m.title === "Complete the tour");
  const setupComplete =
    setupMission?.status === "COMPLETED" || userQuery?.data?.onboardingStep === "COMPLETE";
  const tourComplete = tourMission?.status === "COMPLETED";
  const coursePct = Math.round(
    (courseProgress.completedChapters / Math.max(1, courseProgress.totalChapters)) * 100,
  );

  const score = useMemo(() => {
    const setupShare = setupComplete ? 33 : 0;
    const tourShare = tourComplete ? 33 : 0;
    const courseShare = Math.round((coursePct / 100) * 34);
    return Math.min(100, setupShare + tourShare + courseShare);
  }, [setupComplete, tourComplete, coursePct]);

  const scoreLabel =
    score >= 85
      ? "Strong defences"
      : score >= 65
        ? "Good foundation"
        : score >= 40
          ? "Getting started"
          : "Just beginning";

  const scoreSummary =
    score >= 85
      ? "Your habits and tools are working together. Keep it up."
      : score >= 65
        ? "You're on the right track. Keep up the good habits to stay safer."
        : score >= 40
          ? "You've made a start. A few more steps will lift your protection."
          : "Let's set up the basics together so you're harder to target.";

  const checklist: { label: string; status: "good" | "progress" | "info"; percent?: number }[] = [
    { label: "Account setup", status: setupComplete ? "good" : "info" },
    { label: "Guided walkthrough", status: tourComplete ? "good" : "info" },
    {
      label: "Fundamentals guide",
      status: courseProgress.isComplete ? "good" : "progress",
      percent: coursePct,
    },
  ];

  const redFlags = [
    "Unexpected delivery texts or emails",
    "Links that ask for personal information",
    "Urgent language or threats about delivery",
  ];

  return (
    <div className="max-w-6xl mx-auto pt-4 pb-16 px-4 sm:px-6">
      <div className="space-y-4">
        {/* Welcome strip + daily tip */}
        <section className="px-1 pt-3 pb-4">
          <div className="min-w-0">
            <h1 className="text-[clamp(1.95rem,4.1vw,3rem)] font-semibold leading-[1.06] tracking-tight text-slate-900">
              Welcome back, {userName}!
            </h1>
            <p className="mt-2 max-w-[60ch] text-[15px] sm:text-[15.5px] leading-relaxed text-slate-600">
              <span className="font-semibold text-slate-900">Daily tip:</span>{" "}
              {tipShortDescription}{" "}
              {dailyTipQuery.data && (
                <Link
                  to="/learn"
                  search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
                  className="font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
                >
                  Learn more
                </Link>
              )}
            </p>
          </div>
        </section>

        {/* Hero: Protection Score + Up Next */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,0.96fr)_minmax(0,1.04fr)]">
          {scoreCard === "guardian" ? (
            <ProtectionScoreGuardian
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              checklist={checklist}
            />
          ) : scoreCard === "guardian-split" ? (
            <ProtectionScoreGuardianSplit
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              checklist={checklist}
            />
          ) : scoreCard === "competencies" ? (
            <ProtectionScoreCompetencies
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              checklist={checklist}
            />
          ) : scoreCard === "levels" ? (
            <ProtectionScoreLevels
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              checklist={checklist}
            />
          ) : scoreCard === "bento" ? (
            <ProtectionScoreBento
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero" ? (
            <ProtectionScoreHero
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-quiet" ? (
            <ProtectionScoreHeroQuiet
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-arc" ? (
            <ProtectionScoreHeroArc
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-split" ? (
            <ProtectionScoreHeroSplit
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-edge" ? (
            <ProtectionScoreHeroEdge
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-edge-split" ? (
            <ProtectionScoreHeroEdgeSplit
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-edge-balanced" ? (
            <ProtectionScoreHeroEdgeBalanced
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-edge-stacked" ? (
            <ProtectionScoreHeroEdgeStacked
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-edge-tiles" ? (
            <ProtectionScoreHeroEdgeTiles
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-edge-rail" ? (
            <ProtectionScoreHeroEdgeRail
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "hero-edge-minimal" ? (
            <ProtectionScoreHeroEdgeMinimal
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "journey" ? (
            <ProtectionScoreJourney
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "next-action" ? (
            <ProtectionScoreNextAction
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "trajectory" ? (
            <ProtectionScoreTrajectory
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "milestone-gap" ? (
            <ProtectionScoreMilestoneGap
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "skill-grid" ? (
            <ProtectionScoreSkillGrid
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "credit-gauge" ? (
            <ProtectionScoreCreditGauge
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "credit-breakdown" ? (
            <ProtectionScoreCreditBreakdown
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "credit-factors" ? (
            <ProtectionScoreCreditFactors
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "credit-peer" ? (
            <ProtectionScoreCreditPeer
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "credit-audit" ? (
            <ProtectionScoreCreditAudit
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
              checklist={checklist}
            />
          ) : scoreCard === "pdrc-breakdown" ? (
            <ProtectionScorePDRCBreakdown
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
            />
          ) : scoreCard === "pdrc-pillars" ? (
            <ProtectionScorePDRCPillars
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
            />
          ) : scoreCard === "pdrc-radar" ? (
            <ProtectionScorePDRCRadar
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              points={guardianPoints}
              level={guardianLevel}
            />
          ) : scoreCard === "pdrc-cards" ? (
            <ProtectionScorePDRCCards
              score={score}
              label={scoreLabel}
              points={guardianPoints}
              level={guardianLevel}
            />
          ) : scoreCard === "minimal-level" ? (
            <ProtectionScoreMinimalLevel
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              points={guardianPoints}
            />
          ) : scoreCard === "minimal-score" ? (
            <ProtectionScoreMinimalScore
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
            />
          ) : scoreCard === "minimal-hero" ? (
            <ProtectionScoreMinimalHero
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              level={guardianLevel}
            />
          ) : scoreCard === "minimal-twin" ? (
            <ProtectionScoreMinimalTwin
              score={score}
              label={scoreLabel}
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              points={guardianPoints}
            />
          ) : scoreCard === "minimal-ladder" ? (
            <ProtectionScoreMinimalLadder
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              points={guardianPoints}
            />
          ) : (
            <ProtectionScoreCard
              score={score}
              label={scoreLabel}
              summary={scoreSummary}
              checklist={checklist}
              ringStyle={ringStyle}
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              points={guardianPoints}
              rightSide={rightSide}
              topBlurb={topBlurb}
              userName={userName}
              badgeStyle={badgeStyle}
              checkStyle={checkStyle}
            />
          )}
          <UpNextCardE market={userQuery?.data?.market} />
        </section>

        {/* Quick Actions */}
        <section className="space-y-3">
          <h2 className="px-1 text-[1.05rem] font-semibold tracking-tight text-slate-900">Quick Actions</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <QuickActionCardE
              to="/tools"
              icon={<SearchCheck className="h-6 w-6" />}
              tone="border border-blue-100 bg-blue-50 text-blue-700"
              title="Scam Checker"
              description="Quickly check if something's a scam."
            />
            <QuickActionCardE
              to="/footprint"
              icon={<HiOutlineShieldCheck className="h-6 w-6" />}
              tone="border border-violet-100 bg-violet-50 text-violet-700"
              title="Breach Monitor"
              description="Check if your personal data has been exposed."
            />
            <QuickActionCardE
              to="/contact-support"
              icon={<HiOutlineChatAlt2 className="h-6 w-6" />}
              tone="border border-emerald-100 bg-emerald-50 text-emerald-600"
              title="Ask for Advice"
              description="Get help from our team of security experts."
            />
          </div>
        </section>

        {/* Scam Radar with red flags rail */}
        <ScamRadarWithFlagsCard
          scamOfTheDay={scamOfTheDayQuery.data ?? undefined}
          isLoading={scamOfTheDayQuery.isLoading}
          redFlags={redFlags}
        />
      </div>
    </div>
  );
}

/* ─── Variant F: Best of Both ─────────────────────────────────────── */
//
// Synthesises Variant E (Guardian Level card) with the live home (Image 6):
//   • E's progression hero — Guardian Level card with notched ring, identity
//     badge, checklist, points-aware copy. Full-width so it fully owns the
//     "who you are / what's next" question.
//   • Live home's quick actions row — four cards (vs. E's three) including
//     Report a Scam, with the violet/emerald palette discipline from E.
//   • Live home's side-by-side Up Next + Scam Radar — both important
//     surfaces stay above the fold.
//   • E's color discipline throughout — single violet accent family.

function VariantF() {
  const { userQuery, dailyTipQuery, scamOfTheDayQuery, userName, guardianPoints: realPoints, guardianLevel: realLevel, levelProgress: realLevelProgress, levelProgressPercent: realLevelProgressPercent } = useHomeData();
  const { badgeStyle, previewLevel } = Route.useSearch();

  const previewLevelNum = previewLevel ? Number(previewLevel) : undefined;
  const guardianLevel = previewLevelNum ?? realLevel;
  const levelProgress = previewLevelNum !== undefined ? 30 : realLevelProgress;
  const levelProgressPercent = previewLevelNum !== undefined ? 30 : realLevelProgressPercent;
  const guardianPoints = previewLevelNum !== undefined ? (previewLevelNum - 1) * 100 + 30 : realPoints;

  const tipShortDescription =
    dailyTipQuery.data?.shortDescription ||
    "Take a few minutes to check for new data breaches.";

  const redFlags = [
    "Unexpected delivery texts or emails",
    "Links that ask for personal information",
    "Urgent language or threats about delivery",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
    <div className="max-w-6xl mx-auto pt-8 pb-16 px-4 sm:px-6">
      <div className="space-y-6">
        {/* Top row — Guardian Hub eyebrow + welcome on the left, Guardian
            Level inline on the right. Borrows the editorial typography
            from home-3: small eyebrow, large headline, generous whitespace. */}
        <section className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-center lg:gap-6">
          <div className="min-w-0 px-1 py-1 lg:py-2 lg:pr-8">
            <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-2">Guardian Hub</p>
            <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold leading-[1.05] tracking-tight text-slate-900">
              Welcome back, {userName}
            </h1>
            <p className="mt-3 max-w-[52ch] text-[15.5px] sm:text-[16px] leading-relaxed text-slate-600">
              <span className="font-semibold text-slate-900">Tip:</span>{" "}
              {tipShortDescription}{" "}
              {dailyTipQuery.data && (
                <Link
                  to="/learn"
                  search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
                  className="font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
                >
                  Learn more
                </Link>
              )}
            </p>
          </div>

          <div className="lg:pl-2">
            <GuardianLevelInline
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              points={guardianPoints}
              badgeStyle={badgeStyle}
            />
          </div>
        </section>

        {/* Quick actions — borrows home-3 / Variant D's pattern: scam checker
            expanded with an inline input on the left, two mini action cards
            on the right. Faster path to "do something." */}
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <InlineScamCheckerCard />
          </div>
          <div className="lg:col-span-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <D_ActionCard
              to="/footprint"
              title="Breach Monitor"
              description="Check if your data has leaked."
              icon={<HiOutlineShieldCheck className="h-5 w-5" />}
              iconTone="bg-violet-50 text-violet-500"
            />
            <D_ActionCard
              to="/contact-support"
              title="Ask for Advice"
              description="Get help from our team."
              icon={<HiOutlineChatAlt2 className="h-5 w-5" />}
              iconTone="bg-emerald-50 text-emerald-600"
            />
          </div>
        </section>

        {/* Up Next + Scam Radar — side by side, live-home grid.
            Up Next uses Variant D's missions widget (glass-morphism card
            with the Scam Defence Fundamentals course hero inside). */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          <D_MissionsWidget market={userQuery?.data?.market} />
          <ScamRadarWithFlagsCard
            scamOfTheDay={scamOfTheDayQuery.data ?? undefined}
            isLoading={scamOfTheDayQuery.isLoading}
            redFlags={redFlags}
            showRedFlags={false}
          />
        </section>
      </div>
    </div>
    </div>
  );
}

/* ─── Variant G: Bento Grid ───────────────────────────────────────── */
//
// Asymmetric bento layout:
//
//   [welcome banner]
//   ┌──────────────────┬─────────────┐
//   │  Guardian Level  │  Up Next    │
//   │  (ring + check)  ├─────────────┤
//   ├──────────────────┤  Scam Radar │
//   │  Quick Actions   │             │
//   │  (vertical list) │             │
//   └──────────────────┴─────────────┘
//
// Left card is one tall surface (Level + Quick Actions sharing chrome).
// Right is a stack: Up Next on top, Scam Radar below.

function VariantG() {
  const { userQuery, dailyTipQuery, scamOfTheDayQuery, missionsQuery, userName, guardianPoints: realPoints, guardianLevel: realLevel, levelProgress: realLevelProgress, levelProgressPercent: realLevelProgressPercent } = useHomeData();
  const courseProgress = useCourseProgress();
  const { ringStyle, topBlurb, badgeStyle, checkStyle, previewLevel } = Route.useSearch();

  const previewLevelNum = previewLevel ? Number(previewLevel) : undefined;
  const guardianLevel = previewLevelNum ?? realLevel;
  const levelProgress = previewLevelNum !== undefined ? 30 : realLevelProgress;
  const levelProgressPercent = previewLevelNum !== undefined ? 30 : realLevelProgressPercent;
  const guardianPoints = previewLevelNum !== undefined ? (previewLevelNum - 1) * 100 + 30 : realPoints;

  const tipShortDescription =
    dailyTipQuery.data?.shortDescription ||
    "Take a few minutes to check for new data breaches.";

  const setupMission = missionsQuery.data?.find((m) => m.title === "Complete setup");
  const tourMission = missionsQuery.data?.find((m) => m.title === "Complete the tour");
  const setupComplete =
    setupMission?.status === "COMPLETED" || userQuery?.data?.onboardingStep === "COMPLETE";
  const tourComplete = tourMission?.status === "COMPLETED";
  const coursePct = Math.round(
    (courseProgress.completedChapters / Math.max(1, courseProgress.totalChapters)) * 100,
  );

  const score = useMemo(() => {
    const setupShare = setupComplete ? 33 : 0;
    const tourShare = tourComplete ? 33 : 0;
    const courseShare = Math.round((coursePct / 100) * 34);
    return Math.min(100, setupShare + tourShare + courseShare);
  }, [setupComplete, tourComplete, coursePct]);

  const scoreLabel =
    score >= 85 ? "Strong defences" : score >= 65 ? "Good foundation" : score >= 40 ? "Getting started" : "Just beginning";
  const scoreSummary =
    score >= 85
      ? "Your habits and tools are working together. Keep it up."
      : score >= 65
        ? "You're on the right track. Keep up the good habits to stay safer."
        : score >= 40
          ? "You've made a start. A few more steps will lift your protection."
          : "Let's set up the basics together so you're harder to target.";

  const checklist: { label: string; status: "good" | "progress" | "info"; percent?: number }[] = [
    { label: "Account setup", status: setupComplete ? "good" : "info" },
    { label: "Guided walkthrough", status: tourComplete ? "good" : "info" },
    {
      label: "Fundamentals guide",
      status: courseProgress.isComplete ? "good" : "progress",
      percent: coursePct,
    },
  ];

  const redFlags = [
    "Unexpected delivery texts or emails",
    "Links that ask for personal information",
    "Urgent language or threats about delivery",
  ];

  return (
    <div className="max-w-6xl mx-auto pt-4 pb-16 px-4 sm:px-6">
      <div className="space-y-4">
        {/* Welcome strip + daily tip */}
        <section className="px-1 pt-3 pb-4">
          <div className="min-w-0">
            <h1 className="text-[clamp(1.95rem,4.1vw,3rem)] font-semibold leading-[1.06] tracking-tight text-slate-900">
              Welcome back, {userName}!
            </h1>
            <p className="mt-2 max-w-[60ch] text-[15px] sm:text-[15.5px] leading-relaxed text-slate-600">
              <span className="font-semibold text-slate-900">Daily tip:</span>{" "}
              {tipShortDescription}{" "}
              {dailyTipQuery.data && (
                <Link
                  to="/learn"
                  search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
                  className="font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
                >
                  Learn more
                </Link>
              )}
            </p>
          </div>
        </section>

        {/* Bento grid: tall left card, stacked right column */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-start">
          {/* Left column: stacked Guardian Level + Quick Actions cards */}
          <div className="space-y-4">
            {/* Guardian Level card */}
            <div className="rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-violet-100 bg-white">
                  <img src={gLogoViolet} alt="" className="h-[18px] w-[18px] object-contain" />
                </span>
                <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">Your Guardian Level</h2>
                <button
                  type="button"
                  aria-label="About Guardian Level"
                  className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-300 transition-colors hover:text-slate-500"
                >
                  <HiOutlineInformationCircle className="h-[18px] w-[18px]" />
                </button>
              </div>
              <div className="mt-1 grid gap-0 md:grid-cols-[232px_minmax(0,1fr)] md:items-center md:divide-x md:divide-slate-100">
                <div className="relative mx-auto flex h-[232px] w-[232px] items-center justify-center md:mx-0 md:-translate-x-1">
                  <ScoreRing
                    variant={ringStyle}
                    score={score}
                    level={guardianLevel}
                    levelProgressPercent={levelProgressPercent}
                    badgeStyle={badgeStyle}
                  />
                </div>
                <div className="md:pl-5">
                  <ProtectionScoreRightChecklist
                    label={scoreLabel}
                    summary={scoreSummary}
                    checklist={checklist}
                    topBlurb={topBlurb}
                    level={guardianLevel}
                    levelProgress={levelProgress}
                    levelProgressPercent={levelProgressPercent}
                    points={guardianPoints}
                    userName={userName}
                    checkStyle={checkStyle}
                  />
                </div>
              </div>
            </div>

            {/* Quick Actions card */}
            <div className="rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="flex items-center gap-2">
                <FiZap className="h-[18px] w-[18px] text-slate-500" />
                <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">Quick Actions</h2>
              </div>
              <ul className="mt-3 divide-y divide-slate-100">
                <QuickActionListItem
                  to="/tools"
                  icon={<SearchCheck className="h-5 w-5" />}
                  iconTone="border border-blue-100 bg-blue-50 text-blue-700"
                  title="Scam Checker"
                  description="Analyse a link, message, or email."
                />
                <QuickActionListItem
                  to="/footprint"
                  icon={<HiOutlineShieldCheck className="h-5 w-5" />}
                  iconTone="border border-violet-100 bg-violet-50 text-violet-700"
                  title="Breach Monitor"
                  description="See if your data has been leaked."
                />
                <QuickActionListItem
                  to="/contact-support"
                  icon={<HiOutlineChatAlt2 className="h-5 w-5" />}
                  iconTone="border border-emerald-100 bg-emerald-50 text-emerald-600"
                  title="Ask for Advice"
                  description="Talk to a real security expert."
                />
              </ul>
            </div>
          </div>

          {/* Right: stacked Up Next + Scam Radar */}
          <div className="space-y-4">
            <UpNextCardE market={userQuery?.data?.market} />
            <ScamRadarWithFlagsCard
              scamOfTheDay={scamOfTheDayQuery.data ?? undefined}
              isLoading={scamOfTheDayQuery.isLoading}
              redFlags={redFlags}
              showRedFlags={false}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

// QuickActionListItem — compact horizontal row used inside the bento left
// card. Icon tile on the left, title + description in the middle, chevron
// on the right. Reads as a list, not a row of cards.
function QuickActionListItem({
  to,
  icon,
  iconTone,
  title,
  description,
}: {
  to: string;
  icon: ReactNode;
  iconTone: string;
  title: string;
  description: string;
}) {
  return (
    <li>
      <Link
        to={to}
        search={{ invitationcode: undefined, __clerk_ticket: undefined }}
        className="group flex items-center gap-3.5 py-5 transition-colors"
      >
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] ${iconTone}`}>
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[17.5px] font-semibold text-slate-900 leading-tight group-hover:text-violet-700 transition-colors">
            {title}
          </p>
          <p className="text-[15px] text-slate-500 leading-snug mt-0.5">{description}</p>
        </div>
        <HiOutlineChevronRight className="h-5 w-5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500" />
      </Link>
    </li>
  );
}

/* ─── Guardian Level inline (Variant F) ───────────────────────────── */
//
// Compact, chrome-less version of the Guardian Level surface. No card
// border / no white background — sits inline with the greeting on the
// top row of Variant F. Smaller ring (~150px) so it fits beside prose.
function GuardianLevelInline({
  level,
  levelProgress,
  levelProgressPercent,
  points,
}: {
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  points: number;
  badgeStyle?: BadgeStyle;
}) {
  const ptsToNext = Math.max(0, 100 - levelProgress);
  return (
    <div className="flex items-center gap-4 sm:gap-5 lg:gap-6 rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <CompactNotchedRing level={level} percent={levelProgressPercent} />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-[16px] font-semibold leading-snug tracking-tight text-slate-900">
          You're a Level {level} Guardian.
        </p>
        <p className="max-w-[40ch] text-[14px] leading-snug text-slate-500">
          Your defences are taking shape. You've earned {points} {points === 1 ? "point" : "points"}, just {ptsToNext} more to reach Level {level + 1}.
        </p>
      </div>
    </div>
  );
}

// Smaller-format notched ring for the inline use. Same animation behaviour
// and look as the big one, just sized for a 112px footprint so it lines up
// with the height of the prose beside it. Centre is purely the level number
// — no badge, no eyebrow — so the digit can be the visual anchor.
function CompactNotchedRing({
  level,
  percent,
}: {
  level: number;
  percent: number;
}) {
  const size = 112;
  const cx = size / 2;
  const cy = size / 2;
  const r = 44;
  const baseStroke = 6;
  const tickCount = 60;

  const targetLit = Math.round((Math.max(0, Math.min(100, percent)) / 100) * tickCount);
  const [litCount, setLitCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let i = 0;
    const stepMs = Math.max(8, Math.round(700 / Math.max(1, targetLit)));
    const tick = () => {
      if (cancelled) return;
      i += 1;
      setLitCount(Math.min(targetLit, i));
      if (i < targetLit) window.setTimeout(tick, stepMs);
    };
    const start = window.setTimeout(tick, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(start);
    };
  }, [targetLit]);

  const circumference = 2 * Math.PI * r;
  const tickWidth = circumference / tickCount - 1;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id="compactNotchedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={SCORE_GRADIENT_FROM} />
            <stop offset="100%" stopColor={SCORE_GRADIENT_TO} />
          </linearGradient>
        </defs>
        {Array.from({ length: tickCount }).map((_, i) => {
          const angleDeg = (i / tickCount) * 360 - 90;
          const halfLen = baseStroke / 2;
          const x = cx - tickWidth / 2;
          const isLit = i < litCount;
          return (
            <rect
              key={i}
              x={x}
              y={cy - r - halfLen}
              width={tickWidth}
              height={baseStroke}
              rx={1}
              fill={isLit ? "url(#compactNotchedGrad)" : "rgb(212 220 235)"}
              transform={`rotate(${angleDeg + 90} ${cx} ${cy})`}
              className="transition-colors duration-200"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="text-[48px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
          {level}
        </span>
      </div>
    </div>
  );
}

// Tiny violet progress ring, sized to sit beside the checklist text. Mirrors
// the big Protection Score ring stylistically so "in progress" reads as a
// natural sibling of the larger gauge.
function ProgressDot({ percent, tone = "violet" }: { percent: number; tone?: "violet" | "emerald" }) {
  const r = 7;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, percent)) / 100) * c;
  // Tone matches whatever the checked rows use, so progress reads as "still
  // moving toward done" rather than a different colour family.
  const trackColor = tone === "emerald" ? "rgb(209 250 229)" : "rgb(237 233 254)";
  const strokeColor = tone === "emerald" ? "#059669" : "#7c3aed";
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" className="shrink-0 -rotate-90">
      <circle cx={9} cy={9} r={r} fill="none" stroke={trackColor} strokeWidth={2.5} />
      <circle
        cx={9}
        cy={9}
        r={r}
        fill="none"
        stroke={strokeColor}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
      />
    </svg>
  );
}

/* ─── Score ring visual variants ──────────────────────────────────── */
//
// Six interchangeable ring designs. They all render at 208×208 inside the
// Protection Score card. Each takes the same `score` prop so any can be
// swapped for any other without changing the card layout.

const SCORE_RING_SIZE = 208;
const SCORE_GRADIENT_FROM = "#a78bfa";
const SCORE_GRADIENT_TO = "#7c3aed";

function ScoreRingCentre({ score, small = false }: { score: number; small?: boolean }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      <span
        className={`font-semibold leading-none tracking-tight text-slate-900 tabular-nums ${
          small ? "text-[44px]" : "text-[54px]"
        }`}
      >
        {score}
      </span>
      <span className="mt-1.5 text-[13px] font-medium text-slate-400">/100</span>
    </div>
  );
}

// Classic — solid gradient stroke, the original.
function ScoreRingClassic({ score }: { score: number }) {
  const r = 80;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <>
      <svg width={SCORE_RING_SIZE} height={SCORE_RING_SIZE} viewBox={`0 0 ${SCORE_RING_SIZE} ${SCORE_RING_SIZE}`} className="-rotate-90">
        <circle cx={104} cy={104} r={r} fill="none" stroke="rgb(238 240 250)" strokeWidth={11} />
        <circle
          cx={104}
          cy={104}
          r={r}
          fill="none"
          stroke="url(#scoreClassicGrad)"
          strokeWidth={11}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
        <defs>
          <linearGradient id="scoreClassicGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={SCORE_GRADIENT_FROM} />
            <stop offset="100%" stopColor={SCORE_GRADIENT_TO} />
          </linearGradient>
        </defs>
      </svg>
      <ScoreRingCentre score={score} />
    </>
  );
}

// Notched — the stroke is a ring of discrete tick-marks. Watch-face
// inspired, calm and precise. Ticks light up sequentially on mount.
//
// Now reframed around Guardian Level: the ring fills with progress to the
// next level (0-100% of points within the current level), and the centre
// shows the user's level. This keeps the visual we love but ties it to the
// metric the user actually plays — points and levels — so the card stops
// double-talking about Protection Score.
function ScoreRingNotched({ level, percent, badgeStyle = "pill" }: { level: number; percent: number; badgeStyle?: BadgeStyle }) {
  // Local size override so we can scale this ring up without disturbing the
  // other lab variants that still derive from SCORE_RING_SIZE.
  const size = 232;
  const cx = size / 2;
  const cy = size / 2;
  const r = 92;
  const baseStroke = 12;
  const tickCount = 60;

  // Animate from 0 → percent on mount: ticks fill one-by-one over ~700ms.
  const targetLit = Math.round((Math.max(0, Math.min(100, percent)) / 100) * tickCount);
  const [litCount, setLitCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let i = 0;
    const stepMs = Math.max(8, Math.round(700 / Math.max(1, targetLit)));
    const tick = () => {
      if (cancelled) return;
      i += 1;
      setLitCount(Math.min(targetLit, i));
      if (i < targetLit) window.setTimeout(tick, stepMs);
    };
    // Small delay so the user actually perceives the build-up.
    const start = window.setTimeout(tick, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(start);
    };
  }, [targetLit]);

  return (
    <>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id="scoreNotchedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={SCORE_GRADIENT_FROM} />
            <stop offset="100%" stopColor={SCORE_GRADIENT_TO} />
          </linearGradient>
        </defs>
        {Array.from({ length: tickCount }).map((_, i) => {
          const angleDeg = (i / tickCount) * 360 - 90;
          const halfLen = baseStroke / 2;
          // Match the chunky look of the original dasharray ticks:
          // each tick spans most of its arc slice, leaving only a small gap.
          const circumference = 2 * Math.PI * r;
          const tickWidth = circumference / tickCount - 1.5;
          const x = cx - tickWidth / 2;
          const isLit = i < litCount;
          return (
            <rect
              key={i}
              x={x}
              y={cy - r - halfLen}
              width={tickWidth}
              height={baseStroke}
              rx={1}
              fill={isLit ? "url(#scoreNotchedGrad)" : "rgb(212 220 235)"}
              transform={`rotate(${angleDeg + 90} ${cx} ${cy})`}
              className="transition-colors duration-200"
            />
          );
        })}
      </svg>
      <LevelRingCentre level={level} badgeStyle={badgeStyle} large />
    </>
  );
}

// Dotted — 24 small filled circles arranged on a circle. Lit dots get
// gradient fill; unlit dots stay soft slate. Most "elegant gauge" feel.
function ScoreRingDotted({ score }: { score: number }) {
  const r = 84;
  const dotCount = 24;
  const dotsLit = Math.round((score / 100) * dotCount);

  return (
    <>
      <svg width={SCORE_RING_SIZE} height={SCORE_RING_SIZE} viewBox={`0 0 ${SCORE_RING_SIZE} ${SCORE_RING_SIZE}`}>
        <defs>
          <linearGradient id="scoreDottedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={SCORE_GRADIENT_FROM} />
            <stop offset="100%" stopColor={SCORE_GRADIENT_TO} />
          </linearGradient>
        </defs>
        {Array.from({ length: dotCount }).map((_, i) => {
          // Start at top (-90deg) and go clockwise.
          const angle = -Math.PI / 2 + (i / dotCount) * (Math.PI * 2);
          const x = 104 + r * Math.cos(angle);
          const y = 104 + r * Math.sin(angle);
          const lit = i < dotsLit;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={lit ? 4.5 : 3}
              fill={lit ? "url(#scoreDottedGrad)" : "rgb(226 232 240)"}
              className="transition-all duration-500"
            />
          );
        })}
      </svg>
      <ScoreRingCentre score={score} />
    </>
  );
}

// Half-gauge — a true speedometer. Half-circle arc opening downward, tick
// marks at 0/25/50/75/100, indicator needle pointing at the score. Score
// number sits centred above the arc.
function ScoreRingHalfGauge({ score }: { score: number }) {
  // Half-circle arc on the bottom: from 180° (left) sweeping to 360° (right).
  const cx = 104;
  const cy = 122;
  const r = 76;
  const arcLen = Math.PI * r; // half-circumference
  const offset = arcLen - (score / 100) * arcLen;

  // Tick marks at 0, 25, 50, 75, 100.
  const ticks = [0, 25, 50, 75, 100];
  const tickAngle = (pct: number) => Math.PI + (pct / 100) * Math.PI; // 180° -> 360°
  const tickInner = r - 6;
  const tickOuter = r + 6;
  const tickLabel = r + 18;

  // Needle angle.
  const needleAngle = Math.PI + (score / 100) * Math.PI;
  const needleX = cx + (r - 4) * Math.cos(needleAngle);
  const needleY = cy + (r - 4) * Math.sin(needleAngle);

  return (
    <svg width={SCORE_RING_SIZE} height={SCORE_RING_SIZE} viewBox={`0 0 ${SCORE_RING_SIZE} ${SCORE_RING_SIZE}`}>
      <defs>
        <linearGradient id="scoreHalfGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={SCORE_GRADIENT_FROM} />
          <stop offset="100%" stopColor={SCORE_GRADIENT_TO} />
        </linearGradient>
      </defs>
      {/* Score number, sits above the arc */}
      <text
        x={cx}
        y={cy - 22}
        textAnchor="middle"
        className="fill-slate-900"
        style={{ fontSize: 56, fontWeight: 600, letterSpacing: "-0.02em" }}
      >
        {score}
      </text>
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        className="fill-slate-400"
        style={{ fontSize: 12, fontWeight: 500 }}
      >
        / 100
      </text>
      {/* Track */}
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none"
        stroke="rgb(238 240 250)"
        strokeWidth={9}
        strokeLinecap="round"
      />
      {/* Filled arc */}
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none"
        stroke="url(#scoreHalfGrad)"
        strokeWidth={9}
        strokeLinecap="round"
        strokeDasharray={arcLen}
        strokeDashoffset={offset}
        className="transition-[stroke-dashoffset] duration-700 ease-out"
      />
      {/* Ticks + numerals */}
      {ticks.map((t) => {
        const a = tickAngle(t);
        const x1 = cx + tickInner * Math.cos(a);
        const y1 = cy + tickInner * Math.sin(a);
        const x2 = cx + tickOuter * Math.cos(a);
        const y2 = cy + tickOuter * Math.sin(a);
        const lx = cx + tickLabel * Math.cos(a);
        const ly = cy + tickLabel * Math.sin(a);
        return (
          <g key={t}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgb(148 163 184)" strokeWidth={1.5} strokeLinecap="round" />
            <text
              x={lx}
              y={ly + 3}
              textAnchor="middle"
              className="fill-slate-400"
              style={{ fontSize: 9, fontWeight: 600 }}
            >
              {t}
            </text>
          </g>
        );
      })}
      {/* Needle */}
      <g>
        <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke="#0f172a" strokeWidth={2.5} strokeLinecap="round" />
        <circle cx={cx} cy={cy} r={4.5} fill="#0f172a" />
      </g>
    </svg>
  );
}

// Layered — two concentric rings (Apple Activity ring style). Outer = score,
// inner = level progress (mocked at 80% for visual interest). Most premium.
function ScoreRingLayered({ score }: { score: number }) {
  const r1 = 84;
  const r2 = 64;
  const c1 = 2 * Math.PI * r1;
  const c2 = 2 * Math.PI * r2;
  const off1 = c1 - (score / 100) * c1;
  // Use a stable derived "secondary metric" — distance into the current
  // tier (re-uses the milestone tier idea so the inner ring tells a
  // different but coherent story from the outer ring).
  const tiers = [0, 40, 65, 85, 100];
  const upper = tiers.find((t) => score < t) ?? 100;
  const lower = [...tiers].reverse().find((t) => score >= t) ?? 0;
  const innerPct = upper === lower ? 100 : Math.round(((score - lower) / (upper - lower)) * 100);
  const off2 = c2 - (innerPct / 100) * c2;

  return (
    <>
      <svg width={SCORE_RING_SIZE} height={SCORE_RING_SIZE} viewBox={`0 0 ${SCORE_RING_SIZE} ${SCORE_RING_SIZE}`} className="-rotate-90">
        <defs>
          <linearGradient id="scoreLayerOuter" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={SCORE_GRADIENT_FROM} />
            <stop offset="100%" stopColor={SCORE_GRADIENT_TO} />
          </linearGradient>
          <linearGradient id="scoreLayerInner" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
        {/* Outer ring (score) */}
        <circle cx={104} cy={104} r={r1} fill="none" stroke="rgb(238 240 250)" strokeWidth={9} />
        <circle
          cx={104}
          cy={104}
          r={r1}
          fill="none"
          stroke="url(#scoreLayerOuter)"
          strokeWidth={9}
          strokeLinecap="round"
          strokeDasharray={c1}
          strokeDashoffset={off1}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
        {/* Inner ring (tier progress) */}
        <circle cx={104} cy={104} r={r2} fill="none" stroke="rgb(238 240 250)" strokeWidth={7} />
        <circle
          cx={104}
          cy={104}
          r={r2}
          fill="none"
          stroke="url(#scoreLayerInner)"
          strokeWidth={7}
          strokeLinecap="round"
          strokeDasharray={c2}
          strokeDashoffset={off2}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <ScoreRingCentre score={score} small />
    </>
  );
}

// Tick — minimal numeric tick gauge. Concentric ring + numeric tick markers
// outside it (0/20/40/60/80/100). Score sits in the centre. Reads as a
// scientific instrument: precise, sober, premium.
function ScoreRingTick({ score }: { score: number }) {
  const cx = 104;
  const cy = 104;
  const r = 70;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  const ticks = [0, 20, 40, 60, 80, 100];

  return (
    <svg width={SCORE_RING_SIZE} height={SCORE_RING_SIZE} viewBox={`0 0 ${SCORE_RING_SIZE} ${SCORE_RING_SIZE}`}>
      <defs>
        <linearGradient id="scoreTickGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={SCORE_GRADIENT_FROM} />
          <stop offset="100%" stopColor={SCORE_GRADIENT_TO} />
        </linearGradient>
      </defs>
      {/* Tick marks + numerals around outside */}
      {ticks.map((t) => {
        // angle: start at top (-90°) and go clockwise.
        const a = -Math.PI / 2 + (t / 100) * Math.PI * 2;
        const tickInner = r + 8;
        const tickOuter = r + 14;
        const labelR = r + 24;
        const x1 = cx + tickInner * Math.cos(a);
        const y1 = cy + tickInner * Math.sin(a);
        const x2 = cx + tickOuter * Math.cos(a);
        const y2 = cy + tickOuter * Math.sin(a);
        const lx = cx + labelR * Math.cos(a);
        const ly = cy + labelR * Math.sin(a);
        const isMilestone = t === 0 || t === 50 || t === 100;
        return (
          <g key={t}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={isMilestone ? "rgb(100 116 139)" : "rgb(203 213 225)"}
              strokeWidth={isMilestone ? 1.5 : 1}
              strokeLinecap="round"
            />
            <text
              x={lx}
              y={ly + 3}
              textAnchor="middle"
              className="fill-slate-400"
              style={{ fontSize: 8.5, fontWeight: 600 }}
            >
              {t}
            </text>
          </g>
        );
      })}
      {/* Track */}
      <g transform={`rotate(-90 ${cx} ${cy})`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgb(238 240 250)" strokeWidth={8} />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="url(#scoreTickGrad)"
          strokeWidth={8}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </g>
      {/* Centre score */}
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        className="fill-slate-900"
        style={{ fontSize: 50, fontWeight: 600, letterSpacing: "-0.02em" }}
      >
        {score}
      </text>
      <text
        x={cx}
        y={cy + 18}
        textAnchor="middle"
        className="fill-slate-400"
        style={{ fontSize: 11, fontWeight: 500 }}
      >
        / 100
      </text>
    </svg>
  );
}

function ScoreRing({
  variant,
  score,
  level,
  levelProgressPercent,
  badgeStyle = "pill",
}: {
  variant: RingStyle;
  score: number;
  level?: number;
  levelProgressPercent?: number;
  badgeStyle?: BadgeStyle;
}) {
  // Level-based rings need both signals; fall back to score=0 / level=1 if
  // the parent didn't pass them so we never crash in the lab.
  const lv = level ?? 1;
  const lvPct = levelProgressPercent ?? 0;

  if (variant === "notched") return <ScoreRingNotched level={lv} percent={lvPct} badgeStyle={badgeStyle} />;
  if (variant === "dotted") return <ScoreRingDotted score={score} />;
  if (variant === "half-gauge") return <ScoreRingHalfGauge score={score} />;
  if (variant === "layered") return <ScoreRingLayered score={score} />;
  if (variant === "tick") return <ScoreRingTick score={score} />;
  if (variant === "notched-level") return <ScoreRingNotchedLevel level={lv} percent={lvPct} />;
  if (variant === "notched-level-dense")
    return <ScoreRingNotchedLevel level={lv} percent={lvPct} tickCount={80} milestones={false} />;
  if (variant === "notched-level-active")
    return <ScoreRingNotchedLevel level={lv} percent={lvPct} highlightLeading />;
  if (variant === "notched-level-animated")
    return <ScoreRingNotchedLevel level={lv} percent={lvPct} animateOnMount />;
  if (variant === "notched-level-deluxe")
    return (
      <ScoreRingNotchedLevel
        level={lv}
        percent={lvPct}
        milestones
        highlightLeading
        animateOnMount
      />
    );
  return <ScoreRingClassic score={score} />;
}

// Level title ladder. Each level earns a single-word identity that can sit
// comfortably as a badge inside the ring. The last entry catches every
// level beyond what's in the table (e.g. Level 12 still reads "Legend").
//
// Single words on purpose: longer titles like "Senior Guardian" don't fit
// at hero size inside the ring without auto-shrinking. Move this to a CMS
// config once product owns the copy.
const LEVEL_TITLES = [
  "Newcomer", // 1
  "Learner", // 2
  "Defender", // 3
  "Guardian", // 4
  "Sentinel", // 5
  "Veteran", // 6
  "Master", // 7
  "Champion", // 8
  "Mentor", // 9
  "Legend", // 10+
];

function getLevelTitle(level: number): string {
  const idx = Math.max(0, Math.min(LEVEL_TITLES.length - 1, level - 1));
  return LEVEL_TITLES[idx];
}

// Centre helper for the level-based rings — hero level number with a small
// title label sitting beneath it. The card heading carries "Guardian Level"
// so the ring centre stays focused on (a) the number you've reached and
// (b) the named identity that comes with it. Label style is configurable.
function LevelRingCentre({ level, badgeStyle = "pill", large = false }: { level: number; badgeStyle?: BadgeStyle; large?: boolean }) {
  const title = getLevelTitle(level);
  const numberSize = large ? "text-[68px]" : "text-[58px]";
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      <span className={`${numberSize} font-semibold leading-none tracking-tight text-slate-900 tabular-nums`}>
        {level}
      </span>
      <LevelTitleLabel title={title} variant={badgeStyle} />
    </div>
  );
}

// Seven label styles — calmest at the top, loudest at the bottom. They all
// occupy the same slot under the hero level number; only the chrome changes.
function LevelTitleLabel({ title, variant }: { title: string; variant: BadgeStyle }) {
  const baseText = "mt-2 text-[10.5px] font-bold uppercase tracking-[0.06em] whitespace-nowrap";

  // PILL — current default. Soft violet fill + thin ring.
  if (variant === "pill") {
    return (
      <span className={`${baseText} inline-flex items-center rounded-full bg-violet-50 px-2.5 py-0.5 text-violet-700 ring-1 ring-violet-100`}>
        {title}
      </span>
    );
  }

  // SOFT — very faint violet tint, no ring. The quietest filled treatment.
  if (variant === "soft") {
    return (
      <span className={`${baseText} inline-flex items-center rounded-full bg-violet-50/60 px-2.5 py-0.5 text-violet-700`}>
        {title}
      </span>
    );
  }

  // GHOST — outline only, no fill. Reads as a stamped border, not a chip.
  if (variant === "ghost") {
    return (
      <span className={`${baseText} inline-flex items-center rounded-full px-2.5 py-0.5 text-violet-700 ring-1 ring-violet-200`}>
        {title}
      </span>
    );
  }

  // UNDERLINE — no chrome, just a thin violet underline beneath the text.
  // Most editorial, feels like a section marker rather than a badge.
  if (variant === "underline") {
    return (
      <span className={`${baseText} text-violet-700 border-b border-violet-300 pb-0.5`}>
        {title}
      </span>
    );
  }

  // TEXT — pure type, no chrome at all. Slate-toned so it reads as a quiet
  // system label rather than a brand accent. Most minimal of all the styles.
  if (variant === "text") {
    return <span className={`${baseText} text-slate-500`}>{title}</span>;
  }

  // SLATE — neutral colour palette, no violet. Reads as a quiet system label
  // rather than a brand-tinted accent. Useful when the violet ring is loud.
  if (variant === "slate") {
    return (
      <span className={`${baseText} inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-slate-600`}>
        {title}
      </span>
    );
  }

  // LOGO — small violet G mark precedes the title. Brand-anchored marker;
  // implies "this is your GranGuard rank" without a chip wrapper.
  if (variant === "logo") {
    return (
      <span className={`${baseText} inline-flex items-center gap-1.5 text-slate-700`}>
        <img src={gLogoViolet} alt="" className="h-3.5 w-3.5 object-contain" />
        {title}
      </span>
    );
  }

  // DOT — a small violet shield precedes the text, no chip. Brand-anchored
  // status indicator that ties the level title to GranGuard's defence theme.
  return (
    <span className={`${baseText} inline-flex items-center gap-1.5 text-slate-600`}>
      <FiShield className="h-3 w-3 text-violet-600" />
      {title}
    </span>
  );
}

// Notched · Level — single configurable ring covering all four polish moves
// the user picked: tick density, milestone ticks, leading-tick highlight,
// and animated reveal on mount. Each named variant just toggles a flag.
function ScoreRingNotchedLevel({
  level,
  percent,
  tickCount = 60,
  milestones = true,
  highlightLeading = false,
  animateOnMount = false,
}: {
  level: number;
  percent: number;
  tickCount?: number;
  milestones?: boolean;
  highlightLeading?: boolean;
  animateOnMount?: boolean;
}) {
  const cx = 104;
  const cy = 104;
  const r = 80;
  const baseStroke = 11;

  // Animate from 0 → percent on mount when requested. Tick lighting is driven
  // by `litCount`, which is the integer number of ticks currently filled.
  const targetLit = Math.round((percent / 100) * tickCount);
  const [litCount, setLitCount] = useState(animateOnMount ? 0 : targetLit);

  useEffect(() => {
    if (!animateOnMount) {
      setLitCount(targetLit);
      return;
    }
    let cancelled = false;
    let i = 0;
    const stepMs = Math.max(8, Math.round(700 / Math.max(1, targetLit)));
    const tick = () => {
      if (cancelled) return;
      i += 1;
      setLitCount(Math.min(targetLit, i));
      if (i < targetLit) window.setTimeout(tick, stepMs);
    };
    // Small delay so the user actually perceives the build-up.
    const start = window.setTimeout(tick, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(start);
    };
  }, [animateOnMount, targetLit]);

  // Build N tick rectangles around the ring. Each tick is a rotated rect
  // whose centre sits on the ring's circumference. Length depends on whether
  // the tick is a milestone or not.
  const ticks = Array.from({ length: tickCount }).map((_, i) => {
    const isMilestone = milestones && i % Math.max(1, Math.round(tickCount / 8)) === 0;
    const isLit = i < litCount;
    const isLeading = highlightLeading && i === litCount - 1 && litCount > 0;
    const angleDeg = (i / tickCount) * 360 - 90;
    const tickLen = isMilestone ? baseStroke + 5 : baseStroke;
    const tickWidth = isMilestone ? 2.6 : 2;
    return { i, angleDeg, tickLen, tickWidth, isLit, isLeading, isMilestone };
  });

  return (
    <>
      <svg width={SCORE_RING_SIZE} height={SCORE_RING_SIZE} viewBox={`0 0 ${SCORE_RING_SIZE} ${SCORE_RING_SIZE}`}>
        <defs>
          <linearGradient id="notchedLevelGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={SCORE_GRADIENT_FROM} />
            <stop offset="100%" stopColor={SCORE_GRADIENT_TO} />
          </linearGradient>
        </defs>
        {ticks.map((t) => {
          // Center the tick rectangle on the radius ring, oriented radially.
          const halfLen = t.tickLen / 2;
          // Place a rect at (cx-halfWidth, cy-r-halfLen) then rotate around (cx, cy).
          const x = cx - t.tickWidth / 2;
          const fill = t.isLeading
            ? "#0f172a"
            : t.isLit
              ? "url(#notchedLevelGrad)"
              : t.isMilestone
                ? "rgb(148 163 184)"
                : "rgb(212 220 235)";
          return (
            <rect
              key={t.i}
              x={x}
              y={cy - r - halfLen}
              width={t.tickWidth}
              height={t.tickLen}
              rx={t.tickWidth / 2}
              fill={fill}
              transform={`rotate(${t.angleDeg + 90} ${cx} ${cy})`}
              className={animateOnMount ? "transition-colors duration-200" : undefined}
              style={{
                opacity: t.isLit || t.isMilestone || !milestones ? 1 : 0.85,
              }}
            />
          );
        })}
      </svg>
      <LevelRingCentre level={level} />
    </>
  );
}

export function ProtectionScoreCard({
  score,
  label,
  summary,
  checklist,
  ringStyle = "classic",
  level,
  levelProgress,
  levelProgressPercent,
  points,
  rightSide = "checklist",
  topBlurb = "plain",
  userName,
  badgeStyle = "pill",
  checkStyle = "filled",
}: {
  score: number;
  label: string;
  summary: string;
  checklist: { label: string; status: "good" | "progress" | "info"; percent?: number }[];
  ringStyle?: RingStyle;
  level?: number;
  levelProgress?: number;
  levelProgressPercent?: number;
  points?: number;
  rightSide?: RightSide;
  topBlurb?: TopBlurb;
  userName?: string;
  badgeStyle?: BadgeStyle;
  checkStyle?: CheckStyle;
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-6 sm:p-7 lg:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-1.5">
        <h2 className="text-[24px] lg:text-[20px] font-semibold tracking-tight text-slate-900">Your Guardian Level</h2>
        <HiOutlineInformationCircle className="h-5 w-5 lg:h-[18px] lg:w-[18px] text-slate-300" aria-hidden />
      </div>

      <div className="mt-1 grid gap-0 md:grid-cols-[232px_minmax(0,1fr)] md:items-center md:divide-x md:divide-slate-100">
        {/* Score ring (swappable visual) */}
        <div className="relative mx-auto flex h-[232px] w-[232px] items-center justify-center md:mx-0 md:-translate-x-1">
          <ScoreRing
            variant={ringStyle}
            score={score}
            level={level}
            levelProgressPercent={levelProgressPercent}
            badgeStyle={badgeStyle}
          />
        </div>

        {/* Right side — toggleable */}
        <div className="md:pl-5">
        {rightSide === "level-moves" ? (
          <ProtectionScoreRightLevelMoves
            label={label}
            level={level ?? 1}
            levelProgress={levelProgress ?? 0}
            levelProgressPercent={levelProgressPercent ?? 0}
            points={points ?? 0}
            checklist={checklist}
          />
        ) : (
          <ProtectionScoreRightChecklist
            label={label}
            summary={summary}
            checklist={checklist}
            topBlurb={topBlurb}
            level={level ?? 1}
            levelProgress={levelProgress ?? 0}
            levelProgressPercent={levelProgressPercent ?? 0}
            points={points ?? 0}
            userName={userName ?? "there"}
            checkStyle={checkStyle}
          />
        )}
        </div>
      </div>
    </div>
  );
}

// Right side — top blurb (variable) + checklist. The blurb changes per
// `topBlurb` style; the checklist's "good" marker changes per `checkStyle`.
function ProtectionScoreRightChecklist({
  label,
  summary,
  checklist,
  topBlurb,
  level,
  levelProgress,
  levelProgressPercent,
  points,
  userName,
  checkStyle = "filled",
}: {
  label: string;
  summary: string;
  checklist: { label: string; status: "good" | "progress" | "info"; percent?: number }[];
  topBlurb: TopBlurb;
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  points: number;
  userName: string;
  checkStyle?: CheckStyle;
}) {
  return (
    <div className="min-w-0 space-y-3.5 lg:space-y-2.5">
      <ProtectionScoreTopBlurb
        variant={topBlurb}
        label={label}
        summary={summary}
        level={level}
        levelProgress={levelProgress}
        levelProgressPercent={levelProgressPercent}
        points={points}
        userName={userName}
      />

      <ul className="space-y-2.5 lg:space-y-1.5 pt-2 lg:pt-1">
        {checklist.map((item) => (
          <li key={item.label} className="flex items-center gap-3 lg:gap-2 text-[18px] lg:text-[14px]">
            {item.status === "good" ? (
              <CompletedMark variant={checkStyle} />
            ) : item.status === "progress" ? (
              <ProgressDot percent={item.percent ?? 0} tone="violet" />
            ) : (
              <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <FiAlertTriangle className="h-3 w-3" />
              </span>
            )}
            <span className="text-slate-700">
              {item.label}
              {item.status === "progress" && (
                <span className="ml-1.5 text-[15px] lg:text-[12.5px] font-medium text-slate-400 tabular-nums">
                  ({item.percent ?? 0}%)
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// CompletedMark — the "good" status icon for checklist rows. Violet-toned
// to match the rest of the card's brand accents. Seven styles:
//
//   filled    — solid violet circle + white check (default)
//   outline   — violet-300 ring outside, violet-600 check inside, no fill
//   ring      — light-violet background + violet-700 check (softer fill)
//   square    — same as filled but rounded-square corners
//   tick      — bare violet check, no chrome at all
//   dot       — solid violet dot, no check icon
//   subtle    — slate-100 background + violet-600 check (de-saturated)
function CompletedMark({ variant }: { variant: CheckStyle }) {
  const baseSize = "h-[18px] w-[18px]";

  if (variant === "outline") {
    return (
      <span className={`flex ${baseSize} shrink-0 items-center justify-center rounded-full text-violet-600`} style={{ boxShadow: "inset 0 0 0 1.5px rgb(196 181 253)" }}>
        <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }

  if (variant === "ring") {
    return (
      <span className={`flex ${baseSize} shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700`}>
        <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }

  if (variant === "square") {
    return (
      <span className={`flex ${baseSize} shrink-0 items-center justify-center rounded-[5px] bg-violet-600 text-white`}>
        <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }

  if (variant === "tick") {
    return (
      <span className={`flex ${baseSize} shrink-0 items-center justify-center text-violet-600`}>
        <HiOutlineCheck className="h-4 w-4" strokeWidth={3.5} />
      </span>
    );
  }

  if (variant === "dot") {
    return (
      <span className={`flex ${baseSize} shrink-0 items-center justify-center`}>
        <span className="h-[10px] w-[10px] rounded-full bg-violet-500" />
      </span>
    );
  }

  if (variant === "subtle") {
    return (
      <span className={`flex ${baseSize} shrink-0 items-center justify-center rounded-full bg-slate-100 text-violet-600`}>
        <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }

  // filled — default
  return (
    <span className={`flex ${baseSize} shrink-0 items-center justify-center rounded-full bg-violet-600 text-white`}>
      <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
    </span>
  );
}

// Six top-blurb styles. Each communicates "where you are + what's next" in
// a different tone. They all sit in the same slot so the rest of the card
// stays put.
function ProtectionScoreTopBlurb({
  variant,
  label,
  summary,
  level,
  levelProgress,
  levelProgressPercent,
  points,
  userName,
}: {
  variant: TopBlurb;
  label: string;
  summary: string;
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  points: number;
  userName: string;
}) {
  const ptsToNext = Math.max(0, 100 - levelProgress);
  const firstName = (userName.split(" ")[0] || userName).trim();

  // STATUS — the original. Green shield + tier label + summary line.
  if (variant === "status") {
    return (
      <div className="space-y-0.5">
        <div className="flex items-center gap-1.5">
          <FiShield className="h-[18px] w-[18px] text-emerald-600" />
          <span className="text-[16px] font-semibold leading-none text-emerald-700">{label}</span>
        </div>
        <p className="text-[12.5px] leading-snug text-slate-500 max-w-[32ch]">{summary}</p>
      </div>
    );
  }

  // PLAIN — two clean lines. Title + concrete numbers.
  if (variant === "plain") {
    return (
      <div className="space-y-1.5 lg:space-y-0.5">
        <p className="text-[26px] lg:text-[16px] font-semibold tracking-tight text-slate-900">
          Guardian Level {level}
        </p>
        <p className="text-[18px] lg:text-[12.5px] text-slate-500 tabular-nums">
          {points} pts earned · {ptsToNext} to Level {level + 1}
        </p>
      </div>
    );
  }

  // EMPHATIC — bigger headline number, subtle sub.
  if (variant === "emphatic") {
    return (
      <div className="space-y-0.5">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            Level
          </span>
          <span className="text-[28px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
            {level}
          </span>
        </div>
        <p className="text-[12.5px] text-slate-500 tabular-nums">
          {ptsToNext} points to Level {level + 1}
        </p>
      </div>
    );
  }

  // BAR — level + thin progress bar + numeric line beneath. Most informative.
  if (variant === "bar") {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[15px] font-semibold tracking-tight text-slate-900">
            Guardian Level {level}
          </p>
          <span className="text-[11px] font-medium text-slate-400 tabular-nums">
            {levelProgress}/100
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-[width] duration-500"
            style={{ width: `${levelProgressPercent}%` }}
          />
        </div>
        <p className="text-[11.5px] text-slate-500 tabular-nums">
          {ptsToNext} pts to Level {level + 1}
        </p>
      </div>
    );
  }

  // FRIENDLY — short, warm, second person. Tightened copy.
  if (variant === "friendly") {
    return (
      <div className="space-y-0.5">
        <p className="text-[16px] font-semibold leading-snug tracking-tight text-slate-900">
          Going well, {firstName}.
        </p>
        <p className="text-[14px] leading-snug text-slate-600">
          A few more steps and you'll reach Level {level + 1}. Keep building these habits.
        </p>
      </div>
    );
  }

  // INLINE — single compact row: chip + sentence. Minimal vertical footprint.
  if (variant === "inline") {
    return (
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.08em] text-violet-700 ring-1 ring-violet-100">
          Level {level}
        </span>
        <span className="text-[12.5px] text-slate-600 tabular-nums">
          {ptsToNext} pts to next
        </span>
      </div>
    );
  }

  // PROUD — identity-first headline, then a single prose sub that weaves
  // the level narrative, lifetime points, and next milestone into three
  // short sentences. Numbers live inside the prose so it reads as a calm
  // paragraph rather than a stat block.
  if (variant === "proud") {
    return (
      <div className="space-y-0.5">
        <p className="text-[18px] font-semibold leading-snug tracking-tight text-slate-900">
          You're a Level {level} Guardian.
        </p>
        <p className="text-[15px] leading-snug text-slate-500">
          Your defences are taking shape. You've earned {points} {points === 1 ? "point" : "points"}, just {ptsToNext} more to reach Level {level + 1}.
        </p>
      </div>
    );
  }

  // COACH — encouraging without being saccharine. Named, present tense,
  // no exclamation marks. Reads like someone in your corner, not a slogan.
  if (variant === "coach") {
    return (
      <div className="space-y-0.5">
        <p className="text-[16px] font-semibold leading-snug tracking-tight text-slate-900">
          You're building real habits, {firstName}.
        </p>
        <p className="text-[14px] leading-snug text-slate-600">
          {ptsToNext} more {ptsToNext === 1 ? "point" : "points"} and you'll be a Level {level + 1} Guardian.
        </p>
      </div>
    );
  }

  // JOURNEY — emphasises distance covered, not just distance to go.
  // Looks back as well as forward, which is the most under-used framing.
  if (variant === "journey") {
    return (
      <div className="space-y-0.5">
        <p className="text-[16px] font-semibold leading-snug tracking-tight text-slate-900">
          You've earned {points} {points === 1 ? "point" : "points"} so far.
        </p>
        <p className="text-[14px] leading-snug text-slate-600">
          Another {ptsToNext} and Level {level + 1} is yours. The next skills are waiting.
        </p>
      </div>
    );
  }

  // AFFIRMING — anchors progress in capability, not numbers. The user
  // walks away knowing what they can do, not just how many points they have.
  if (variant === "affirming") {
    return (
      <div className="space-y-0.5">
        <p className="text-[16px] font-semibold leading-snug tracking-tight text-slate-900">
          Strong habits are forming.
        </p>
        <p className="text-[14px] leading-snug text-slate-600">
          You can already spot the scams that catch most people. Level {level + 1} sharpens the rest.
        </p>
      </div>
    );
  }

  // Fallback — match plain.
  return (
    <>
      <p className="text-[16px] font-semibold tracking-tight text-slate-900">
        Guardian Level {level}
      </p>
      <p className="text-[12.5px] text-slate-500 tabular-nums">
        {points} pts earned · {ptsToNext} to Level {level + 1}
      </p>
    </>
  );
}

// Combo right side — Level + points header + smart "next 3 moves" list.
//
// The list is dynamic: incomplete checklist items get top priority, then we
// fall back to lateral moves (try the Scam Checker, invite a family member,
// start a new skill path, run a breach scan). This keeps the surface useful
// long after the user finishes Scam Fundamentals.
function ProtectionScoreRightLevelMoves({
  label,
  level,
  levelProgress,
  levelProgressPercent,
  points,
  checklist,
}: {
  label: string;
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  points: number;
  checklist: { label: string; status: "good" | "progress" | "info"; percent?: number }[];
}) {
  const moves = buildNextMoves(checklist).slice(0, 3);
  const ptsToNext = Math.max(0, 100 - levelProgress);

  return (
    <div className="min-w-0">
      {/* Header: level + status pill + progress bar */}
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[15px] font-semibold tracking-tight text-slate-900">
          Guardian Level <span className="tabular-nums">{level}</span>
        </p>
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700">
          <FiShield className="h-3.5 w-3.5 text-emerald-600" />
          {label}
        </span>
      </div>
      <p className="mt-0.5 text-[12px] text-slate-500 tabular-nums">
        {points} pts earned · {ptsToNext} to Level {level + 1}
      </p>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-[width] duration-500"
          style={{ width: `${levelProgressPercent}%` }}
        />
      </div>

      {/* Next moves */}
      <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
        Next moves
      </p>
      <ul className="mt-1.5 space-y-1">
        {moves.map((m) => (
          <li key={m.title}>
            <Link
              to={m.to}
              search={{ tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
              className="group flex items-center gap-2.5 rounded-lg px-1.5 py-1.5 -mx-1.5 transition-colors hover:bg-slate-50"
            >
              {m.status === "in-progress" ? (
                <ProgressDot percent={m.progressPct ?? 0} />
              ) : (
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-violet-50 text-violet-600 ring-1 ring-violet-100">
                  <HiOutlineArrowRight className="h-2.5 w-2.5" />
                </span>
              )}
              <span className="flex-1 min-w-0 text-[13px] font-medium text-slate-700 leading-tight truncate">
                {m.title}
              </span>
              <span className="text-[11px] font-semibold text-violet-700 tabular-nums whitespace-nowrap">
                +{m.reward} pts
              </span>
              <HiOutlineChevronRight className="h-3.5 w-3.5 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500" />
            </Link>
          </li>
        ))}
      </ul>

      <div className="mt-3 pt-3 border-t border-slate-100">
        <Link
          to="/missions"
          search={{ tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
          className="text-[12.5px] font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
        >
          See how to earn more
        </Link>
      </div>
    </div>
  );
}

// Dynamic "next 3 best moves" picker. Walks the existing checklist for
// incomplete items first, then falls back to lateral moves so the user
// always has 3 things to do regardless of stage.
type NextMove = {
  title: string;
  reward: number;
  to: string;
  status: "in-progress" | "available";
  progressPct?: number;
};

const FALLBACK_MOVES: NextMove[] = [
  { title: "Try the Scam Checker", reward: 10, to: "/tools", status: "available" },
  { title: "Run a breach scan", reward: 15, to: "/footprint", status: "available" },
  { title: "Start a Detection skill path", reward: 20, to: "/learn", status: "available" },
  { title: "Invite a family member", reward: 25, to: "/account", status: "available" },
];

function buildNextMoves(
  checklist: { label: string; status: "good" | "progress" | "info"; percent?: number }[],
): NextMove[] {
  const moves: NextMove[] = [];
  for (const item of checklist) {
    if (item.status === "good") continue;
    if (item.label === "Account setup") {
      moves.push({ title: "Finish account setup", reward: 33, to: "/onboarding", status: "available" });
    } else if (item.label === "Guided walkthrough") {
      moves.push({ title: "Take the walkthrough", reward: 33, to: "/missions", status: "available" });
    } else if (item.label === "Fundamentals guide") {
      const remaining = Math.max(1, Math.round(34 * (1 - (item.percent ?? 0) / 100)));
      moves.push({
        title: "Continue Scam Fundamentals",
        reward: remaining,
        to: "/learning-journeys/scam-fundamentals",
        status: "in-progress",
        progressPct: item.percent ?? 0,
      });
    }
  }
  // Fill to 3 with fallbacks.
  for (const f of FALLBACK_MOVES) {
    if (moves.length >= 3) break;
    if (!moves.some((m) => m.title === f.title)) moves.push(f);
  }
  return moves;
}

/* ─── Score card alternatives ─────────────────────────────────────── */

type ChecklistItem = { label: string; status: "good" | "progress" | "info"; percent?: number };

function ChecklistRow({ item, dense = false }: { item: ChecklistItem; dense?: boolean }) {
  const size = dense ? "h-[16px] w-[16px]" : "h-[18px] w-[18px]";
  const text = dense ? "text-[12.5px]" : "text-[13px]";
  return (
    <li className={`flex items-center gap-2 ${text}`}>
      {item.status === "good" ? (
        <span className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white`}>
          <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
        </span>
      ) : item.status === "progress" ? (
        <ProgressDot percent={item.percent ?? 0} />
      ) : (
        <span className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600`}>
          <FiAlertTriangle className="h-3 w-3" />
        </span>
      )}
      <span className="text-slate-700">{item.label}</span>
      {item.status === "progress" && (
        <span className="ml-auto text-[12px] font-medium text-violet-700 tabular-nums">
          {item.percent ?? 0}%
        </span>
      )}
    </li>
  );
}

// Guardian style — a Protection Score / Guardian Points hybrid that matches
// the existing GuardianPointsCard hierarchy: tiny label, big number, badge,
// gradient divider, level + progress bar. Adds the protection checklist below.
function ProtectionScoreGuardian({
  score,
  label,
  points,
  level,
  levelProgress,
  levelProgressPercent,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      {/* Top row: score + shield badge */}
      <div className="flex items-start justify-between gap-5">
        <div>
          <div className="flex items-center gap-1.5">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
              Protection score
            </p>
            <HiOutlineInformationCircle className="h-3.5 w-3.5 text-slate-300" aria-hidden />
          </div>
          <p className="mt-1.5 text-5xl font-semibold leading-none text-slate-900 tabular-nums tracking-tight">
            {score}
            <span className="ml-1 text-[18px] font-medium text-slate-400">/100</span>
          </p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
            <FiShield className="h-4 w-4 text-emerald-600" />
            {label}
          </p>
        </div>
        <div className="flex h-[56px] w-[56px] items-center justify-center rounded-[18px] border border-violet-100/65 bg-gradient-to-br from-violet-50/70 via-white/90 to-indigo-50/65 shadow-[inset_2px_2px_5px_rgb(255_255_255_/_24%),inset_-2px_-2px_6px_rgb(230_232_255_/_72%)]">
          <img src={gLogoViolet} alt="" className="h-[32px] w-[32px] object-contain" />
        </div>
      </div>

      <div className="mt-3 h-px w-full bg-gradient-to-r from-violet-200/55 via-indigo-200/35 to-transparent" />

      {/* Guardian level + progress */}
      <div className="mt-3.5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-slate-900">Guardian Level {level}</span>
          <span className="text-slate-500 tabular-nums">{levelProgress}/100 to next</span>
        </div>
        <div className="mt-2 h-2.5 w-full rounded-full bg-slate-200/80">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-[width] duration-500"
            style={{ width: `${levelProgressPercent}%` }}
          />
        </div>
        <p className="mt-1.5 text-[12px] text-slate-400 tabular-nums">{points} pts earned</p>
      </div>

      <div className="mt-4 h-px w-full bg-slate-100" />

      {/* Checklist */}
      <ul className="mt-4 space-y-1.5">
        {checklist.map((item) => (
          <ChecklistRow key={item.label} item={item} />
        ))}
      </ul>
    </div>
  );
}

// Guardian · split — same building blocks as Guardian, but laid out in two
// vertical columns: score + label + shield on the left, level + progress +
// checklist on the right. A vertical divider runs between them so each side
// reads as its own panel without needing color blocks.
function ProtectionScoreGuardianSplit({
  score,
  label,
  points,
  level,
  levelProgress,
  levelProgressPercent,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="grid gap-5 md:grid-cols-2 md:items-stretch">
        {/* Left column: score + label + level progress */}
        <div className="md:border-r md:border-slate-100 md:pr-5">
          <div className="flex items-center gap-1.5">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
              Protection score
            </p>
            <HiOutlineInformationCircle className="h-3.5 w-3.5 text-slate-300" aria-hidden />
          </div>
          <p className="mt-1.5 text-[60px] font-semibold leading-[0.92] tracking-tight text-slate-900 tabular-nums">
            {score}
          </p>
          <p className="text-[13px] text-slate-400 tabular-nums">/ 100</p>
          <p className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
            <FiShield className="h-4 w-4 text-emerald-600" />
            {label}
          </p>

          <div className="mt-4 h-px w-full bg-slate-100" />

          <div className="mt-3.5 flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-900">Guardian Level {level}</span>
            <span className="text-slate-500 tabular-nums">{levelProgress}/100 to next</span>
          </div>
          <div className="mt-2 h-2.5 w-full rounded-full bg-slate-200/80">
            <div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-[width] duration-500"
              style={{ width: `${levelProgressPercent}%` }}
            />
          </div>
          <p className="mt-1.5 text-[12px] text-slate-400 tabular-nums">{points} pts earned</p>
        </div>

        {/* Right column: checklist */}
        <div className="md:pl-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 pb-2">
            Your defences
          </p>
          <ul className="space-y-1.5">
            {checklist.map((item) => (
              <ChecklistRow key={item.label} item={item} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Competencies style — score on left, checklist as bordered tiles on right.
// Each tile has its own little progress meter so the user can see where to
// invest next. Reads as a "skills" surface.
function ProtectionScoreCompetencies({
  score,
  label,
  summary,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  checklist: ChecklistItem[];
}) {
  const radius = 60;
  const ringSize = 156;
  const center = ringSize / 2;
  const c = 2 * Math.PI * radius;
  const offset = c - (score / 100) * c;

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-1.5">
        <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">Your Protection Score</h2>
        <HiOutlineInformationCircle className="h-[18px] w-[18px] text-slate-300" aria-hidden />
      </div>

      <div className="mt-4 grid gap-5 md:grid-cols-[156px_minmax(0,1fr)] md:items-center">
        <div className="relative mx-auto flex h-[156px] w-[156px] items-center justify-center md:mx-0">
          <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`} className="-rotate-90">
            <circle cx={center} cy={center} r={radius} fill="none" stroke="rgb(238 240 250)" strokeWidth={9} />
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="url(#competGradient)"
              strokeWidth={9}
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={offset}
            />
            <defs>
              <linearGradient id="competGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#a78bfa" />
                <stop offset="100%" stopColor="#7c3aed" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[42px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="mt-1.5 text-[12px] font-medium text-emerald-700">{label}</span>
          </div>
        </div>

        <div className="space-y-2">
          {checklist.map((item) => {
            const isDone = item.status === "good";
            const isProgress = item.status === "progress";
            const tone = isDone
              ? "border-emerald-200/80 bg-emerald-50/40"
              : isProgress
                ? "border-violet-200/80 bg-violet-50/40"
                : "border-amber-200/80 bg-amber-50/40";
            return (
              <div key={item.label} className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 ${tone}`}>
                {isDone ? (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <HiOutlineCheck className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                ) : isProgress ? (
                  <ProgressDot percent={item.percent ?? 0} />
                ) : (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <FiAlertTriangle className="h-3.5 w-3.5" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-semibold text-slate-900 leading-tight">{item.label}</p>
                  <p className="mt-0.5 text-[11.5px] text-slate-500">
                    {isDone ? "Complete" : isProgress ? `In progress · ${item.percent ?? 0}%` : "Not started"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="sr-only">{summary}</p>
    </div>
  );
}

// Levels style — gamified, level-prominent. Big level badge on left with the
// score nested below. Right side shows progress to next level + competencies.
function ProtectionScoreLevels({
  score,
  label,
  points,
  level,
  levelProgress,
  levelProgressPercent,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      {/* Header band with subtle gradient */}
      <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-violet-50/60 via-white to-indigo-50/40">
        <div className="flex items-start gap-4">
          <div className="relative flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-[24px] bg-white shadow-[0_2px_8px_rgba(124,58,237,0.18),inset_0_-2px_4px_rgba(124,58,237,0.08)] border border-violet-100/80">
            <FaTrophy className="h-9 w-9 text-violet-600" />
            <span className="absolute -bottom-1.5 -right-1.5 inline-flex h-7 min-w-[28px] items-center justify-center rounded-full bg-violet-600 px-1.5 text-[12px] font-bold text-white shadow-[0_2px_4px_rgba(124,58,237,0.3)]">
              {level}
            </span>
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">Guardian Level {level}</p>
            <p className="mt-1 text-[26px] font-semibold leading-none tracking-tight text-slate-900">
              {score}
              <span className="ml-1 text-[15px] font-medium text-slate-400">/100 protection</span>
            </p>
            <p className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
              <FiShield className="h-3.5 w-3.5 text-emerald-600" />
              {label}
            </p>
          </div>
        </div>
      </div>

      {/* Level progress */}
      <div className="px-5 sm:px-6 py-3.5 border-t border-slate-100">
        <div className="flex items-center justify-between text-[12.5px]">
          <span className="font-semibold text-slate-700">Next level: {level + 1}</span>
          <span className="text-slate-500 tabular-nums">{levelProgress}/100 · {points} pts</span>
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-slate-200/80">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500"
            style={{ width: `${levelProgressPercent}%` }}
          />
        </div>
      </div>

      {/* Competencies */}
      <div className="px-5 sm:px-6 pt-3 pb-5 border-t border-slate-100">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 pb-2">Competencies</p>
        <ul className="space-y-1.5">
          {checklist.map((item) => (
            <ChecklistRow key={item.label} item={item} />
          ))}
        </ul>
      </div>
    </div>
  );
}

// Bento style — small grid with score, level, and quick competency tiles.
// Reads as an at-a-glance dashboard.
function ProtectionScoreBento({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  const completedCount = checklist.filter((i) => i.status === "good").length;
  const totalCount = checklist.length;

  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="grid grid-cols-2 gap-2">
        {/* Score tile */}
        <div className="col-span-2 rounded-[18px] bg-gradient-to-br from-violet-50/70 via-white to-indigo-50/40 p-4 border border-violet-100/60">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[44px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[13px] font-medium text-slate-400">/100</span>
          </div>
          <p className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
        </div>

        {/* Level tile */}
        <div className="rounded-[18px] bg-slate-50/70 p-3.5 border border-slate-100">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Level</p>
          <div className="mt-1 flex items-center gap-2">
            <FaTrophy className="h-4 w-4 text-violet-600" />
            <span className="text-[20px] font-semibold leading-none text-slate-900">{level}</span>
          </div>
          <p className="mt-1.5 text-[11.5px] text-slate-500 tabular-nums">{points} pts</p>
        </div>

        {/* Defences tile */}
        <div className="rounded-[18px] bg-slate-50/70 p-3.5 border border-slate-100">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Defences</p>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-[20px] font-semibold leading-none text-slate-900 tabular-nums">{completedCount}</span>
            <span className="text-[13px] font-medium text-slate-400">/ {totalCount}</span>
          </div>
          <p className="mt-1.5 text-[11.5px] text-slate-500">in place</p>
        </div>

        {/* Checklist tile */}
        <div className="col-span-2 rounded-[18px] bg-slate-50/40 p-3.5 border border-slate-100">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 pb-2">Next steps</p>
          <ul className="space-y-1.5">
            {checklist.map((item) => (
              <ChecklistRow key={item.label} item={item} dense />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Hero style — score is the headline. Big, confident, gradient-rich. Aimed
// at making the score itself feel like an achievement, with smaller info
// stacked beneath.
function ProtectionScoreHero({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="relative px-5 sm:px-6 pt-5 pb-5 bg-gradient-to-br from-violet-600 via-violet-500 to-indigo-500 text-white">
        <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_top_right,rgba(255,255,255,0.55),transparent_55%)]" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-white/75">Protection score</p>
            <p className="mt-1 text-[68px] font-semibold leading-none tracking-tight tabular-nums">{score}</p>
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[13px] font-semibold backdrop-blur-sm">
              <FiShield className="h-3.5 w-3.5" />
              {label}
            </p>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[12px] font-semibold backdrop-blur-sm">
              <FaTrophy className="h-3 w-3" />
              Level {level}
            </div>
            <p className="mt-2 text-[12px] text-white/75 tabular-nums">{points} pts earned</p>
          </div>
        </div>
        <p className="relative mt-3 max-w-[36ch] text-[13px] leading-snug text-white/85">{summary}</p>
      </div>

      <div className="px-5 sm:px-6 py-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 pb-2">Your defences</p>
        <ul className="space-y-1.5">
          {checklist.map((item) => (
            <ChecklistRow key={item.label} item={item} />
          ))}
        </ul>
      </div>
    </div>
  );
}

// Hero · quiet — score is the hero, but the card is mostly white. Color used
// only on the score number itself (subtle violet tint), the label chip, and
// the status icons in the checklist. The number is huge so it still anchors
// attention without competing against the colored Up Next card on the right.
function ProtectionScoreHeroQuiet({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <p className="mt-1 text-[88px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
            {score}
            <span className="ml-1 text-[20px] font-medium text-slate-300">/100</span>
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[13px] font-semibold text-emerald-700 ring-1 ring-emerald-100/80">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11.5px] font-semibold text-slate-600">
            <FaTrophy className="h-3 w-3 text-violet-600" />
            Level {level}
          </span>
          <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">{points} pts</p>
        </div>
      </div>

      <p className="mt-4 max-w-[44ch] text-[13px] leading-snug text-slate-500">{summary}</p>

      <div className="mt-4 h-px w-full bg-slate-100" />

      <ul className="mt-4 space-y-1.5">
        {checklist.map((item) => (
          <ChecklistRow key={item.label} item={item} />
        ))}
      </ul>
    </div>
  );
}

// Hero · arc — semicircular gauge "speedometer" on a clean white card.
// Score sits inside the arc with a small label below. Sparing color: just
// the gradient stroke on the arc itself.
function ProtectionScoreHeroArc({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  // Half-circle arc length: PI * radius. We map score 0..100 to that length.
  const radius = 88;
  const arcLength = Math.PI * radius;
  const offset = arcLength - (score / 100) * arcLength;

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
        <div className="text-right">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11.5px] font-semibold text-slate-600">
            <FaTrophy className="h-3 w-3 text-violet-600" />
            Level {level}
          </span>
          <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">{points} pts</p>
        </div>
      </div>

      <div className="relative mx-auto mt-3 flex w-full max-w-[260px] flex-col items-center">
        <svg width={220} height={130} viewBox="0 0 220 130">
          {/* Track */}
          <path
            d={`M 18 110 A ${radius} ${radius} 0 0 1 202 110`}
            fill="none"
            stroke="rgb(238 240 250)"
            strokeWidth={14}
            strokeLinecap="round"
          />
          {/* Filled arc */}
          <path
            d={`M 18 110 A ${radius} ${radius} 0 0 1 202 110`}
            fill="none"
            stroke="url(#heroArcGrad)"
            strokeWidth={14}
            strokeLinecap="round"
            strokeDasharray={arcLength}
            strokeDashoffset={offset}
            className="transition-[stroke-dashoffset] duration-700 ease-out"
          />
          <defs>
            <linearGradient id="heroArcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#a78bfa" />
              <stop offset="100%" stopColor="#7c3aed" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-x-0 top-[36px] flex flex-col items-center">
          <span className="text-[64px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
          <span className="mt-1 text-[12px] font-medium text-slate-400">/100</span>
        </div>
      </div>

      <div className="-mt-2 flex flex-col items-center">
        <p className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
          <FiShield className="h-4 w-4 text-emerald-600" />
          {label}
        </p>
        <p className="mt-1.5 max-w-[40ch] text-center text-[12.5px] leading-snug text-slate-500">{summary}</p>
      </div>

      <div className="mt-4 h-px w-full bg-slate-100" />

      <ul className="mt-3 space-y-1.5">
        {checklist.map((item) => (
          <ChecklistRow key={item.label} item={item} />
        ))}
      </ul>
    </div>
  );
}

// Hero · split — number on the left, defences list on the right, all white.
// Big confident score; checklist gets equal billing without color flooding.
function ProtectionScoreHeroSplit({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-stretch">
        {/* Left: number */}
        <div className="md:border-r md:border-slate-100 md:pr-5">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <p className="mt-1 text-[96px] font-semibold leading-[0.92] tracking-tight text-slate-900 tabular-nums">
            {score}
          </p>
          <p className="-mt-1 text-[14px] text-slate-400 tabular-nums">/ 100</p>
          <p className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
            <FiShield className="h-4 w-4 text-emerald-600" />
            {label}
          </p>
          <p className="mt-2 max-w-[28ch] text-[12.5px] leading-snug text-slate-500">{summary}</p>
          <div className="mt-3 inline-flex items-center gap-3 text-[11.5px] text-slate-400">
            <span className="inline-flex items-center gap-1">
              <FaTrophy className="h-3 w-3 text-violet-600" /> Level {level}
            </span>
            <span className="text-slate-200">·</span>
            <span className="tabular-nums">{points} pts</span>
          </div>
        </div>

        {/* Right: checklist */}
        <div className="md:pl-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 pb-2.5">Your defences</p>
          <ul className="space-y-2">
            {checklist.map((item) => (
              <ChecklistRow key={item.label} item={item} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Hero · edge — white card with a thin colored stripe down the left edge as
// the only big block of color. Score is huge; everything else is calm.
function ProtectionScoreHeroEdge({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-violet-500 to-indigo-500" aria-hidden />
      <div className="pl-6 pr-5 sm:pl-7 sm:pr-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="text-[92px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
              <span className="text-[18px] font-medium text-slate-300">/100</span>
            </div>
            <p className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
              <FiShield className="h-4 w-4 text-emerald-600" />
              {label}
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11.5px] font-semibold text-slate-600">
              <FaTrophy className="h-3 w-3 text-violet-600" />
              Level {level}
            </span>
            <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">{points} pts</p>
          </div>
        </div>

        <p className="mt-3 max-w-[44ch] text-[13px] leading-snug text-slate-500">{summary}</p>

        <div className="mt-4 h-px w-full bg-slate-100" />

        <ul className="mt-3.5 space-y-1.5">
          {checklist.map((item) => (
            <ChecklistRow key={item.label} item={item} />
          ))}
        </ul>
      </div>
    </div>
  );
}

// Hero · edge split — same thin colored stripe down the left edge, but the
// body of the card splits 50/50: score + label + level on the left, defences
// list on the right with a vertical hairline divider between them. Keeps the
// "edge" personality but trades vertical stacking for a side-by-side balance.
function ProtectionScoreHeroEdgeSplit({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-violet-500 to-indigo-500" aria-hidden />
      <div className="pl-6 pr-5 sm:pl-7 sm:pr-6 py-5">
        <div className="grid gap-5 md:grid-cols-2 md:items-stretch">
          {/* Left column: score + label + level/points */}
          <div className="md:border-r md:border-slate-100 md:pr-5">
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
              Protection score
            </p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-[80px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
                {score}
              </span>
              <span className="text-[16px] font-medium text-slate-300">/100</span>
            </div>
            <p className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
              <FiShield className="h-4 w-4 text-emerald-600" />
              {label}
            </p>
            <p className="mt-2.5 max-w-[28ch] text-[12.5px] leading-snug text-slate-500">{summary}</p>
            <div className="mt-3 inline-flex items-center gap-3 text-[11.5px] text-slate-400">
              <span className="inline-flex items-center gap-1">
                <FaTrophy className="h-3 w-3 text-violet-600" /> Level {level}
              </span>
              <span className="text-slate-200">·</span>
              <span className="tabular-nums">{points} pts</span>
            </div>
          </div>

          {/* Right column: defences */}
          <div className="md:pl-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 pb-2.5">
              Your defences
            </p>
            <ul className="space-y-2">
              {checklist.map((item) => (
                <ChecklistRow key={item.label} item={item} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Shared chrome — same edge stripe + outer shell across the family. Keeps
// every variant feeling like a sibling rather than a redesign.
function HeroEdgeShell({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-violet-500 to-indigo-500" aria-hidden />
      {children}
    </div>
  );
}

// Hero · edge balanced — true 50/50 with both halves vertically centered. The
// score reads as the visual anchor on the left, defences carry equal weight
// on the right. Hairline divider runs full height of the body so the two
// halves feel like equal panels rather than one feeling like an afterthought.
function ProtectionScoreHeroEdgeBalanced({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <HeroEdgeShell>
      <div className="grid pl-6 pr-5 sm:pl-7 sm:pr-6 py-5 md:grid-cols-2 md:divide-x md:divide-slate-100">
        {/* Left: score, vertically centered */}
        <div className="flex flex-col justify-center md:pr-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
            Protection score
          </p>
          <div className="mt-1.5 flex items-baseline gap-2.5">
            <span className="text-[88px] font-semibold leading-[0.92] tracking-tight text-slate-900 tabular-nums">
              {score}
            </span>
            <span className="text-[16px] font-medium text-slate-300">/100</span>
          </div>
          <p className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
            <FiShield className="h-4 w-4 text-emerald-600" />
            {label}
          </p>
          <p className="mt-2.5 max-w-[28ch] text-[12.5px] leading-snug text-slate-500">{summary}</p>
        </div>

        {/* Right: defences, vertically centered */}
        <div className="mt-5 md:mt-0 md:pl-6 flex flex-col justify-center">
          <div className="flex items-center justify-between pb-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
              Your defences
            </p>
            <span className="text-[10.5px] font-medium text-slate-400 tabular-nums">
              Level {level} · {points} pts
            </span>
          </div>
          <ul className="space-y-2">
            {checklist.map((item) => (
              <ChecklistRow key={item.label} item={item} />
            ))}
          </ul>
        </div>
      </div>
    </HeroEdgeShell>
  );
}

// Hero · edge stacked — three orderly horizontal bands. Top: score + label.
// Middle: thin meta strip with level + points. Bottom: defences row. Each
// band breathes independently and the user reads top-to-bottom naturally.
function ProtectionScoreHeroEdgeStacked({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <HeroEdgeShell>
      <div className="pl-6 pr-5 sm:pl-7 sm:pr-6">
        {/* Band 1: hero */}
        <div className="py-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
              Protection score
            </p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-[80px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
                {score}
              </span>
              <span className="text-[16px] font-medium text-slate-300">/100</span>
            </div>
          </div>
          <div className="text-right pb-1">
            <p className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
              <FiShield className="h-4 w-4 text-emerald-600" />
              {label}
            </p>
            <p className="mt-1.5 max-w-[28ch] text-[12.5px] leading-snug text-slate-500">{summary}</p>
          </div>
        </div>

        {/* Band 2: meta strip */}
        <div className="border-t border-slate-100 py-2.5 flex items-center justify-between text-[12px] text-slate-500">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <FaTrophy className="h-3 w-3 text-violet-600" />
            Guardian Level {level}
          </span>
          <span className="tabular-nums">{points} pts earned</span>
        </div>

        {/* Band 3: defences */}
        <div className="border-t border-slate-100 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 pb-2.5">
            Your defences
          </p>
          <ul className="space-y-1.5">
            {checklist.map((item) => (
              <ChecklistRow key={item.label} item={item} />
            ))}
          </ul>
        </div>
      </div>
    </HeroEdgeShell>
  );
}

// Hero · edge tiles — score on top as a centered hero, then defences as a
// row of three equal mini-tiles below. Reads as a dashboard summary: one
// big number, three discrete states. Clean grid, very modern.
function ProtectionScoreHeroEdgeTiles({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <HeroEdgeShell>
      <div className="pl-6 pr-5 sm:pl-7 sm:pr-6 py-5">
        {/* Hero band */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
              Protection score
            </p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-[72px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
                {score}
              </span>
              <span className="text-[16px] font-medium text-slate-300">/100</span>
            </div>
            <p className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
              <FiShield className="h-4 w-4 text-emerald-600" />
              {label}
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11.5px] font-semibold text-slate-600">
              <FaTrophy className="h-3 w-3 text-violet-600" />
              Level {level}
            </span>
            <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">{points} pts</p>
          </div>
        </div>

        <p className="mt-3 max-w-[44ch] text-[13px] leading-snug text-slate-500">{summary}</p>

        {/* Defences tile row */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          {checklist.map((item) => {
            const isDone = item.status === "good";
            const isProgress = item.status === "progress";
            return (
              <div
                key={item.label}
                className="rounded-xl border border-slate-100 bg-slate-50/50 p-3"
              >
                {isDone ? (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
                  </span>
                ) : isProgress ? (
                  <ProgressDot percent={item.percent ?? 0} />
                ) : (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <FiAlertTriangle className="h-3 w-3" />
                  </span>
                )}
                <p className="mt-2 text-[12.5px] font-semibold text-slate-900 leading-tight">
                  {item.label}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500">
                  {isDone
                    ? "Complete"
                    : isProgress
                      ? `${item.percent ?? 0}%`
                      : "Not started"}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </HeroEdgeShell>
  );
}

// Hero · edge rail — narrow rail on the left holds score + label as a tight
// "stat column," then a wider area on the right gives defences room to read
// comfortably. Asymmetric but feels balanced because the rail is slim and
// confident, the right side is generous and breathable.
function ProtectionScoreHeroEdgeRail({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <HeroEdgeShell>
      <div className="grid pl-6 pr-5 sm:pl-7 sm:pr-6 py-5 md:grid-cols-[180px_minmax(0,1fr)] md:gap-6 md:items-stretch">
        {/* Stat rail */}
        <div className="md:border-r md:border-slate-100 md:pr-5 flex flex-col justify-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            Protection
          </p>
          <p className="mt-0.5 text-[64px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
            {score}
          </p>
          <p className="text-[12px] font-medium text-slate-400 tabular-nums">/ 100</p>
          <p className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-400">
            <FaTrophy className="h-3 w-3 text-violet-600" />
            <span className="font-medium">Lvl {level}</span>
            <span className="text-slate-200">·</span>
            <span className="tabular-nums">{points} pts</span>
          </div>
        </div>

        {/* Defences area */}
        <div className="mt-4 md:mt-0 flex flex-col justify-center">
          <div className="flex items-center justify-between pb-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
              Your defences
            </p>
            <span className="text-[11px] text-slate-400 max-w-[24ch] text-right hidden sm:block">
              {summary}
            </span>
          </div>
          <ul className="space-y-2">
            {checklist.map((item) => (
              <ChecklistRow key={item.label} item={item} />
            ))}
          </ul>
        </div>
      </div>
    </HeroEdgeShell>
  );
}

// Hero · edge minimal — the quietest of the family. Score stays bold and
// confident; defences collapse to a single line "3/3 in place" with three
// tiny status icons. Ideal when this is one of many cards on the page.
function ProtectionScoreHeroEdgeMinimal({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  const completedCount = checklist.filter((i) => i.status === "good").length;
  const totalCount = checklist.length;

  return (
    <HeroEdgeShell>
      <div className="pl-6 pr-5 sm:pl-7 sm:pr-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
              Protection score
            </p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-[88px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
                {score}
              </span>
              <span className="text-[16px] font-medium text-slate-300">/100</span>
            </div>
            <p className="mt-2.5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
              <FiShield className="h-4 w-4 text-emerald-600" />
              {label}
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11.5px] font-semibold text-slate-600">
              <FaTrophy className="h-3 w-3 text-violet-600" />
              Level {level}
            </span>
            <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">{points} pts</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50/70 px-3.5 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-1">
              {checklist.map((item) => (
                <span
                  key={item.label}
                  className={`flex h-[18px] w-[18px] items-center justify-center rounded-full ring-2 ring-white ${
                    item.status === "good"
                      ? "bg-emerald-600 text-white"
                      : item.status === "progress"
                        ? "bg-violet-100"
                        : "bg-amber-100 text-amber-600"
                  }`}
                  title={item.label}
                >
                  {item.status === "good" ? (
                    <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
                  ) : item.status === "progress" ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-violet-600" />
                  ) : (
                    <FiAlertTriangle className="h-3 w-3" />
                  )}
                </span>
              ))}
            </div>
            <p className="text-[12.5px] font-medium text-slate-700">
              {completedCount} of {totalCount} defences in place
            </p>
          </div>
          <span className="text-[11.5px] font-semibold text-violet-700">View all</span>
        </div>
      </div>
    </HeroEdgeShell>
  );
}

/* ─── Free-form score cards ───────────────────────────────────────── */
//
// These five step away from the edge family and try to answer different
// "what's the user supposed to take away" questions:
//
//   journey       → "I'm moving along a path. Here's where I am."
//   next-action   → "Here is the single best thing to do next."
//   trajectory    → "I've been growing. Here's recent momentum."
//   milestone-gap → "I'm N points from the next named state."
//   skill-grid    → "Here are my skills as a system, and what's next."

const NEXT_ACTION_BY_LABEL: Record<string, { title: string; description: string; cta: string; to: string; reward: number }> = {
  "Account setup": {
    title: "Finish setting up your account",
    description: "Three quick questions personalize your defences and unlock your dashboard.",
    cta: "Complete setup",
    to: "/onboarding",
    reward: 33,
  },
  "Guided walkthrough": {
    title: "Take the guided walkthrough",
    description: "Two minutes to learn what every card does. Earn points and confidence in one go.",
    cta: "Start walkthrough",
    to: "/missions",
    reward: 33,
  },
  "Fundamentals guide": {
    title: "Continue Scam Fundamentals",
    description: "Each chapter teaches one habit that real scams target. Pick up where you left off.",
    cta: "Resume guide",
    to: "/learning-journeys/scam-fundamentals",
    reward: 34,
  },
};

function pickNextAction(checklist: ChecklistItem[]) {
  // First incomplete item — that's the highest-leverage next step.
  const firstIncomplete = checklist.find((i) => i.status !== "good");
  if (!firstIncomplete) return null;
  return NEXT_ACTION_BY_LABEL[firstIncomplete.label];
}

// JOURNEY — milestones along a path.
//
// Takeaway: "I started here, I'm here now, and Strong defences is the goal."
// The dot-and-line visual communicates *movement* in a way numbers can't.
// Especially powerful for new users who can see the whole arc upfront.
function ProtectionScoreJourney({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  // Five fixed waypoints. The user's "current position" is mapped from score.
  const milestones = [
    { name: "Just beginning", threshold: 0 },
    { name: "Getting started", threshold: 40 },
    { name: "Good foundation", threshold: 65 },
    { name: "Strong defences", threshold: 85 },
    { name: "Guardian", threshold: 100 },
  ];

  // Position the player as a percentage along the track.
  const playerPct = Math.max(0, Math.min(100, score));

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Your journey</p>
          <p className="mt-1 text-[16px] font-semibold tracking-tight text-slate-900">
            You're at <span className="text-violet-700">{label}</span>
          </p>
          <p className="mt-0.5 text-[12.5px] text-slate-500 tabular-nums">
            Score {score}/100 · Level {level} · {points} pts
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-[12px] font-semibold text-violet-700 ring-1 ring-violet-100">
          <FaTrophy className="h-3 w-3" />
          {milestones.findIndex((m) => m.name === label) + 1} of {milestones.length}
        </span>
      </div>

      {/* Path track */}
      <div className="relative mt-7 mb-2">
        {/* Base track */}
        <div className="absolute left-2 right-2 top-[11px] h-1 rounded-full bg-slate-100" />
        {/* Filled track up to player */}
        <div
          className="absolute left-2 top-[11px] h-1 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all duration-700"
          style={{ width: `calc(${playerPct}% - 16px)` }}
        />

        <div className="relative flex items-start justify-between">
          {milestones.map((m) => {
            const reached = score >= m.threshold;
            const isCurrent = m.name === label;
            return (
              <div key={m.name} className="flex flex-col items-center" style={{ width: `${100 / milestones.length}%` }}>
                <span
                  className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white ${
                    isCurrent
                      ? "bg-violet-600 text-white shadow-[0_0_0_4px_rgba(139,92,246,0.18)]"
                      : reached
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-200 text-slate-400"
                  }`}
                >
                  {isCurrent ? (
                    <span className="h-2 w-2 rounded-full bg-white" />
                  ) : reached ? (
                    <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
                  ) : null}
                </span>
                <span
                  className={`mt-2 text-center text-[10.5px] font-medium leading-tight ${
                    isCurrent ? "text-violet-700" : reached ? "text-slate-700" : "text-slate-400"
                  }`}
                >
                  {m.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-5 h-px w-full bg-slate-100" />

      <ul className="mt-3 space-y-1.5">
        {checklist.map((item) => (
          <ChecklistRow key={item.label} item={item} />
        ))}
      </ul>
    </div>
  );
}

// NEXT ACTION — one decisive thing to do next.
//
// Takeaway: "If I do this one thing, my score goes up." Replaces the
// "checklist as wall" with "checklist as queue, top item promoted."
// Best for users prone to deciding-fatigue. Score is supporting, not lead.
function ProtectionScoreNextAction({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  const next = pickNextAction(checklist);
  const completedCount = checklist.filter((i) => i.status === "good").length;
  const totalCount = checklist.length;

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[44px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
              {score}
            </span>
            <span className="text-[14px] font-medium text-slate-300">/100</span>
            <span className="ml-1 inline-flex items-center gap-1 text-[12.5px] font-semibold text-emerald-700">
              <FiShield className="h-3.5 w-3.5 text-emerald-600" />
              {label}
            </span>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11.5px] font-semibold text-slate-600">
          <FaTrophy className="h-3 w-3 text-violet-600" />
          Lvl {level} · {points} pts
        </span>
      </div>

      {next ? (
        <Link
          to={next.to}
          search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
          className="group mt-4 block rounded-[18px] border border-violet-100 bg-gradient-to-br from-violet-50/70 via-white to-indigo-50/40 p-4 transition-shadow hover:shadow-[0_4px_12px_rgba(124,58,237,0.12)]"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-700">
            Do this next
          </p>
          <p className="mt-1.5 text-[16px] font-semibold leading-tight text-slate-900">
            {next.title}
          </p>
          <p className="mt-1.5 max-w-[40ch] text-[12.5px] leading-snug text-slate-500">
            {next.description}
          </p>
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors group-hover:bg-violet-700">
              {next.cta}
              <HiOutlineArrowRight className="h-3.5 w-3.5" />
            </span>
            <span className="text-[11.5px] font-semibold text-violet-700">+{next.reward} pts</span>
          </div>
        </Link>
      ) : (
        <div className="mt-4 rounded-[18px] border border-emerald-100 bg-emerald-50/50 p-4">
          <p className="text-[14px] font-semibold text-emerald-800">All defences complete.</p>
          <p className="mt-1 text-[12.5px] text-emerald-700/80">Keep your habits sharp — review weekly.</p>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50/70 px-3.5 py-2">
        <p className="text-[11.5px] font-medium text-slate-500">
          {completedCount} of {totalCount} defences complete
        </p>
        <div className="flex -space-x-1">
          {checklist.map((item) => (
            <span
              key={item.label}
              title={item.label}
              className={`flex h-[18px] w-[18px] items-center justify-center rounded-full ring-2 ring-white ${
                item.status === "good"
                  ? "bg-emerald-600 text-white"
                  : item.status === "progress"
                    ? "bg-violet-100"
                    : "bg-amber-100 text-amber-600"
              }`}
            >
              {item.status === "good" ? (
                <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
              ) : item.status === "progress" ? (
                <span className="h-1.5 w-1.5 rounded-full bg-violet-600" />
              ) : (
                <FiAlertTriangle className="h-3 w-3" />
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// TRAJECTORY — score over time.
//
// Takeaway: "Look how far I've come." Sparkline reframes the score from a
// static number into a story of progress. Especially powerful 30+ days in,
// when the user can see "I've gone from 32 → 78". Mocked weekly history.
function ProtectionScoreTrajectory({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  // Mock 7 weekly points up to current score. In production, derive from a
  // ScoreSnapshot history table (one row per week, last 12 weeks).
  const series = [Math.max(0, score - 30), Math.max(0, score - 24), Math.max(0, score - 18), Math.max(0, score - 14), Math.max(0, score - 10), Math.max(0, score - 4), score];
  const min = Math.min(...series);
  const max = Math.max(...series, min + 1);
  const w = 240;
  const h = 60;
  const points2D = series.map((v, i) => {
    const x = (i / (series.length - 1)) * w;
    const y = h - ((v - min) / (max - min)) * h;
    return [x, y] as const;
  });
  const path = points2D.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const areaPath = `${path} L ${w} ${h} L 0 ${h} Z`;
  const delta = series[series.length - 1] - series[0];

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-[64px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[14px] font-medium text-slate-300">/100</span>
          </div>
          <p className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
            <FiShield className="h-4 w-4 text-emerald-600" />
            {label}
          </p>
        </div>
        <div className="text-right">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${
              delta > 0
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
                : delta < 0
                  ? "bg-rose-50 text-rose-700 ring-1 ring-rose-100"
                  : "bg-slate-50 text-slate-600 ring-1 ring-slate-100"
            }`}
          >
            <HiOutlineTrendingUp className={`h-3.5 w-3.5 ${delta < 0 ? "rotate-180" : ""}`} />
            {delta > 0 ? `+${delta}` : delta} this month
          </span>
          <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">Lvl {level} · {points} pts</p>
        </div>
      </div>

      {/* Sparkline */}
      <div className="mt-4">
        <svg width="100%" viewBox={`0 0 ${w} ${h + 4}`} preserveAspectRatio="none" className="block w-full">
          <defs>
            <linearGradient id="trajArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#trajArea)" />
          <path d={path} fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {/* Final dot */}
          <circle cx={points2D[points2D.length - 1][0]} cy={points2D[points2D.length - 1][1]} r="3.5" fill="#7c3aed" stroke="white" strokeWidth="2" />
        </svg>
        <div className="mt-1 flex items-center justify-between text-[10px] font-medium uppercase tracking-[0.1em] text-slate-400">
          <span>4 weeks ago</span>
          <span>This week</span>
        </div>
      </div>

      <div className="mt-4 h-px w-full bg-slate-100" />

      <ul className="mt-3 space-y-1.5">
        {checklist.map((item) => (
          <ChecklistRow key={item.label} item={item} />
        ))}
      </ul>
    </div>
  );
}

// MILESTONE GAP — progress to the next named tier.
//
// Takeaway: "I am 22 points from Strong defences. Here's what I need."
// Reframes the abstract score as a closeable gap. Naming the *next* state
// gives the user something to want. The bar shows how close they are.
function ProtectionScoreMilestoneGap({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  const tiers = [
    { name: "Just beginning", floor: 0 },
    { name: "Getting started", floor: 40 },
    { name: "Good foundation", floor: 65 },
    { name: "Strong defences", floor: 85 },
    { name: "Guardian", floor: 100 },
  ];
  const currentIdx = Math.max(0, tiers.findIndex((t, i) => score >= t.floor && (i === tiers.length - 1 || score < tiers[i + 1].floor)));
  const current = tiers[currentIdx];
  const next = tiers[Math.min(tiers.length - 1, currentIdx + 1)];
  const span = next.floor - current.floor;
  const progressIntoTier = Math.max(0, Math.min(span, score - current.floor));
  const tierPct = span === 0 ? 100 : Math.round((progressIntoTier / span) * 100);
  const remaining = Math.max(0, next.floor - score);
  const next1 = pickNextAction(checklist);

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[60px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[14px] font-medium text-slate-300">/100</span>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11.5px] font-semibold text-slate-600">
          <FaTrophy className="h-3 w-3 text-violet-600" />
          Lvl {level} · {points} pts
        </span>
      </div>

      {/* Gap visualization */}
      <div className="mt-4 rounded-[18px] bg-slate-50/70 p-4">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-[12.5px] font-semibold text-slate-600">{current.name}</p>
          <p className="text-[12.5px] font-semibold text-slate-400">{next.name}</p>
        </div>
        <div className="relative mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-200/80">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all duration-700"
            style={{ width: `${tierPct}%` }}
          />
        </div>
        {remaining > 0 ? (
          <p className="mt-2 text-[13px] font-medium text-slate-700">
            <span className="text-violet-700 tabular-nums">{remaining} pts</span> to <span className="text-slate-900 font-semibold">{next.name}</span>
          </p>
        ) : (
          <p className="mt-2 text-[13px] font-semibold text-emerald-700">You've reached the top tier — Guardian.</p>
        )}
      </div>

      {/* Suggestion */}
      {next1 && (
        <Link
          to={next1.to}
          search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
          className="group mt-3 flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-2.5 transition-colors hover:bg-slate-50"
        >
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-700">
              Closest next step
            </p>
            <p className="mt-0.5 text-[13.5px] font-semibold text-slate-900">
              {next1.title}
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-violet-700 shrink-0">
            +{next1.reward} pts
            <HiOutlineArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>
      )}

      <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-700">
        <FiShield className="h-3.5 w-3.5 text-emerald-600" />
        Currently: {label}
      </div>
    </div>
  );
}

// SKILL GRID — defences as a system.
//
// Takeaway: "These are my three skills, here's where each one stands."
// Most game-y of the set. Each defence is a node with its own status mark
// and a shared score header. Visually distinctive because no other variant
// presents the defences as equally-weighted skill cells with state colors.
function ProtectionScoreSkillGrid({
  score,
  label,
  summary,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      {/* Header — score is small, lives at the top */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Your defence kit</p>
          <p className="mt-1 text-[16px] font-semibold tracking-tight text-slate-900">
            {label} · <span className="text-violet-700 tabular-nums">{score}/100</span>
          </p>
          <p className="mt-0.5 max-w-[40ch] text-[12px] leading-snug text-slate-500">{summary}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11.5px] font-semibold text-slate-600 shrink-0">
          <FaTrophy className="h-3 w-3 text-violet-600" />
          Lvl {level} · {points} pts
        </span>
      </div>

      {/* Skill cells */}
      <div className="mt-4 grid grid-cols-3 gap-2.5">
        {checklist.map((item) => {
          const isDone = item.status === "good";
          const isProgress = item.status === "progress";
          return (
            <div
              key={item.label}
              className={`relative overflow-hidden rounded-[16px] border p-3.5 transition-shadow ${
                isDone
                  ? "border-emerald-200 bg-emerald-50/40"
                  : isProgress
                    ? "border-violet-200 bg-violet-50/40"
                    : "border-slate-200 bg-slate-50/50"
              }`}
            >
              {isProgress && (
                <div
                  className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-violet-500 to-indigo-500"
                  style={{ width: `${item.percent ?? 0}%` }}
                />
              )}
              <div className="flex items-start justify-between gap-2">
                {isDone ? (
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <HiOutlineCheck className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                ) : isProgress ? (
                  <ProgressDot percent={item.percent ?? 0} />
                ) : (
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-slate-500">
                    <HiOutlineLockClosed className="h-3.5 w-3.5" />
                  </span>
                )}
                <span
                  className={`text-[10px] font-bold uppercase tracking-[0.06em] ${
                    isDone ? "text-emerald-700" : isProgress ? "text-violet-700" : "text-slate-400"
                  }`}
                >
                  {isDone ? "Active" : isProgress ? "Building" : "Locked"}
                </span>
              </div>
              <p className="mt-3 text-[13px] font-semibold text-slate-900 leading-tight">{item.label}</p>
              <p className="mt-0.5 text-[10.5px] text-slate-500">
                {isDone
                  ? "Defence in place"
                  : isProgress
                    ? `${item.percent ?? 0}% trained`
                    : "Start to unlock"}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Credit-score-inspired cards ─────────────────────────────────── */
//
// Mental model: protection is a composite of weighted "factors," much like a
// FICO/Credit Karma score. Each card surfaces this reality differently:
//
//   credit-gauge      → speedometer with grade, factor pills below
//   credit-breakdown  → segmented contribution bar + per-factor rows
//   credit-factors    → "what's helping" vs "what's hurting" columns
//   credit-peer       → percentile + distribution histogram
//   credit-audit      → dense audit-report view, factor-by-factor

// Per-factor weights match the real score calc in VariantE: 33 / 33 / 34.
type ScoreFactor = {
  label: string;
  weight: number; // max points this factor contributes
  earned: number; // points actually earned
  status: "good" | "progress" | "info";
  percent?: number;
};

function buildFactors(checklist: ChecklistItem[]): ScoreFactor[] {
  // Map checklist items 1:1 to weighted factors.
  const weights: Record<string, number> = {
    "Account setup": 33,
    "Guided walkthrough": 33,
    "Fundamentals guide": 34,
  };
  return checklist.map((item) => {
    const weight = weights[item.label] ?? 0;
    const earned =
      item.status === "good"
        ? weight
        : item.status === "progress"
          ? Math.round(((item.percent ?? 0) / 100) * weight)
          : 0;
    return { label: item.label, weight, earned, status: item.status, percent: item.percent };
  });
}

// Letter grade derived from score, similar to credit-rating tiers.
function gradeForScore(score: number) {
  if (score >= 90) return { letter: "A+", tone: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-100" };
  if (score >= 80) return { letter: "A", tone: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-100" };
  if (score >= 65) return { letter: "B", tone: "text-violet-600", bg: "bg-violet-50", ring: "ring-violet-100" };
  if (score >= 45) return { letter: "C", tone: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-100" };
  if (score >= 25) return { letter: "D", tone: "text-orange-600", bg: "bg-orange-50", ring: "ring-orange-100" };
  return { letter: "F", tone: "text-rose-600", bg: "bg-rose-50", ring: "ring-rose-100" };
}

// CREDIT · GAUGE — speedometer with tier zones.
//
// Takeaway: "I'm in the B band, doing well, on track to A." Maps the FICO
// tier-color visual: red/amber/violet/green arc segments, score in centre,
// grade letter top-right, factors as small pills below.
function ProtectionScoreCreditGauge({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  const factors = buildFactors(checklist);
  const grade = gradeForScore(score);
  const radius = 86;
  const circumference = Math.PI * radius;
  // Five tiers: 0–25, 25–45, 45–65, 65–85, 85–100. Each segment gets its
  // own color zone in the arc.
  const zones = [
    { from: 0, to: 25, color: "#fb7185" },
    { from: 25, to: 45, color: "#fbbf24" },
    { from: 45, to: 65, color: "#facc15" },
    { from: 65, to: 85, color: "#a78bfa" },
    { from: 85, to: 100, color: "#10b981" },
  ];
  // Indicator rotation: -90 (left) at 0, +90 (right) at 100.
  const indicatorAngle = -90 + (score / 100) * 180;

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection rating</p>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold ring-1 ${grade.tone} ${grade.bg} ${grade.ring}`}>
          Grade {grade.letter}
        </span>
      </div>

      {/* Gauge */}
      <div className="relative mx-auto mt-2 flex w-full max-w-[280px] flex-col items-center">
        <svg width={240} height={140} viewBox="0 0 240 140">
          {/* Track segments */}
          {zones.map((z, i) => {
            const fromPct = z.from / 100;
            const toPct = z.to / 100;
            const dashLength = (toPct - fromPct) * circumference;
            const startOffset = fromPct * circumference;
            return (
              <path
                key={i}
                d={`M 30 120 A ${radius} ${radius} 0 0 1 210 120`}
                fill="none"
                stroke={z.color}
                strokeWidth={12}
                strokeLinecap="butt"
                strokeDasharray={`${dashLength} ${circumference}`}
                strokeDashoffset={-startOffset}
                opacity={score >= z.from ? 1 : 0.25}
              />
            );
          })}
          {/* Indicator needle */}
          <g transform={`translate(120 120) rotate(${indicatorAngle})`}>
            <line x1={0} y1={0} x2={0} y2={-radius - 6} stroke="#0f172a" strokeWidth={2.5} strokeLinecap="round" />
            <circle cx={0} cy={0} r={5} fill="#0f172a" />
          </g>
        </svg>
        <div className="absolute inset-x-0 top-[44px] flex flex-col items-center">
          <span className="text-[56px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
          <span className="mt-1 text-[11px] font-medium text-slate-400">/100</span>
        </div>
      </div>

      <div className="mt-1 flex flex-col items-center">
        <p className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-slate-900">
          <FiShield className={`h-4 w-4 ${grade.tone}`} />
          {label}
        </p>
        <p className="mt-1 text-[11.5px] text-slate-400 tabular-nums">Lvl {level} · {points} pts</p>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {factors.map((f) => (
          <span
            key={f.label}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${
              f.status === "good"
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
                : f.status === "progress"
                  ? "bg-violet-50 text-violet-700 ring-1 ring-violet-100"
                  : "bg-slate-50 text-slate-500 ring-1 ring-slate-100"
            }`}
          >
            {f.label}
            <span className="tabular-nums opacity-70">
              {f.earned}/{f.weight}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// CREDIT · BREAKDOWN — segmented contribution bar.
//
// Takeaway: "Here's exactly how I got to 78. Each component contributes
// proportional to its weight." Reads like a credit-report factor breakdown.
function ProtectionScoreCreditBreakdown({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  const factors = buildFactors(checklist);
  const grade = gradeForScore(score);
  const factorTones = [
    { earned: "bg-emerald-500", track: "bg-emerald-100" },
    { earned: "bg-violet-500", track: "bg-violet-100" },
    { earned: "bg-indigo-500", track: "bg-indigo-100" },
  ];

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[60px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[14px] font-medium text-slate-300">/100</span>
          </div>
          <p className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center justify-center rounded-2xl px-3 py-2 text-[24px] font-semibold ring-1 ${grade.tone} ${grade.bg} ${grade.ring}`}>
            {grade.letter}
          </span>
          <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">Lvl {level} · {points} pts</p>
        </div>
      </div>

      {/* Segmented contribution bar */}
      <div className="mt-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 pb-2">How your score adds up</p>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
          {factors.map((f, i) => {
            const widthPct = f.earned; // points map 1:1 to width %
            const tone = factorTones[i % factorTones.length].earned;
            return widthPct > 0 ? (
              <div
                key={f.label}
                className={`h-full ${tone} ${i === 0 ? "" : "border-l-2 border-white"}`}
                style={{ width: `${widthPct}%` }}
                title={`${f.label}: ${f.earned}/${f.weight}`}
              />
            ) : null;
          })}
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-400 tabular-nums">
          <span>0</span>
          <span>{score} earned</span>
          <span>100</span>
        </div>
      </div>

      {/* Per-factor rows */}
      <div className="mt-4 space-y-2.5">
        {factors.map((f, i) => {
          const factorPct = f.weight === 0 ? 0 : Math.round((f.earned / f.weight) * 100);
          const tones = factorTones[i % factorTones.length];
          return (
            <div key={f.label}>
              <div className="flex items-center justify-between gap-2 text-[12.5px]">
                <span className="font-semibold text-slate-700">{f.label}</span>
                <span className="text-slate-500 tabular-nums">
                  {f.earned} / {f.weight} pts
                  <span className="ml-2 text-[11px] text-slate-400">{factorPct}%</span>
                </span>
              </div>
              <div className={`mt-1 h-1.5 w-full overflow-hidden rounded-full ${tones.track}`}>
                <div className={`h-full rounded-full ${tones.earned}`} style={{ width: `${factorPct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// CREDIT · FACTORS — what's helping vs what's hurting.
//
// Takeaway: "These specific things lifted my score. These are dragging it
// down." Same content frame as Credit Karma's "factors affecting your score."
// Reduces ambiguity about *why* a number is what it is.
function ProtectionScoreCreditFactors({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  const factors = buildFactors(checklist);
  const helping = factors.filter((f) => f.status === "good" || (f.status === "progress" && f.earned > 0));
  const hurting = factors.filter((f) => f.status === "info" || (f.status === "progress" && f.earned < f.weight));
  const grade = gradeForScore(score);

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[56px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[14px] font-medium text-slate-300">/100</span>
            <span className={`ml-2 inline-flex items-center justify-center rounded-lg px-2 py-1 text-[14px] font-semibold ring-1 ${grade.tone} ${grade.bg} ${grade.ring}`}>
              {grade.letter}
            </span>
          </div>
          <p className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
        </div>
        <p className="text-[11.5px] text-slate-400 tabular-nums">Lvl {level} · {points} pts</p>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {/* Helping */}
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700 pb-2">
            What's helping ({helping.length})
          </p>
          {helping.length > 0 ? (
            <ul className="space-y-2">
              {helping.map((f) => (
                <li key={f.label} className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-slate-700 leading-tight">{f.label}</p>
                    <p className="text-[10.5px] text-emerald-700 tabular-nums">+{f.earned} pts</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] text-slate-500">No factors lifting your score yet.</p>
          )}
        </div>

        {/* Hurting */}
        <div className="rounded-xl border border-amber-100 bg-amber-50/30 p-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700 pb-2">
            Holding you back ({hurting.length})
          </p>
          {hurting.length > 0 ? (
            <ul className="space-y-2">
              {hurting.map((f) => {
                const lostPts = f.weight - f.earned;
                return (
                  <li key={f.label} className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                      <FiAlertTriangle className="h-3 w-3" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-semibold text-slate-700 leading-tight">{f.label}</p>
                      <p className="text-[10.5px] text-amber-700 tabular-nums">
                        −{lostPts} pts unrealised
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-[12px] text-slate-500">Nothing holding you back. 🎉</p>
          )}
        </div>
      </div>
    </div>
  );
}

// CREDIT · PEER — percentile vs the population.
//
// Takeaway: "I'm doing better than 78% of households." Tap social proof —
// a deeply effective motivator from credit-score apps. Histogram makes the
// percentile concrete; copy keeps it positive even at low scores.
function ProtectionScoreCreditPeer({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  // Mock a percentile from score. In production this would come from server-
  // computed population stats per market. Skewed so most users sit mid-range.
  const percentile = Math.min(99, Math.max(2, Math.round(score * 0.85 + 5)));
  const grade = gradeForScore(score);
  // 20-bin histogram (0–5, 5–10, …, 95–100). Bell-ish shape mocked here.
  const bins = Array.from({ length: 20 }, (_, i) => {
    const center = i * 5 + 2.5;
    const dist = Math.abs(center - 55) / 25;
    return Math.round(80 * Math.exp(-dist * dist * 1.5)) + (i % 2 === 0 ? 6 : 4);
  });
  const userBin = Math.min(19, Math.floor(score / 5));
  const maxBinHeight = Math.max(...bins);

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[60px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[14px] font-medium text-slate-300">/100</span>
            <span className={`ml-1 inline-flex items-center justify-center rounded-lg px-2 py-1 text-[14px] font-semibold ring-1 ${grade.tone} ${grade.bg} ${grade.ring}`}>
              {grade.letter}
            </span>
          </div>
          <p className="mt-1 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[28px] font-semibold leading-none tracking-tight text-violet-700 tabular-nums">
            {percentile}<span className="text-[14px] text-slate-400 align-top">%</span>
          </p>
          <p className="mt-1 text-[11px] text-slate-500 leading-tight">
            Stronger than<br /> {percentile}% of households
          </p>
        </div>
      </div>

      {/* Histogram */}
      <div className="mt-5">
        <div className="flex h-16 w-full items-end gap-[3px]">
          {bins.map((v, i) => {
            const heightPct = (v / maxBinHeight) * 100;
            const isUser = i === userBin;
            return (
              <div key={i} className="relative flex-1">
                <div
                  className={`w-full rounded-sm transition-colors ${
                    isUser
                      ? "bg-violet-600"
                      : i < userBin
                        ? "bg-violet-200"
                        : "bg-slate-100"
                  }`}
                  style={{ height: `${heightPct}%` }}
                />
                {isUser && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex h-2 w-2 rounded-full bg-violet-600 ring-2 ring-white" />
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
          <span>Low protection</span>
          <span>You</span>
          <span>High protection</span>
        </div>
      </div>

      <div className="mt-4 h-px w-full bg-slate-100" />

      <div className="mt-3 flex items-center justify-between text-[11.5px] text-slate-500">
        <span>{checklist.filter((i) => i.status === "good").length} of {checklist.length} defences in place</span>
        <span className="tabular-nums">Lvl {level} · {points} pts</span>
      </div>
    </div>
  );
}

// CREDIT · AUDIT — full audit-report view.
//
// Takeaway: "Here's the full breakdown — what's contributing what, weight,
// status." Maxes out information density. Suits power users and the "show
// me everything" personality. Reads like a credit-report summary page.
function ProtectionScoreCreditAudit({
  score,
  label,
  points,
  level,
  checklist,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
  checklist: ChecklistItem[];
}) {
  const factors = buildFactors(checklist);
  const grade = gradeForScore(score);
  const completedCount = factors.filter((f) => f.earned >= f.weight).length;

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      {/* Audit header band */}
      <div className="flex items-center justify-between gap-4 px-5 sm:px-6 py-4 border-b border-slate-100">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Protection report</p>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="text-[36px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[12px] font-medium text-slate-300">/100</span>
            <span className="ml-1 text-[12px] font-semibold text-emerald-700">{label}</span>
          </div>
        </div>
        <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl text-[22px] font-semibold ring-1 ${grade.tone} ${grade.bg} ${grade.ring}`}>
          {grade.letter}
        </span>
      </div>

      {/* Audit table */}
      <div className="px-5 sm:px-6 py-3">
        <div className="grid grid-cols-[minmax(0,1fr)_50px_60px_70px] gap-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
          <span>Factor</span>
          <span className="text-right">Weight</span>
          <span className="text-right">Earned</span>
          <span className="text-right">Status</span>
        </div>
        <div className="divide-y divide-slate-100">
          {factors.map((f) => {
            const isDone = f.earned >= f.weight;
            const isProgress = f.status === "progress";
            return (
              <div
                key={f.label}
                className="grid grid-cols-[minmax(0,1fr)_50px_60px_70px] items-center gap-2 py-2.5 text-[12.5px]"
              >
                <span className="font-semibold text-slate-700 truncate">{f.label}</span>
                <span className="text-right tabular-nums text-slate-500">{f.weight}</span>
                <span className="text-right tabular-nums font-medium text-slate-900">{f.earned}</span>
                <span
                  className={`inline-flex items-center justify-end gap-1 text-[11px] font-semibold ${
                    isDone ? "text-emerald-700" : isProgress ? "text-violet-700" : "text-amber-700"
                  }`}
                >
                  {isDone ? (
                    <>
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Met
                    </>
                  ) : isProgress ? (
                    <>
                      <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                      {f.percent ?? 0}%
                    </>
                  ) : (
                    <>
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                      Open
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {/* Footer summary */}
        <div className="mt-2 grid grid-cols-3 gap-2 rounded-xl bg-slate-50/70 p-3 text-center">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Defences</p>
            <p className="mt-0.5 text-[16px] font-semibold text-slate-900 tabular-nums">
              {completedCount}/{factors.length}
            </p>
          </div>
          <div className="border-x border-slate-200">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Level</p>
            <p className="mt-0.5 text-[16px] font-semibold text-slate-900 tabular-nums">{level}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Points</p>
            <p className="mt-0.5 text-[16px] font-semibold text-slate-900 tabular-nums">{points}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── PDRC competency cards ───────────────────────────────────────── */
//
// GranGuard's competency framework (PROTECT, DETECT, RESPOND, CONNECT) maps
// beautifully to a credit-style score. Each competency = a weighted factor
// (max 25 each → 100 total), each has its own personality and color.
//
// Per-competency scores are mocked-but-plausible: they sum to the user's
// real overall score, distributed with a slight lean (PROTECT highest,
// CONNECT lowest) reflecting typical user progression. Once we wire per-
// competency progress tracking through the Experience Builder, swap the
// inputs to `buildPDRC` for the real signals.

type CompetencyCode = "PROTECT" | "DETECT" | "RESPOND" | "CONNECT";

type PDRC = {
  code: CompetencyCode;
  label: string;
  tagline: string;
  earned: number;
  max: number;
  color: {
    fill: string; // bar fill class
    track: string; // bar track class
    text: string; // colored text
    bg: string; // soft bg
    ring: string; // soft ring
  };
  icon: ReactNode;
};

const PDRC_BASE: Record<CompetencyCode, Omit<PDRC, "earned">> = {
  PROTECT: {
    code: "PROTECT",
    label: "Protect",
    tagline: "Shield yourself before threats arrive",
    max: 25,
    color: {
      fill: "bg-violet-500",
      track: "bg-violet-100",
      text: "text-violet-700",
      bg: "bg-violet-50",
      ring: "ring-violet-100",
    },
    icon: <FiShield className="h-4 w-4" />,
  },
  DETECT: {
    code: "DETECT",
    label: "Detect",
    tagline: "Recognize threats when they appear",
    max: 25,
    color: {
      fill: "bg-blue-500",
      track: "bg-blue-100",
      text: "text-blue-700",
      bg: "bg-blue-50",
      ring: "ring-blue-100",
    },
    icon: <FiEye className="h-4 w-4" />,
  },
  RESPOND: {
    code: "RESPOND",
    label: "Respond",
    tagline: "Act effectively when targeted",
    max: 25,
    color: {
      fill: "bg-amber-500",
      track: "bg-amber-100",
      text: "text-amber-700",
      bg: "bg-amber-50",
      ring: "ring-amber-100",
    },
    icon: <FiZap className="h-4 w-4" />,
  },
  CONNECT: {
    code: "CONNECT",
    label: "Connect",
    tagline: "Build your support network",
    max: 25,
    color: {
      fill: "bg-emerald-500",
      track: "bg-emerald-100",
      text: "text-emerald-700",
      bg: "bg-emerald-50",
      ring: "ring-emerald-100",
    },
    icon: <FiUsers className="h-4 w-4" />,
  },
};

// Distribute the overall score across the four competencies so the bars sum
// to the headline number. Default lean: PROTECT > DETECT > RESPOND > CONNECT.
function buildPDRC(score: number): PDRC[] {
  const baseEach = score / 4;
  const dist = [
    Math.min(25, Math.max(0, Math.round(baseEach + 4))), // PROTECT slightly higher
    Math.min(25, Math.max(0, Math.round(baseEach + 1))), // DETECT
    Math.min(25, Math.max(0, Math.round(baseEach + 0))), // RESPOND
    Math.min(25, Math.max(0, Math.round(baseEach - 5))), // CONNECT lower
  ];
  // Absorb rounding drift into PROTECT so the four bars sum to score exactly.
  const sum = dist.reduce((a, b) => a + b, 0);
  const drift = score - sum;
  dist[0] = Math.min(25, Math.max(0, dist[0] + drift));
  const order: CompetencyCode[] = ["PROTECT", "DETECT", "RESPOND", "CONNECT"];
  return order.map((code, i) => ({ ...PDRC_BASE[code], earned: dist[i] }));
}

// PDRC · BREAKDOWN — segmented contribution bar across the four
// competencies plus per-row breakdown. Direct evolution of credit-breakdown.
function ProtectionScorePDRCBreakdown({
  score,
  label,
  points,
  level,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
}) {
  const factors = buildPDRC(score);
  const grade = gradeForScore(score);

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[60px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[14px] font-medium text-slate-300">/100</span>
          </div>
          <p className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center justify-center rounded-2xl px-3 py-2 text-[24px] font-semibold ring-1 ${grade.tone} ${grade.bg} ${grade.ring}`}>
            {grade.letter}
          </span>
          <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">Lvl {level} · {points} pts</p>
        </div>
      </div>

      {/* Segmented bar across PDRC */}
      <div className="mt-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 pb-2">
          Across your defence skills
        </p>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
          {factors.map((f, i) =>
            f.earned > 0 ? (
              <div
                key={f.code}
                className={`h-full ${f.color.fill} ${i === 0 ? "" : "border-l-2 border-white"}`}
                style={{ width: `${f.earned}%` }}
                title={`${f.label}: ${f.earned}/${f.max}`}
              />
            ) : null,
          )}
        </div>
        {/* Letter rail underneath */}
        <div className="mt-2 grid grid-cols-4 gap-1 text-center">
          {factors.map((f) => (
            <div key={f.code} className="flex flex-col items-center">
              <span className={`text-[10px] font-bold tracking-[0.12em] ${f.color.text}`}>{f.code}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-competency rows */}
      <div className="mt-4 space-y-2.5">
        {factors.map((f) => {
          const pct = f.max === 0 ? 0 : Math.round((f.earned / f.max) * 100);
          return (
            <div key={f.code}>
              <div className="flex items-center justify-between gap-2 text-[12.5px]">
                <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full ${f.color.bg} ${f.color.text} ring-1 ${f.color.ring}`}>
                    {f.icon}
                  </span>
                  {f.label}
                </span>
                <span className="text-slate-500 tabular-nums">
                  {f.earned} / {f.max} pts
                  <span className="ml-2 text-[11px] text-slate-400">{pct}%</span>
                </span>
              </div>
              <div className={`mt-1 h-1.5 w-full overflow-hidden rounded-full ${f.color.track}`}>
                <div className={`h-full rounded-full ${f.color.fill}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// PDRC · PILLARS — four vertical bars, each filling up to its earned height.
// Reads like a "vials" or "pillars" view. Each pillar is its own competency
// with code + name. Score sits at the top as the headline.
function ProtectionScorePDRCPillars({
  score,
  label,
  points,
  level,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
}) {
  const factors = buildPDRC(score);
  const grade = gradeForScore(score);

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[56px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[14px] font-medium text-slate-300">/100</span>
          </div>
          <p className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center justify-center rounded-lg px-2.5 py-1 text-[16px] font-semibold ring-1 ${grade.tone} ${grade.bg} ${grade.ring}`}>
            {grade.letter}
          </span>
          <p className="mt-1.5 text-[11.5px] text-slate-400 tabular-nums">Lvl {level} · {points} pts</p>
        </div>
      </div>

      {/* Pillars row */}
      <div className="mt-5 grid grid-cols-4 gap-3">
        {factors.map((f) => {
          const pct = f.max === 0 ? 0 : Math.round((f.earned / f.max) * 100);
          return (
            <div key={f.code} className="flex flex-col items-center">
              <span className={`text-[11px] font-bold uppercase tracking-[0.06em] ${f.color.text} tabular-nums`}>
                {f.earned}
                <span className="text-[10px] text-slate-300">/{f.max}</span>
              </span>
              <div
                className={`relative mt-1.5 flex h-[88px] w-full max-w-[36px] items-end overflow-hidden rounded-full ${f.color.track}`}
              >
                <div
                  className={`w-full rounded-full ${f.color.fill} transition-[height] duration-700 ease-out`}
                  style={{ height: `${pct}%` }}
                />
              </div>
              <span className={`mt-2 flex h-7 w-7 items-center justify-center rounded-full ${f.color.bg} ${f.color.text} ring-1 ${f.color.ring}`}>
                {f.icon}
              </span>
              <p className="mt-1.5 text-[11.5px] font-semibold text-slate-700 leading-tight text-center">{f.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// PDRC · RADAR — four-axis spider chart. Most distinctive visualization.
// Score in the center, polygon shape shows shape of competence. Reads as
// "where am I balanced vs uneven?"
function ProtectionScorePDRCRadar({
  score,
  label,
  summary,
  points,
  level,
}: {
  score: number;
  label: string;
  summary: string;
  points: number;
  level: number;
}) {
  const factors = buildPDRC(score);
  // 4 axes pointing N (PROTECT), E (DETECT), S (RESPOND), W (CONNECT).
  const cx = 110;
  const cy = 110;
  const maxR = 80;
  // Normalised radii (0..1) per axis.
  const radii = factors.map((f) => f.earned / f.max);
  // axis positions for 4 axes (N E S W)
  const angles = [-Math.PI / 2, 0, Math.PI / 2, Math.PI];

  const point = (r: number, angle: number) => [cx + r * maxR * Math.cos(angle), cy + r * maxR * Math.sin(angle)] as const;

  const ringSteps = [0.25, 0.5, 0.75, 1];

  // Polygon path
  const polyPath = factors
    .map((_, i) => {
      const [x, y] = point(radii[i], angles[i]);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ") + " Z";

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Defence balance</p>
          <p className="mt-1 text-[16px] font-semibold tracking-tight text-slate-900">
            {label} · <span className="text-violet-700 tabular-nums">{score}/100</span>
          </p>
          <p className="mt-0.5 max-w-[40ch] text-[11.5px] leading-snug text-slate-500">{summary}</p>
        </div>
        <p className="text-[11.5px] text-slate-400 tabular-nums whitespace-nowrap">Lvl {level} · {points} pts</p>
      </div>

      <div className="mt-2 grid gap-4 md:grid-cols-[220px_minmax(0,1fr)] md:items-center">
        {/* Radar */}
        <div className="relative mx-auto h-[220px] w-[220px]">
          <svg width={220} height={220} viewBox="0 0 220 220">
            {/* Concentric guides */}
            {ringSteps.map((step, i) => {
              const path = factors
                .map((_, j) => {
                  const [x, y] = point(step, angles[j]);
                  return `${j === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
                })
                .join(" ") + " Z";
              return (
                <path
                  key={i}
                  d={path}
                  fill="none"
                  stroke="rgb(226 232 240)"
                  strokeWidth={1}
                  strokeDasharray={i === ringSteps.length - 1 ? "0" : "2 3"}
                />
              );
            })}
            {/* Axes */}
            {factors.map((_, i) => {
              const [x, y] = point(1, angles[i]);
              return (
                <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="rgb(226 232 240)" strokeWidth={1} />
              );
            })}
            {/* User polygon */}
            <path
              d={polyPath}
              fill="rgba(124, 58, 237, 0.18)"
              stroke="#7c3aed"
              strokeWidth={2}
              strokeLinejoin="round"
            />
            {/* Vertex dots */}
            {factors.map((f, i) => {
              const [x, y] = point(radii[i], angles[i]);
              return <circle key={f.code} cx={x} cy={y} r={4} fill="white" stroke="#7c3aed" strokeWidth={2} />;
            })}
          </svg>
          {/* Axis labels */}
          <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1 text-[10px] font-bold tracking-[0.1em] text-violet-700">
            PROTECT
          </span>
          <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 text-[10px] font-bold tracking-[0.1em] text-blue-700">
            DETECT
          </span>
          <span className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1 text-[10px] font-bold tracking-[0.1em] text-amber-700">
            RESPOND
          </span>
          <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 text-[10px] font-bold tracking-[0.1em] text-emerald-700">
            CONNECT
          </span>
        </div>

        {/* Right legend */}
        <div className="space-y-2">
          {factors.map((f) => {
            const pct = Math.round((f.earned / f.max) * 100);
            return (
              <div key={f.code} className="flex items-center gap-2.5">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full ${f.color.bg} ${f.color.text} ring-1 ${f.color.ring}`}>
                  {f.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 text-[12.5px]">
                    <span className="font-semibold text-slate-800">{f.label}</span>
                    <span className="text-slate-500 tabular-nums">
                      {f.earned}<span className="text-slate-300">/{f.max}</span>
                    </span>
                  </div>
                  <div className={`mt-1 h-1 w-full overflow-hidden rounded-full ${f.color.track}`}>
                    <div className={`h-full rounded-full ${f.color.fill}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// PDRC · CARDS — 2x2 grid of mini competency cards. Each card has its own
// identity (icon + name + tagline + earned/max + thin progress bar). Score
// header sits at the top. Most modular and dashboard-y.
function ProtectionScorePDRCCards({
  score,
  label,
  points,
  level,
}: {
  score: number;
  label: string;
  points: number;
  level: number;
}) {
  const factors = buildPDRC(score);
  const grade = gradeForScore(score);

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Protection score</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[44px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{score}</span>
            <span className="text-[13px] font-medium text-slate-300">/100</span>
            <span className={`ml-1 inline-flex items-center justify-center rounded-lg px-2 py-1 text-[13px] font-semibold ring-1 ${grade.tone} ${grade.bg} ${grade.ring}`}>
              {grade.letter}
            </span>
          </div>
          <p className="mt-1 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-700">
            <FiShield className="h-3.5 w-3.5 text-emerald-600" />
            {label}
          </p>
        </div>
        <p className="text-[11.5px] text-slate-400 tabular-nums whitespace-nowrap">Lvl {level} · {points} pts</p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2.5">
        {factors.map((f) => {
          const pct = f.max === 0 ? 0 : Math.round((f.earned / f.max) * 100);
          return (
            <div
              key={f.code}
              className={`relative overflow-hidden rounded-[14px] border p-3.5 ${f.color.bg.replace("bg-", "bg-")}/40 border-slate-100`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full bg-white ${f.color.text} ring-1 ${f.color.ring}`}>
                  {f.icon}
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-[0.08em] ${f.color.text}`}>
                  {f.code}
                </span>
              </div>
              <p className="mt-3 text-[13.5px] font-semibold text-slate-900 leading-tight">{f.label}</p>
              <p className="mt-0.5 text-[10.5px] text-slate-500 line-clamp-1">{f.tagline}</p>
              <div className="mt-2.5 flex items-center justify-between gap-2">
                <div className={`h-1.5 flex-1 overflow-hidden rounded-full ${f.color.track}`}>
                  <div className={`h-full rounded-full ${f.color.fill}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[11px] font-medium text-slate-500 tabular-nums">
                  {f.earned}<span className="text-slate-300">/{f.max}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Minimal cards ───────────────────────────────────────────────── */
//
// Reference image: small ring + big title + helpful one-liner + text-link
// CTA. Generous white space, single accent, no chrome competing with the
// Up Next card. These five all share that visual language but answer
// different questions.

// Reusable thin ring used across the minimal family. ~120px default, single
// accent stroke, soft track. Can hold any string in the centre.
function MinimalRing({
  size = 120,
  stroke = 8,
  percent,
  centre,
  accent = "#7c3aed",
}: {
  size?: number;
  stroke?: number;
  percent: number;
  centre: ReactNode;
  accent?: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, percent)) / 100) * c;
  const cx = size / 2;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgb(238 240 250)" strokeWidth={stroke} />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{centre}</div>
    </div>
  );
}

// Minimal · level — direct port of the reference image. Small ring shows
// progress to next level. Big "Guardian Level N" title. One helpful line
// of why levels matter. Underlined text-link CTA.
function ProtectionScoreMinimalLevel({
  level,
  levelProgress,
  levelProgressPercent,
  points,
}: {
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  points: number;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-5 sm:gap-6">
        <MinimalRing
          percent={levelProgressPercent}
          centre={
            <span className="text-[22px] font-semibold tabular-nums text-slate-900">{levelProgressPercent}%</span>
          }
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-[22px] sm:text-[24px] font-semibold leading-tight tracking-tight text-slate-900">
            Guardian Level {level}
          </h2>
          <p className="mt-2 max-w-[36ch] text-[14px] leading-relaxed text-slate-600">
            You've earned {points} points. Just {100 - levelProgress} more to reach Level {level + 1}.
          </p>
          <Link
            to="/missions"
            search={{ tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
            className="mt-3 inline-flex text-[14px] font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
          >
            See how to earn more
          </Link>
        </div>
      </div>
    </div>
  );
}

// Minimal · score — same shape as the reference but headlining the
// Protection Score instead. Ring shows score percent, big title is the
// tier name, helpful line summarizes state.
function ProtectionScoreMinimalScore({
  score,
  label,
  summary,
}: {
  score: number;
  label: string;
  summary: string;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-5 sm:gap-6">
        <MinimalRing
          percent={score}
          centre={
            <span className="text-[22px] font-semibold tabular-nums text-slate-900">{score}%</span>
          }
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-[22px] sm:text-[24px] font-semibold leading-tight tracking-tight text-slate-900">
            {label}
          </h2>
          <p className="mt-2 max-w-[40ch] text-[14px] leading-relaxed text-slate-600">{summary}</p>
          <Link
            to="/footprint"
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="mt-3 inline-flex text-[14px] font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
          >
            See score details
          </Link>
        </div>
      </div>
    </div>
  );
}

// Minimal · hero — no ring at all. Score is the hero, set in heroic
// typography. Tiny "Level N" pill above as orientation, big tier label, a
// thoughtful sentence beneath. Text-link CTA. Most committed to type.
function ProtectionScoreMinimalHero({
  score,
  label,
  summary,
  level,
}: {
  score: number;
  label: string;
  summary: string;
  level: number;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white p-6 sm:p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
        Protection score · Guardian Level {level}
      </p>
      <div className="mt-2 flex items-baseline gap-3">
        <span className="text-[120px] font-semibold leading-[0.92] tracking-tight text-slate-900 tabular-nums">
          {score}
        </span>
        <span className="text-[24px] font-medium text-slate-300">/100</span>
      </div>
      <p className="mt-3 inline-flex items-center gap-1.5 text-[16px] font-semibold text-emerald-700">
        <FiShield className="h-4 w-4 text-emerald-600" />
        {label}
      </p>
      <p className="mt-3 max-w-[44ch] text-[15px] leading-relaxed text-slate-600">{summary}</p>
      <Link
        to="/footprint"
        search={{ invitationcode: undefined, __clerk_ticket: undefined }}
        className="mt-3.5 inline-flex text-[14px] font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
      >
        See score details
      </Link>
    </div>
  );
}

// Minimal · twin — two minimal rings side by side. Protection score on the
// left, Guardian Level on the right. Each gets its own short caption. Most
// informative without losing the calm reference style.
function ProtectionScoreMinimalTwin({
  score,
  label,
  level,
  levelProgress,
  levelProgressPercent,
  points,
}: {
  score: number;
  label: string;
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  points: number;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="grid gap-5 sm:grid-cols-2 sm:divide-x sm:divide-slate-100">
        {/* Score */}
        <div className="flex items-center gap-4 sm:pr-5">
          <MinimalRing
            size={92}
            stroke={7}
            percent={score}
            centre={
              <span className="text-[18px] font-semibold tabular-nums text-slate-900">{score}</span>
            }
          />
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
              Protection score
            </p>
            <p className="mt-0.5 text-[16px] font-semibold tracking-tight text-slate-900">{label}</p>
            <Link
              to="/footprint"
              search={{ invitationcode: undefined, __clerk_ticket: undefined }}
              className="mt-1.5 inline-flex text-[12.5px] font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4"
            >
              View details
            </Link>
          </div>
        </div>
        {/* Level */}
        <div className="flex items-center gap-4 sm:pl-5">
          <MinimalRing
            size={92}
            stroke={7}
            percent={levelProgressPercent}
            centre={
              <span className="text-[18px] font-semibold tabular-nums text-slate-900">{level}</span>
            }
          />
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
              Guardian level
            </p>
            <p className="mt-0.5 text-[16px] font-semibold tracking-tight text-slate-900 tabular-nums">
              {points} pts
            </p>
            <p className="mt-0.5 text-[12.5px] text-slate-500 tabular-nums">
              {100 - levelProgress} to Level {level + 1}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Minimal · ladder — current level prominent, plus a 3-bullet "what this
// level means" / "what's next" mini list. Adds the "context for the level"
// the user asked about without crowding.
function ProtectionScoreMinimalLadder({
  level,
  levelProgress,
  levelProgressPercent,
  points,
}: {
  level: number;
  levelProgress: number;
  levelProgressPercent: number;
  points: number;
}) {
  // Light, encouraging copy describing the present level. Real implementation
  // would pull these from a Level config / CMS.
  const levelStory = [
    { kind: "now", text: "Welcome content unlocked" },
    { kind: "now", text: "Daily tips, scam alerts, link checker" },
    { kind: "next", text: `Level ${level + 1} unlocks personalised threat alerts` },
  ];

  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start gap-5 sm:gap-6">
        <MinimalRing
          size={132}
          stroke={9}
          percent={levelProgressPercent}
          centre={
            <div className="flex flex-col items-center">
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">Level</span>
              <span className="text-[36px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{level}</span>
            </div>
          }
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-[20px] sm:text-[22px] font-semibold leading-tight tracking-tight text-slate-900">
            You're a Guardian Level {level}
          </h2>
          <p className="mt-1.5 text-[13px] text-slate-500 tabular-nums">
            {points} pts earned · {100 - levelProgress} to Level {level + 1}
          </p>

          <ul className="mt-3 space-y-1.5">
            {levelStory.map((row) => (
              <li key={row.text} className="flex items-start gap-2 text-[13px]">
                {row.kind === "now" ? (
                  <span className="mt-0.5 flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
                  </span>
                ) : (
                  <span className="mt-0.5 flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-600">
                    <HiOutlineLockClosed className="h-3 w-3" />
                  </span>
                )}
                <span className={row.kind === "now" ? "text-slate-700" : "text-slate-500"}>{row.text}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/missions"
            search={{ tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
            className="mt-3.5 inline-flex text-[14px] font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
          >
            How to level up
          </Link>
        </div>
      </div>
    </div>
  );
}

export function UpNextCardE({ market }: { market?: string | null }) {
  const title = market === "US" || market === "CA" ? "Scam Defense Fundamentals" : "Scam Defence Fundamentals";
  const { resumeRoute, hasProgress, completedChapters, totalChapters } = useCourseProgress();
  const pct = Math.round((completedChapters / Math.max(1, totalChapters)) * 100);

  return (
    <div data-tour-id="tour-home-start-training-card" className="relative overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-5 sm:p-6 lg:p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 lg:h-10 lg:w-10 items-center justify-center rounded-[12px] bg-violet-50 text-violet-600">
          <HiOutlineAcademicCap className="h-6 w-6 lg:h-5 lg:w-5" />
        </div>
        <div>
          <h2 className="text-[24px] lg:text-[20px] font-semibold leading-tight tracking-tight text-slate-900">
            Up Next
          </h2>
          <p className="text-[15px] lg:text-[14px] leading-snug text-slate-400">
            Continue your scam defense guide
          </p>
        </div>
      </div>

      <Link
        to={resumeRoute}
        search={{ invitationcode: undefined, __clerk_ticket: undefined }}
        className="group relative mt-3 lg:mt-2 block overflow-hidden rounded-[20px] px-6 lg:px-5 py-0 transition-shadow hover:shadow-[0_4px_12px_rgba(124,58,237,0.12)]"
        style={{ backgroundImage: `url(${courseBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="relative grid gap-3 sm:grid-cols-[minmax(0,1fr)_320px] lg:grid-cols-[minmax(0,1fr)_240px] sm:items-end">
          <div className="min-w-0 pt-7 pb-5 lg:pt-3 lg:pb-3 sm:self-center sm:-translate-y-3">
            <div className="space-y-4 lg:space-y-6">
              <div className="space-y-1 lg:space-y-0.5">
                <h3 className="text-[22px] lg:text-[19px] font-semibold leading-tight tracking-tight text-slate-900 whitespace-nowrap">
                  {title}
                </h3>
                <p className="text-[16px] leading-snug text-slate-500 max-w-[32ch] lg:max-w-[28ch] line-clamp-2">
                  Build the skills to spot, stop, and respond to modern scams.
                </p>
              </div>
              <div className="space-y-2 lg:space-y-1.5 max-w-[300px] lg:max-w-[260px]">
                <div className="flex items-center justify-between gap-3 text-[14px] lg:text-[12px] font-medium text-slate-500">
                  <span>
                    {completedChapters} of {totalChapters} lessons completed
                  </span>
                  <span className="tabular-nums">{pct}%</span>
                </div>
                <div className="h-2 lg:h-1.5 w-full overflow-hidden rounded-full bg-slate-200/90">
                  <div
                    className="h-full rounded-full bg-violet-600 transition-all duration-700"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </div>
            <span className="mt-4 lg:mt-6 flex w-full max-w-[300px] lg:max-w-[260px] items-center justify-center gap-1.5 rounded-[12px] bg-violet-600 px-6 lg:px-5 py-2.5 lg:py-2 text-[16px] lg:text-[14px] font-semibold text-white transition-colors group-hover:bg-violet-700">
              {hasProgress ? "Continue Guide" : "Start Guide"} <HiOutlineArrowRight className="h-5 w-5 lg:h-4 lg:w-4" />
            </span>
          </div>

          <div className="hidden shrink-0 self-end sm:flex sm:items-end sm:justify-end translate-x-3">
            <img
              src={courseGraphic}
              alt=""
              className="w-[360px] max-h-[300px] lg:w-[290px] lg:max-h-[240px] object-contain transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </div>
      </Link>
    </div>
  );
}

export function QuickActionCardE({
  to,
  icon,
  tone,
  title,
  description,
}: {
  to: string;
  icon: ReactNode;
  tone: string;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      search={{ invitationcode: undefined, __clerk_ticket: undefined }}
      className="group flex min-h-[112px] items-center gap-4 rounded-[24px] border border-slate-200/80 bg-white/95 px-5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:bg-slate-50/80"
    >
      <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ${tone} shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] [&_svg]:h-6 [&_svg]:w-6`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[17px] font-semibold leading-tight tracking-tight text-slate-900">{title}</p>
        <p className="mt-1 text-[14.5px] leading-5 text-slate-500 line-clamp-2">{description}</p>
      </div>
      <HiOutlineChevronRight className="h-5 w-5 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-slate-500" />
    </Link>
  );
}

export function ScamRadarWithFlagsCard({
  scamOfTheDay,
  isLoading,
  redFlags,
  showRedFlags = true,
}: {
  scamOfTheDay: ScamOfTheDay | undefined;
  isLoading: boolean;
  redFlags: string[];
  showRedFlags?: boolean;
}) {
  const slug = scamOfTheDay?.scam?.slug;
  const title = scamOfTheDay?.scam?.title || scamOfTheDay?.title;
  const description = scamOfTheDay?.scam?.description || scamOfTheDay?.shortDescription;

  return (
    <section className="rounded-[28px] border border-slate-200/80 bg-white/95 p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-red-50 text-red-600">
            <HiOutlineShieldExclamation className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 tracking-tight leading-tight">Scam Radar</h2>
            <p className="text-sm text-slate-400 leading-tight">This week's top scam to watch for.</p>
          </div>
        </div>
        <Link
          to="/scam-library"
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="text-[13px] font-medium text-slate-500 transition-colors hover:text-slate-700 whitespace-nowrap"
        >
          View all scams
        </Link>
      </div>

      {isLoading ? (
        <div className={`mt-5 grid grid-cols-1 gap-5 ${showRedFlags ? "lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:gap-6" : ""} animate-pulse`}>
          <div className="h-40 rounded-[24px] bg-slate-50" />
          {showRedFlags && <div className="h-40 rounded-[24px] bg-slate-50" />}
        </div>
      ) : (
        <div className={`mt-5 grid grid-cols-1 gap-5 ${showRedFlags ? "lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:gap-6" : ""}`}>
          {/* Scam of the week — flat content, no red chrome. The radar
              header above already signals "alerts" with the red icon, so the
              inner panel doesn't need to repeat the warning colour. */}
          {scamOfTheDay ? (
            <div className="min-w-0">
              {slug ? (
                <Link
                  to="/scam-library/$slug"
                  params={{ slug }}
                  search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                  className="block text-[1.4rem] font-semibold tracking-tight text-slate-900 transition-colors hover:text-violet-700"
                >
                  {title}
                </Link>
              ) : (
                <p className="text-[1.4rem] font-semibold tracking-tight text-slate-900">{title}</p>
              )}
              <p className="mt-2 text-[16px] leading-[1.55] text-slate-600">
                {description || "We're tracking current scams and will surface the most important ones here."}
              </p>
            </div>
          ) : (
            <p className="text-[15px] text-slate-500">No scams to display yet.</p>
          )}

          {/* Red flags — hidden when showRedFlags=false (Variant F) */}
          {showRedFlags && (
            <div className="rounded-[24px] border border-slate-100 bg-white p-4 sm:p-5">
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-red-500">
                Red flags to watch for
              </p>
              <ul className="space-y-3.5">
                {redFlags.map((flag) => (
                  <li key={flag} className="flex items-start gap-2.5">
                    <HiOutlineFlag className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    <span className="text-[15px] leading-6 text-slate-700">{flag}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
